import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DollarSign, Plus, Search, Edit, Trash2, X, AlertCircle, Sparkles, RefreshCw, TrendingUp, BarChart3, Loader2, Eye, FileText, ChevronRight, Package, Wine, Beer, Coffee, Droplets, GlassWater, Save, Check, Building2, ChevronDown, ExternalLink, Upload, FileImage, FileType, Printer, Calculator, LayoutDashboard, Target, ArrowUpRight, ArrowDownRight, Scale
} from 'lucide-react';
import { toast } from 'sonner';
import FoodCostCalculator from '../components/foodcost/FoodCostCalculator';
import CostiProdotti from '../components/foodcost/CostiProdotti';
import ContoFoodCost from '../components/foodcost/ContoFoodCost';
import FatturatoMeseModal from '../components/foodcost/FatturatoMeseModal';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Helper per ottenere headers con tenant
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  
  // Prova prima tenant_db esplicito
  let tenantDb = localStorage.getItem('tenant_db');
  
  // Se non esiste, prova a ottenerlo dalla lista tenant
  if (!tenantDb) {
    try {
      const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (tenantList.length > 0 && tenantList[0].database_name) {
        tenantDb = tenantList[0].database_name;
      }
    } catch (e) {
      console.warn('Errore parsing tenant_list:', e);
    }
  }
  
  // Prova anche tenant_user
  if (!tenantDb) {
    try {
      const tenantUser = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tenantUser.tenants && tenantUser.tenants[0]) {
        tenantDb = tenantUser.tenants[0].database_name;
      }
    } catch (e) {
      console.warn('Errore parsing tenant_user:', e);
    }
  }
  
  // Se non c'è tenant, usa database principale
  if (!tenantDb) {
    console.warn('Nessun tenant configurato - usando database principale');
  }
  
  if (tenantDb) {
    // Rimuovi prefisso tenant_ se presente
    headers['X-Tenant-DB'] = tenantDb.replace(/^tenant_/, '');
  }
  
  return headers;
};

const FBCostPage = () => {
  const [ricette, setRicette] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [activeTab, setActiveTab] = useState('ricette');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRicetta, setEditingRicetta] = useState(null);
  const [showNuovoDropdown, setShowNuovoDropdown] = useState(false);  // Dropdown Nuovo
  const [nuovoPos, setNuovoPos] = useState(null);
  
  // Stati per operazioni AI
  const [generatingFromMenu, setGeneratingFromMenu] = useState(false);
  const [updatingCosts, setUpdatingCosts] = useState(false);
  const [analyzingIngredients, setAnalyzingIngredients] = useState(false);
  const [analisiMarginalita, setAnalisiMarginalita] = useState(null);
  const [loadingAnalisi, setLoadingAnalisi] = useState(false);
  const [analisiFilterStatus, setAnalisiFilterStatus] = useState(''); // '', 'ottimo', 'buono', 'attenzione', 'critico'
  const [analisiSearchTerm, setAnalisiSearchTerm] = useState('');
  
  // Stati per dettaglio ricetta con rese
  const [selectedRicettaRese, setSelectedRicettaRese] = useState(null);
  const [loadingRicettaRese, setLoadingRicettaRese] = useState(false);
  
  // Stati per Beverage
  const [beverageList, setBeverageList] = useState([]);
  const [beverageStats, setBeverageStats] = useState(null);
  const [loadingBeverage, setLoadingBeverage] = useState(false);
  const [beverageSearch, setBeverageSearch] = useState('');
  const [beverageCategoria, setBeverageCategoria] = useState('');
  const [editingBeverage, setEditingBeverage] = useState(null);
  const [beverageFilterStatus, setBeverageFilterStatus] = useState('');
  const [expandedBeverage, setExpandedBeverage] = useState(null); // Per espandere storico acquisti
  
  // === STATO TAB VINI ===
  const [viniList, setViniList] = useState([]);
  const [viniStats, setViniStats] = useState(null);
  const [viniFiltri, setViniFiltri] = useState({ nazioni: [], regioni: [], tipi: [], fornitori: [] });
  const [loadingVini, setLoadingVini] = useState(false);
  const [viniSearch, setViniSearch] = useState('');
  const [viniNazione, setViniNazione] = useState('');
  const [viniRegione, setViniRegione] = useState('');
  const [viniTipo, setViniTipo] = useState('');
  const [viniFornitore, setViniFornitore] = useState('');
  const [expandedVino, setExpandedVino] = useState(null);
  
  // Stato per stampa carta vini
  const [showStampaVini, setShowStampaVini] = useState(false);
  const [stampaSettings, setStampaSettings] = useState({
    nomeRistorante: '',
    mostraPrezzi: true,
    mostraDescrizione: true,
    mostraAnnata: true,
    layout: 'elegante', // 'elegante', 'moderno', 'classico'
    numeroPagine: '', // vuoto = auto, oppure numero specifico
    separaChampagne: true, // Separa Champagne dalle bollicine italiane
    mostraCopertina: false, // Copertina su pagina dedicata
    margineSup: 10, // margine superiore/inferiore in mm
    margineLat: 15  // margine laterale in mm
  });
  
  // Stato per correzione abbinamento vini
  const [showCorreggiVino, setShowCorreggiVino] = useState(false);
  const [vinoCorrezione, setVinoCorrezione] = useState(null);
  const [viniFattureDisponibili, setViniFattureDisponibili] = useState([]);
  const [loadingViniFatture, setLoadingViniFatture] = useState(false);
  const [searchViniFatture, setSearchViniFatture] = useState('');
  const [generandoDescrizione, setGenerandoDescrizione] = useState(false);
  const [generandoTutte, setGenerandoTutte] = useState(false);
  
  // Stato per dettaglio ricetta
  const [selectedRicetta, setSelectedRicetta] = useState(null);
  const [ricettaDetail, setRicettaDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Stato per match da confermare
  const [matchDaConfermare, setMatchDaConfermare] = useState([]);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [loadingMatch, setLoadingMatch] = useState(false);

  // Stato per ricerca prodotti e modifica ingredienti
  const [searchProdotti, setSearchProdotti] = useState('');
  const [prodottiTrovati, setProdottiTrovati] = useState([]);
  const [loadingProdotti, setLoadingProdotti] = useState(false);
  const [editingIngrediente, setEditingIngrediente] = useState(null); // {ricettaId, idx, quantita}
  const [showProdottiModal, setShowProdottiModal] = useState(false);
  const [showProdottiSearchInCreate, setShowProdottiSearchInCreate] = useState(false); // Per ricerca DDT nel modal creazione

  // Stato per analisi food cost da menu
  const [showAnalisiMenu, setShowAnalisiMenu] = useState(false);
  const [analisiMenuFile, setAnalisiMenuFile] = useState(null);
  const [analisiMenuLoading, setAnalisiMenuLoading] = useState(false);
  const [analisiMenuRisultato, setAnalisiMenuRisultato] = useState(null);
  
  // Stato per modifica abbinamento ingrediente
  const [showModificaAbbinamento, setShowModificaAbbinamento] = useState(false);
  const [ingredienteSelezionato, setIngredienteSelezionato] = useState(null); // {piattoIdx, ingredienteIdx, ingrediente}
  const [prodottiFatture, setProdottiFatture] = useState([]);
  const [searchProdotto, setSearchProdotto] = useState('');
  // loadingProdotti già definito sopra

  const [formData, setFormData] = useState({
    nome: '', categoria: '', prezzo_vendita: '', porzioni: 1, ingredienti: [], note: ''
  });
  const [newIngrediente, setNewIngrediente] = useState({
    nome: '', quantita: '', unita_misura: 'g', costo_kg: ''
  });

  // ============ STATI DASHBOARD FOOD COST ============
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardAnno, setDashboardAnno] = useState(new Date().getFullYear());
  const [dashboardMese, setDashboardMese] = useState(new Date().getMonth() + 1);
  const [dashboardVista, setDashboardVista] = useState('mese'); // 'mese' o 'anno'
  const [showFatturatoModal, setShowFatturatoModal] = useState(false);
  // Stati per gestione fornitori
  const [showFornitoriModal, setShowFornitoriModal] = useState(false);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [loadingFornitori, setLoadingFornitori] = useState(false);
  const [fornitoriSearch, setFornitoriSearch] = useState('');

  useEffect(() => {
    loadRicette();
  }, [selectedCategoria, searchTerm]);

  const loadRicette = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategoria) params.append('categoria', selectedCategoria);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/gestione/ricette?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setRicette(response.data.ricette || []);
      setCategorie(response.data.categorie || []);
    } catch (error) {
      console.error('Error loading ricette:', error);
      
    } finally {
      setLoading(false);
    }
  };

  // ============ FUNZIONI DASHBOARD FOOD COST ============
  const loadDashboard = async (anno = dashboardAnno, mese = dashboardMese, vista = dashboardVista) => {
    setLoadingDashboard(true);
    try {
      const response = await axios.get(
        `${API}/gestione/ricette/dashboard/food-cost-mensile?anno=${anno}&mese=${mese}&vista=${vista}`,
        { headers: getAuthHeaders() }
      );
      setDashboardData(response.data);
    } catch (error) {
      console.error('Errore caricamento dashboard:', error);
      
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Carica lista fornitori per classificazione
  const loadFornitori = async () => {
    setLoadingFornitori(true);
    try {
      const response = await axios.get(
        `${API}/gestione/ricette/dashboard/fornitori`,
        { headers: getAuthHeaders() }
      );
      setFornitoriList(response.data.fornitori || []);
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
      
    } finally {
      setLoadingFornitori(false);
    }
  };

  // Salva categoria fornitore
  const salvaCategroiaFornitore = async (fornitore, categoria) => {
    try {
      await axios.post(
        `${API}/gestione/ricette/dashboard/fornitori/categoria`,
        { fornitore, categoria },
        { headers: getAuthHeaders() }
      );
      
      // Aggiorna lista locale
      setFornitoriList(prev => prev.map(f => 
        f.fornitore === fornitore ? { ...f, categoria } : f
      ));
      
      toast.success(`${fornitore.substring(0, 20)}... → ${categoria}`);
      
      // Ricarica dashboard per aggiornare i calcoli
      loadDashboard();
    } catch (error) {
      console.error('Errore salvataggio categoria:', error);
      toast.error('Errore salvataggio');
    }
  };

  // Carica fornitori quando si apre il modal
  useEffect(() => {
    if (showFornitoriModal) {
      loadFornitori();
    }
  }, [showFornitoriModal]);

  // Carica dashboard quando si cambia tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    }
  }, [activeTab, dashboardAnno, dashboardMese, dashboardVista]);

  const saveRicetta = async () => {
    try {
      const data = {
        ...formData,
        prezzo_vendita: parseFloat(formData.prezzo_vendita) || 0,
        porzioni: parseInt(formData.porzioni) || 1
      };

      if (editingRicetta) {
        await axios.put(`${API}/gestione/ricette/${editingRicetta.id}`, data, {
          headers: getAuthHeaders()
        });
        toast.success('Ricetta aggiornata');
      } else {
        await axios.post(`${API}/gestione/ricette`, data, {
          headers: getAuthHeaders()
        });
        toast.success('Ricetta creata');
      }
      
      setShowCreateModal(false);
      setEditingRicetta(null);
      resetForm();
      loadRicette();
    } catch (error) {
      toast.error('Errore salvataggio ricetta');
    }
  };

  const deleteRicetta = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa ricetta?')) return;
    try {
      await axios.delete(`${API}/gestione/ricette/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Ricetta eliminata');
      loadRicette();
    } catch (error) {
      toast.error('Errore eliminazione ricetta');
    }
  };

  const addIngrediente = () => {
    if (!newIngrediente.nome || !newIngrediente.quantita) return;
    setFormData({
      ...formData,
      ingredienti: [...formData.ingredienti, {
        ...newIngrediente,
        quantita: parseFloat(newIngrediente.quantita),
        costo_kg: parseFloat(newIngrediente.costo_kg) || 0
      }]
    });
    setNewIngrediente({ nome: '', quantita: '', unita_misura: 'g', costo_kg: '' });
  };

  const removeIngrediente = (index) => {
    setFormData({
      ...formData,
      ingredienti: formData.ingredienti.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setFormData({ nome: '', categoria: '', prezzo_vendita: '', porzioni: 1, ingredienti: [], note: '' });
    setNewIngrediente({ nome: '', quantita: '', unita_misura: 'g', costo_kg: '' });
  };

  const openEditModal = (ricetta) => {
    setEditingRicetta(ricetta);
    setFormData({
      nome: ricetta.nome,
      categoria: ricetta.categoria,
      prezzo_vendita: ricetta.prezzo_vendita?.toString() || '',
      porzioni: ricetta.porzioni || 1,
      ingredienti: ricetta.ingredienti || [],
      note: ricetta.note || ''
    });
    setShowCreateModal(true);
  };

  const getIncidenzaColor = (incidenza) => {
    if (incidenza <= 25) return 'text-green-600 bg-green-50 border-green-200';
    if (incidenza <= 35) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // Carica dettaglio ricetta con fonti prezzi
  const loadRicettaDetail = async (ricetta) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/${ricetta.id}/dettaglio-costi`, {
        headers: getAuthHeaders()
      });
      // Usa i dati aggiornati dal server
      setSelectedRicetta({
        ...ricetta,
        ...res.data,
        ingredienti: res.data.ingredienti_dettaglio || res.data.ingredienti || ricetta.ingredienti
      });
      setRicettaDetail(res.data);
    } catch (error) {
      console.error('Errore caricamento dettaglio:', error);
      // Fallback ai dati base della ricetta
      setSelectedRicetta(ricetta);
      setRicettaDetail({
        ...ricetta,
        ingredienti_dettaglio: ricetta.ingredienti?.map(ing => ({
          ...ing,
          fonte_prezzo: ing.prodotto_fattura ? 'fatture' : 'ai_stimato',
          fattura_riferimento: null
        }))
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  // Genera ricette dal menu usando AI
  const handleGeneraRicetteDaMenu = async () => {
    setGeneratingFromMenu(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/genera-da-menu`, {}, {
        headers: getAuthHeaders(),
        timeout: 120000
      });
      
      if (res.data.success) {
        toast.success(`Create ${res.data.ricette_create} ricette dal menu!`);
        loadRicette();
      } else {
        toast.error(res.data.error || 'Errore nella generazione');
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingFromMenu(false);
    }
  };

  // Aggiorna costi ingredienti dalle fatture
  const handleAggiornaCostiDaFatture = async () => {
    setUpdatingCosts(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/aggiorna-costi-fatture`, {}, {
        headers: getAuthHeaders(),
        timeout: 180000  // 3 minuti timeout
      });
      
      if (res.data.success) {
        const { ricette_aggiornate, ingredienti_matchati, match_da_confermare } = res.data;
        
        if (match_da_confermare > 0) {
          toast.info(`${ingredienti_matchati} abbinamenti automatici, ${match_da_confermare} da confermare!`);
          loadMatchDaConfermare();
        } else {
          toast.success(`Aggiornate ${ricette_aggiornate} ricette con ${ingredienti_matchati} ingredienti!`);
        }
        loadRicette();
      } else {
        toast.error(res.data.error || 'Errore nell\'aggiornamento');
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUpdatingCosts(false);
    }
  };

  // Sincronizza prezzi ricette con menu
  const [syncingPrices, setSyncingPrices] = useState(false);
  const handleSincronizzaPrezziMenu = async () => {
    setSyncingPrices(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/sincronizza-prezzi-menu`, {}, {
        headers: getAuthHeaders()
      });
      
      if (res.data.success) {
        const { ricette_aggiornate, dettaglio } = res.data;
        if (ricette_aggiornate > 0) {
          toast.success(`Sincronizzati ${ricette_aggiornate} prezzi con il menu!`);
          // Mostra dettaglio
          dettaglio.forEach(d => {
            console.log(`${d.nome}: €${d.prezzo_precedente} → €${d.prezzo_nuovo}`);
          });
          loadRicette();
          loadAnalisiMarginalita();
        } else {
          toast.info('Tutti i prezzi sono già sincronizzati con il menu');
        }
      } else {
        toast.error(res.data.error || 'Errore nella sincronizzazione');
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingPrices(false);
    }
  };

  // Analizza ingredienti con AI dalle fatture
  const handleAnalizzaIngredientiAI = async () => {
    setAnalyzingIngredients(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/analizza-ingredienti-ai`, {}, {
        headers: getAuthHeaders(),
        timeout: 300000  // 5 minuti timeout per AI
      });
      
      if (res.data.success) {
        const { ricette_analizzate, ricette_aggiornate } = res.data;
        toast.success(ricette_analizzate
          ? `🤖 AI ha analizzato ${ricette_analizzate} ricette, aggiornate ${ricette_aggiornate ?? 0} con ingredienti!`
          : (res.data.message || 'Nessuna ricetta da analizzare'));
        loadRicette();
        loadAnalisiMarginalita();
      } else {
        toast.error(res.data.error || 'Errore nell\'analisi AI');
      }
    } catch (error) {
      toast.error('Errore AI: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzingIngredients(false);
    }
  };

  // Abbina vini direttamente alle fatture SENZA AI (molto più veloce)
  const [abbinandoVini, setAbbinandoVini] = useState(false);
  const handleAbbinaVini = async () => {
    setAbbinandoVini(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/abbina-vini`, {}, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        const { abbinati, non_abbinati, vini_fatture_disponibili } = res.data;
        if (abbinati > 0) {
          toast.success(`Abbinati ${abbinati} vini alle fatture!`);
        } else if (vini_fatture_disponibili === 0) {
          toast.error('Nessun vino trovato nelle fatture. Carica prima le fatture.');
        } else {
          toast.info(`Nessun nuovo abbinamento. ${non_abbinati} vini senza match.`);
        }
        loadRicette();
        loadAnalisiMarginalita();
      } else {
        toast.error(res.data.error || 'Errore abbinamento vini');
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAbbinandoVini(false);
    }
  };

  // Carica match da confermare (NON apre la modal automaticamente)
  const loadMatchDaConfermare = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ricette/match-da-confermare`, {
        headers: getAuthHeaders()
      });
      setMatchDaConfermare(res.data.matches || []);
      // NON apriamo la modal automaticamente - l'utente clicca sul banner
    } catch (error) {
      console.error('Errore caricamento match:', error);
    }
  };

  // Conferma un match manualmente
  const handleConfermaMatch = async (match, opzioneSelezionata) => {
    setLoadingMatch(true);
    try {
      await axios.post(`${API}/gestione/ricette/conferma-match`, null, {
        params: {
          ricetta_id: match.ricetta_id,
          ingrediente_idx: match.ingrediente_idx,
          prodotto_selezionato: opzioneSelezionata.prodotto,
          prezzo_kg: opzioneSelezionata.prezzo_kg,
          fornitore: opzioneSelezionata.fornitore
        },
        headers: getAuthHeaders()
      });
      toast.success('Match confermato!');
      // Rimuovi dalla lista
      setMatchDaConfermare(prev => prev.filter(m => 
        !(m.ricetta_id === match.ricetta_id && m.ingrediente_idx === match.ingrediente_idx)
      ));
      loadRicette();
    } catch (error) {
      toast.error('Errore conferma match');
    } finally {
      setLoadingMatch(false);
    }
  };

  // Cerca prodotti nelle fatture
  const cercaProdottiFatture = async (query) => {
    setLoadingProdotti(true);
    try {
      const res = await axios.get(`${API}/gestione/ordini/cerca-prodotti-fatture`, {
        params: { q: query, limit: 30 },
        headers: getAuthHeaders()
      });
      setProdottiTrovati(res.data.prodotti || []);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
      toast.error('Errore ricerca prodotti');
    } finally {
      setLoadingProdotti(false);
    }
  };

  // Apri modal ricerca prodotti per un ingrediente
  const openCercaProdotto = (ricettaId, idx, ingrediente) => {
    setEditingIngrediente({ ricettaId, idx, ...ingrediente });
    setSearchProdotti(ingrediente.nome || '');
    setProdottiTrovati([]);
    setShowProdottiModal(true);
    // Cerca subito con il nome ingrediente
    if (ingrediente.nome) {
      cercaProdottiFatture(ingrediente.nome.split(' ')[0]); // Cerca prima parola
    }
  };

  // Seleziona prodotto per ingrediente
  const selezionaProdottoPerIngrediente = async (prodotto) => {
    if (!editingIngrediente) return;
    
    try {
      await axios.put(
        `${API}/gestione/ricette/${editingIngrediente.ricettaId}/ingrediente/${editingIngrediente.idx}`,
        null,
        {
          params: {
            prodotto_fattura: prodotto.descrizione,
            prezzo_kg: prodotto.ultimo_prezzo || prodotto.prezzo || prodotto.prezzo_kg,
            fornitore: prodotto.fornitore || (prodotto.fornitori && prodotto.fornitori[0]) || ''
          },
          headers: getAuthHeaders()
        }
      );
      toast.success(`Abbinato: ${prodotto.descrizione}`);
      setShowProdottiModal(false);
      // Ricarica dettaglio ricetta
      if (selectedRicetta) {
        loadRicettaDetail(selectedRicetta);
      }
      loadRicette();
    } catch (error) {
      console.error('Errore abbinamento:', error);
      toast.error('Errore abbinamento prodotto');
    }
  };

  // Aggiorna quantità ingrediente
  const aggiornaQuantitaIngrediente = async (ricettaId, idx, nuovaQuantita) => {
    try {
      await axios.put(
        `${API}/gestione/ricette/${ricettaId}/ingrediente/${idx}`,
        null,
        {
          params: { quantita: nuovaQuantita },
          headers: getAuthHeaders()
        }
      );
      toast.success('Quantità aggiornata');
      // Ricarica dettaglio
      if (selectedRicetta) {
        loadRicettaDetail(selectedRicetta);
      }
    } catch (error) {
      console.error('Errore aggiornamento quantità:', error);
      toast.error('Errore aggiornamento quantità');
    }
  };

  // Aggiorna campo ricetta (nome, prezzo, categoria)
  const aggiornaRicetta = async (ricettaId, campo, valore) => {
    try {
      await axios.put(
        `${API}/gestione/ricette/${ricettaId}`,
        { [campo]: valore },
        { headers: getAuthHeaders() }
      );
      toast.success('Ricetta aggiornata');
      // Aggiorna stato locale
      setSelectedRicetta(prev => ({ ...prev, [campo]: valore }));
      loadRicette();
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  // Elimina ingrediente
  const eliminaIngrediente = async (ricettaId, idx) => {
    try {
      await axios.delete(
        `${API}/gestione/ricette/${ricettaId}/ingrediente/${idx}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Ingrediente eliminato');
      loadRicettaDetail(selectedRicetta);
      loadRicette();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Aggiungi ingrediente alla ricetta
  const aggiungiIngredienteARicetta = async (ricettaId, ingrediente) => {
    try {
      const res = await axios.post(
        `${API}/gestione/ricette/${ricettaId}/ingrediente`,
        ingrediente,
        { headers: getAuthHeaders() }
      );
      
      if (res.data.abbinato_automaticamente) {
        toast.success(`Ingrediente aggiunto e abbinato a: ${res.data.ingrediente?.prodotto_fattura}`);
      } else {
        toast.success('Ingrediente aggiunto (da abbinare manualmente)');
      }
      
      loadRicettaDetail(selectedRicetta);
      loadRicette();
    } catch (error) {
      console.error('Errore aggiunta ingrediente:', error);
      toast.error('Errore aggiunta ingrediente');
    }
  };

  // === SOTTO-RICETTE ===
  const [showSottoRicetteSearch, setShowSottoRicetteSearch] = useState(false);
  const [sottoRicetteDisponibili, setSottoRicetteDisponibili] = useState([]);
  const [searchSottoRicetta, setSearchSottoRicetta] = useState('');
  const [loadingSottoRicette, setLoadingSottoRicette] = useState(false);

  // Cerca sotto-ricette disponibili
  const cercaSottoRicette = async (search = '') => {
    setLoadingSottoRicette(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/sotto-ricette/disponibili`, {
        params: { search: search || undefined },
        headers: getAuthHeaders()
      });
      setSottoRicetteDisponibili(res.data.ricette || []);
    } catch (error) {
      console.error('Errore ricerca sotto-ricette:', error);
    } finally {
      setLoadingSottoRicette(false);
    }
  };

  // Aggiungi sotto-ricetta come ingrediente
  const aggiungiSottoRicetta = async (ricettaId, sottoRicettaId, quantita = 1, nomeDisplay = null) => {
    try {
      const res = await axios.post(
        `${API}/gestione/ricette/${ricettaId}/sotto-ricetta`,
        {
          sotto_ricetta_id: sottoRicettaId,
          quantita: quantita,
          nome_display: nomeDisplay
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(res.data.message);
      setShowSottoRicetteSearch(false);
      
      // Se siamo nel modale di edit, aggiorna il formData con il nuovo ingrediente
      if (editingRicetta && res.data.ingrediente) {
        setFormData(prev => ({
          ...prev,
          ingredienti: [...(prev.ingredienti || []), res.data.ingrediente]
        }));
        // Aggiorna anche editingRicetta
        setEditingRicetta(prev => ({
          ...prev,
          ingredienti: [...(prev?.ingredienti || []), res.data.ingrediente]
        }));
      }
      
      // Se siamo nel modale di dettaglio
      if (selectedRicetta) {
        loadRicettaDetail(selectedRicetta);
      }
      
      loadRicette();
    } catch (error) {
      console.error('Errore aggiunta sotto-ricetta:', error);
      toast.error(error.response?.data?.detail || 'Errore aggiunta sotto-ricetta');
    }
  };

  // Carica match da confermare all'avvio
  useEffect(() => {
    loadMatchDaConfermare();
  }, []);

  // === ANALISI FOOD COST DA MENU ===
  const analizzaMenuFoodCost = async () => {
    if (!analisiMenuFile) {
      toast.error('Seleziona un file (PDF, immagine o TXT)');
      return;
    }
    
    setAnalisiMenuLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', analisiMenuFile);
      
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      };
      console.log('🔍 Headers per analisi menu:', headers);
      
      const res = await axios.post(
        `${API}/gestione/ricette/analizza-food-cost-menu`,
        formData,
        { 
          headers,
          timeout: 120000 // 2 minuti timeout
        }
      );
      
      console.log('📊 Risposta analisi menu:', res.data);
      setAnalisiMenuRisultato(res.data);
      toast.success(`Analizzati ${res.data.piatti_analizzati} piatti!`);
    } catch (error) {
      console.error('Errore analisi menu:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi del menu');
    } finally {
      setAnalisiMenuLoading(false);
    }
  };

  // Carica prodotti dalle fatture per modifica abbinamento
  const loadProdottiFatture = async (searchTerm = '') => {
    setLoadingProdotti(true);
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const res = await axios.get(`${API}/gestione/ricette/prodotti-fatture${params}`, {
        headers: getAuthHeaders()
      });
      setProdottiFatture(res.data.prodotti || []);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      
    } finally {
      setLoadingProdotti(false);
    }
  };

  // Apri modal modifica abbinamento
  const apriModificaAbbinamento = (piattoIdx, ingredienteIdx, ingrediente) => {
    setIngredienteSelezionato({ piattoIdx, ingredienteIdx, ingrediente });
    setSearchProdotto(ingrediente.nome);
    setShowModificaAbbinamento(true);
    loadProdottiFatture(ingrediente.nome);
  };

  // Applica nuovo abbinamento
  const applicaNuovoAbbinamento = (prodotto) => {
    if (!ingredienteSelezionato) return;
    
    const { piattoIdx, ingredienteIdx, ingrediente } = ingredienteSelezionato;
    const quantita_g = ingrediente.quantita_g || 100;
    const prezzo_kg = prodotto.prezzo_kg;
    const costo_netto = (quantita_g / 1000) * prezzo_kg;
    const aliquota = 0.10; // Default 10%
    const costo_lordo = costo_netto * (1 + aliquota);
    
    setAnalisiMenuRisultato(prev => ({
      ...prev,
      risultati: prev.risultati.map((pl, pIdx) => {
        if (pIdx === piattoIdx) {
          const nuoviIng = pl.ingredienti.map((ing, iIdx) => {
            if (iIdx === ingredienteIdx) {
              return {
                ...ing,
                prezzo_kg: prezzo_kg,
                costo_netto: costo_netto,
                costo_lordo: costo_lordo,
                aliquota_iva: aliquota * 100,
                fornitore: prodotto.fornitore,
                prodotto_fattura: prodotto.descrizione,
                fonte_prezzo: 'fatture'
              };
            }
            return ing;
          });
          const nuovoCostoLordo = nuoviIng.reduce((s, ing) => s + (ing.costo_lordo || 0), 0);
          const nuovoFoodCostPerc = pl.prezzo_vendita > 0 ? (nuovoCostoLordo / pl.prezzo_vendita * 100) : 0;
          return {
            ...pl,
            ingredienti: nuoviIng,
            costo_lordo: nuovoCostoLordo,
            costo_netto: nuoviIng.reduce((s, ing) => s + (ing.costo_netto || 0), 0),
            food_cost_perc: nuovoFoodCostPerc,
            margine_euro: pl.prezzo_vendita - nuovoCostoLordo,
            margine_perc: pl.prezzo_vendita > 0 ? ((pl.prezzo_vendita - nuovoCostoLordo) / pl.prezzo_vendita * 100) : 0,
            valutazione: nuovoFoodCostPerc <= 25 ? 'ottimo' : nuovoFoodCostPerc <= 35 ? 'buono' : 'da_rivedere'
          };
        }
        return pl;
      })
    }));
    
    setShowModificaAbbinamento(false);
    setIngredienteSelezionato(null);
    toast.success(`Abbinamento aggiornato: ${ingrediente.nome} → ${prodotto.descrizione}`);
  };

  // Imposta prezzo manuale
  const impostaPrezzoManuale = () => {
    if (!ingredienteSelezionato) return;
    
    const { piattoIdx, ingredienteIdx, ingrediente } = ingredienteSelezionato;
    const prezzoStr = prompt(`Inserisci il prezzo al kg per "${ingrediente.nome}":`, ingrediente.prezzo_kg || '10');
    if (!prezzoStr) return;
    
    const prezzo_kg = parseFloat(prezzoStr);
    if (isNaN(prezzo_kg) || prezzo_kg < 0) {
      toast.error('Prezzo non valido');
      return;
    }
    
    const quantita_g = ingrediente.quantita_g || 100;
    const costo_netto = (quantita_g / 1000) * prezzo_kg;
    const aliquota = 0.10;
    const costo_lordo = costo_netto * (1 + aliquota);
    
    setAnalisiMenuRisultato(prev => ({
      ...prev,
      risultati: prev.risultati.map((pl, pIdx) => {
        if (pIdx === piattoIdx) {
          const nuoviIng = pl.ingredienti.map((ing, iIdx) => {
            if (iIdx === ingredienteIdx) {
              return {
                ...ing,
                prezzo_kg: prezzo_kg,
                costo_netto: costo_netto,
                costo_lordo: costo_lordo,
                fornitore: null,
                prodotto_fattura: null,
                fonte_prezzo: 'manuale'
              };
            }
            return ing;
          });
          const nuovoCostoLordo = nuoviIng.reduce((s, ing) => s + (ing.costo_lordo || 0), 0);
          const nuovoFoodCostPerc = pl.prezzo_vendita > 0 ? (nuovoCostoLordo / pl.prezzo_vendita * 100) : 0;
          return {
            ...pl,
            ingredienti: nuoviIng,
            costo_lordo: nuovoCostoLordo,
            costo_netto: nuoviIng.reduce((s, ing) => s + (ing.costo_netto || 0), 0),
            food_cost_perc: nuovoFoodCostPerc,
            margine_euro: pl.prezzo_vendita - nuovoCostoLordo,
            margine_perc: pl.prezzo_vendita > 0 ? ((pl.prezzo_vendita - nuovoCostoLordo) / pl.prezzo_vendita * 100) : 0,
            valutazione: nuovoFoodCostPerc <= 25 ? 'ottimo' : nuovoFoodCostPerc <= 35 ? 'buono' : 'da_rivedere'
          };
        }
        return pl;
      })
    }));
    
    setShowModificaAbbinamento(false);
    setIngredienteSelezionato(null);
    toast.success(`Prezzo manuale impostato: €${prezzo_kg.toFixed(2)}/kg`);
  };

  // Carica analisi marginalità
  const loadAnalisiMarginalita = async () => {
    setLoadingAnalisi(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/analisi-marginalita`, {
        headers: getAuthHeaders()
      });
      setAnalisiMarginalita(res.data);
    } catch (error) {
      
    } finally {
      setLoadingAnalisi(false);
    }
  };

  // Carica dettaglio rese di una ricetta
  const loadDettaglioRese = async (ricettaId) => {
    setLoadingRicettaRese(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/dettaglio-rese/${ricettaId}`, {
        headers: getAuthHeaders()
      });
      setSelectedRicettaRese(res.data);
    } catch (error) {
      
      console.error(error);
    } finally {
      setLoadingRicettaRese(false);
    }
  };

  // Aggiorna tipo utilizzo ingrediente (INTERO/LAVORATO)
  const updateTipoUtilizzo = async (ricettaId, ingredienteNome, nuovoTipo) => {
    try {
      await axios.put(`${API}/gestione/ricette/tipo-utilizzo/${ricettaId}`, {
        ingrediente_nome: ingredienteNome,
        tipo_utilizzo: nuovoTipo
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`${ingredienteNome}: ${nuovoTipo === 'INTERO' ? '🐚 Servito intero' : '🔪 Sgusciato/Pulito'}`);
      
      // Ricarica il dettaglio
      loadDettaglioRese(ricettaId);
      // Ricarica anche l'analisi
      loadAnalisiMarginalita();
    } catch (error) {
      toast.error('Errore aggiornamento');
      console.error(error);
    }
  };

  // === BEVERAGE FUNCTIONS ===
  const loadBeverageList = async () => {
    setLoadingBeverage(true);
    try {
      const params = new URLSearchParams();
      // Solo filtro per categoria lato server, la ricerca testo è locale
      if (beverageCategoria) params.append('categoria', beverageCategoria);
      
      const res = await axios.get(`${API}/gestione/beverage?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setBeverageList(res.data.prodotti || []);
      setBeverageStats(res.data.statistiche_categoria || {});
    } catch (error) {
      console.error('Error loading beverage:', error);
      
    } finally {
      setLoadingBeverage(false);
    }
  };

  const updateBeveragePrice = async (prodottoId, prezzoVendita) => {
    try {
      await axios.put(`${API}/gestione/beverage/${encodeURIComponent(prodottoId)}`, {
        prezzo_vendita: parseFloat(prezzoVendita)
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Prezzo aggiornato');
      setEditingBeverage(null);
      loadBeverageList();
    } catch (error) {
      toast.error('Errore aggiornamento prezzo');
    }
  };

  // Aggiorna porzioni e prezzo per porzione
  const updateBeveragePortions = async (prodottoId, prezzoVenditaPorzione, porzioniPerBottiglia) => {
    try {
      await axios.put(`${API}/gestione/beverage/${encodeURIComponent(prodottoId)}`, {
        prezzo_vendita_porzione: parseFloat(prezzoVenditaPorzione),
        porzioni_per_bottiglia: parseInt(porzioniPerBottiglia)
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Porzioni aggiornate');
      setEditingBeverage(null);
      loadBeverageList();
    } catch (error) {
      toast.error('Errore aggiornamento porzioni');
    }
  };

  // === STAMPA CARTA VINI ===
  
  // Carica vini disponibili dalle fatture per correzione
  const loadViniFattureDisponibili = async () => {
    setLoadingViniFatture(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/vini-fatture-disponibili`, { headers: getAuthHeaders() });
      setViniFattureDisponibili(res.data.vini || []);
    } catch (error) {
      console.error('Error loading vini fatture:', error);
      
    }
    setLoadingViniFatture(false);
  };
  
  // Apri modal correzione
  const handleApriCorrezioneVino = (vino) => {
    setVinoCorrezione(vino);
    setSearchViniFatture('');
    setShowCorreggiVino(true);
    loadViniFattureDisponibili();
  };
  
  // Applica correzione abbinamento
  const handleCorreggiAbbinamento = async (prodottoFattura) => {
    try {
      const res = await axios.post(`${API}/gestione/ricette/vino-correggi-abbinamento`, {
        vino_menu_id: vinoCorrezione.id || vinoCorrezione._id,
        prodotto_fattura_nome: prodottoFattura.nome,
        prodotto_fattura_prezzo: prodottoFattura.prezzo_unitario,
        prodotto_fattura_fornitore: prodottoFattura.fornitore,
        prezzo_vendita: vinoCorrezione.prezzo_vendita || vinoCorrezione.prezzo || 0
      }, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        toast.success(`✅ Abbinamento corretto: ${prodottoFattura.nome}`);
        setShowCorreggiVino(false);
        loadViniList(); // Ricarica lista
      } else {
        toast.error(res.data.error || 'Errore correzione');
      }
    } catch (error) {
      console.error('Error correcting match:', error);
      toast.error('Errore correzione abbinamento');
    }
  };
  
  // Rimuovi abbinamento
  const handleRimuoviAbbinamento = async () => {
    try {
      const res = await axios.post(`${API}/gestione/ricette/vino-correggi-abbinamento`, {
        vino_menu_id: vinoCorrezione.id || vinoCorrezione._id,
        rimuovi_abbinamento: true
      }, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        toast.success('Abbinamento rimosso');
        setShowCorreggiVino(false);
        loadViniList();
      } else {
        toast.error(res.data.error || 'Errore rimozione');
      }
    } catch (error) {
      console.error('Error removing match:', error);
      toast.error('Errore rimozione abbinamento');
    }
  };
  
  // Genera descrizione con AI
  const handleGeneraDescrizioneAI = async () => {
    if (!vinoCorrezione) return;
    
    setGenerandoDescrizione(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/vino-genera-descrizione`, {
        nome_vino: vinoCorrezione.nome,
        regione: vinoCorrezione.regione || '',
        tipo_vino: vinoCorrezione.tipo_vino || ''
      }, { headers: getAuthHeaders() });
      
      if (res.data.success) {
        // Aggiorna lo stato locale
        setVinoCorrezione(prev => ({
          ...prev,
          vitigno: res.data.uvaggio,
          descrizione: res.data.descrizione
        }));
        toast.success('Descrizione generata con AI!');
      } else {
        toast.error(res.data.error || 'Errore generazione');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Errore generazione descrizione');
    }
    setGenerandoDescrizione(false);
  };
  
  // Genera descrizioni per TUTTI i vini
  const [progressGenerazione, setProgressGenerazione] = useState({ current: 0, total: 0, vinoCorrente: '' });
  
  const handleGeneraTutteDescrizioni = async () => {
    if (generandoTutte) return;
    
    // Filtra vini senza descrizione
    const viniSenzaDesc = viniList.filter(v => !v.descrizione || v.descrizione.trim() === '');
    
    if (viniSenzaDesc.length === 0) {
      toast.info('Tutti i vini hanno già una descrizione!');
      return;
    }
    
    const conferma = window.confirm(
      `Vuoi generare automaticamente le descrizioni AI per ${viniSenzaDesc.length} vini senza descrizione?\n\nQuesta operazione può richiedere alcuni minuti.`
    );
    if (!conferma) return;
    
    setGenerandoTutte(true);
    setProgressGenerazione({ current: 0, total: viniSenzaDesc.length, vinoCorrente: '' });
    
    let successi = 0;
    let errori = 0;
    
    for (let i = 0; i < viniSenzaDesc.length; i++) {
      const vino = viniSenzaDesc[i];
      setProgressGenerazione({ 
        current: i + 1, 
        total: viniSenzaDesc.length, 
        vinoCorrente: vino.nome 
      });
      
      try {
        // Genera descrizione
        const resGen = await axios.post(`${API}/gestione/ricette/vino-genera-descrizione`, {
          nome_vino: vino.nome,
          regione: vino.regione || '',
          tipo_vino: vino.tipo_vino || ''
        }, { headers: getAuthHeaders() });
        
        if (resGen.data.success) {
          // Salva descrizione
          await axios.post(`${API}/gestione/ricette/vino-correggi-abbinamento`, {
            vino_menu_id: vino.id || vino._id,
            descrizione: resGen.data.descrizione,
            vitigno: resGen.data.uvaggio
          }, { headers: getAuthHeaders() });
          
          successi++;
        } else {
          errori++;
        }
      } catch (error) {
        console.error(`Errore per ${vino.nome}:`, error);
        errori++;
      }
      
      // Piccola pausa per non sovraccaricare l'API
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setGenerandoTutte(false);
    setProgressGenerazione({ current: 0, total: 0, vinoCorrente: '' });
    
    // Ricarica lista vini per mostrare le nuove descrizioni
    try {
      await loadViniList();
      console.log('Lista vini ricaricata dopo generazione batch');
    } catch (err) {
      console.error('Errore ricaricamento lista vini:', err);
    }
    
    toast.success(`Completato! ${successi} descrizioni generate, ${errori} errori`);
  };
  
  const handleStampaCartaVini = () => {
    // Raggruppa vini per tipo e poi per regione
    // Se separaChampagne è attivo, creiamo categorie separate per Champagne/Francia e Bollicine Italiane
    const viniPerTipo = {
      champagne: [], // Champagne e bollicine francesi
      bollicine: [], // Franciacorta, Prosecco, Metodo Classico italiano
      bianchi: [],
      rosati: [],
      rossi: []
    };
    
    viniList.forEach(vino => {
      const tipo = (vino.tipo_vino || '').toLowerCase();
      const regione = (vino.regione || '').toLowerCase();
      const nome = (vino.nome || '').toLowerCase();
      
      if (['bollicine', 'spumante', 'champagne', 'prosecco', 'franciacorta', 'metodo_classico'].includes(tipo)) {
        // Separa Champagne e bollicine francesi dalle italiane
        const isChampagne = regione === 'champagne' || 
                           regione === 'borgogna' || 
                           nome.includes('champagne') ||
                           nome.includes('cremant');
        
        if (stampaSettings.separaChampagne && isChampagne) {
          viniPerTipo.champagne.push(vino);
        } else {
          viniPerTipo.bollicine.push(vino);
        }
      } else if (['bianco', 'white'].includes(tipo)) {
        viniPerTipo.bianchi.push(vino);
      } else if (['rosato', 'rose', 'rosé'].includes(tipo)) {
        viniPerTipo.rosati.push(vino);
      } else {
        viniPerTipo.rossi.push(vino);
      }
    });
    
    // Funzione per raggruppare per regione
    const raggruppaPerRegione = (vini) => {
      const perRegione = {};
      vini.forEach(v => {
        const regione = v.regione || v.nazione || 'Altro';
        if (!perRegione[regione]) perRegione[regione] = [];
        perRegione[regione].push(v);
      });
      return Object.entries(perRegione).sort((a, b) => a[0].localeCompare(b[0]));
    };
    
    // Calcola dimensione font in base al numero di pagine desiderate
    const calcolaStileStampa = () => {
      const totaleVini = viniList.length;
      const numPagine = parseInt(stampaSettings.numeroPagine) || 0;
      
      // Se non specificato o auto, usa valori di default
      if (!numPagine || numPagine <= 0) {
        return { fontSize: '9pt', lineHeight: '1.25', marginVino: '4px', headerSize: '11pt', regioneSize: '9pt', sectionMargin: '15px' };
      }
      
      // Stima più precisa: header/sezioni occupano ~15% dello spazio
      // Ogni pagina A4 con margini 10mm ha ~277mm di altezza utile
      // Con font 8pt, circa 35-40 righe per pagina
      const viniPerPagina = Math.ceil(totaleVini / numPagine);
      
      // Calcolo più aggressivo per rispettare il numero di pagine
      if (viniPerPagina > 40) {
        // Ultra compatto - per tanti vini in poche pagine
        return { fontSize: '5.5pt', lineHeight: '1.0', marginVino: '0px', headerSize: '7pt', regioneSize: '6pt', sectionMargin: '3px' };
      } else if (viniPerPagina > 35) {
        return { fontSize: '6pt', lineHeight: '1.02', marginVino: '0px', headerSize: '8pt', regioneSize: '6.5pt', sectionMargin: '4px' };
      } else if (viniPerPagina > 30) {
        return { fontSize: '6.5pt', lineHeight: '1.05', marginVino: '1px', headerSize: '8pt', regioneSize: '7pt', sectionMargin: '5px' };
      } else if (viniPerPagina > 25) {
        return { fontSize: '7pt', lineHeight: '1.08', marginVino: '1px', headerSize: '9pt', regioneSize: '7.5pt', sectionMargin: '6px' };
      } else if (viniPerPagina > 20) {
        return { fontSize: '7.5pt', lineHeight: '1.1', marginVino: '2px', headerSize: '9pt', regioneSize: '8pt', sectionMargin: '8px' };
      } else if (viniPerPagina > 16) {
        return { fontSize: '8pt', lineHeight: '1.12', marginVino: '2px', headerSize: '10pt', regioneSize: '8pt', sectionMargin: '10px' };
      } else if (viniPerPagina > 12) {
        return { fontSize: '8.5pt', lineHeight: '1.15', marginVino: '3px', headerSize: '10pt', regioneSize: '8.5pt', sectionMargin: '12px' };
      } else if (viniPerPagina > 8) {
        return { fontSize: '9pt', lineHeight: '1.2', marginVino: '4px', headerSize: '11pt', regioneSize: '9pt', sectionMargin: '15px' };
      }
      return { fontSize: '10pt', lineHeight: '1.25', marginVino: '5px', headerSize: '12pt', regioneSize: '9pt', sectionMargin: '18px' };
    };
    
    const stile = calcolaStileStampa();
    
    // Genera HTML per una sezione
    const generaSezioneHTML = (titolo, vini, coloreAccento) => {
      if (vini.length === 0) return '';
      
      const perRegione = raggruppaPerRegione(vini);
      
      return `
        <div class="sezione-tipo">
          <div class="tipo-header" style="border-color: ${coloreAccento};">
            <h2>${titolo}</h2>
          </div>
          ${perRegione.map(([regione, viniRegione]) => `
            <div class="regione-section">
              <h3 class="regione-title">${regione.toUpperCase()}</h3>
              ${viniRegione.sort((a, b) => (a.nome || '').localeCompare(b.nome || '')).map(vino => `
                <div class="vino-row">
                  <div class="vino-left">
                    <div class="vino-nome">${vino.nome || 'Vino'}</div>
                    ${stampaSettings.mostraDescrizione && (vino.cantina || vino.produttore) ? `
                      <div class="vino-cantina">${vino.cantina || vino.produttore || ''}</div>
                    ` : ''}
                    ${vino.vitigno || vino.uve ? `<div class="vino-uve">(${vino.vitigno || vino.uve})</div>` : ''}
                    ${stampaSettings.mostraDescrizione && vino.descrizione ? `<div class="vino-desc">${vino.descrizione}</div>` : ''}
                  </div>
                  <div class="vino-prezzo">
                    ${stampaSettings.mostraPrezzi && vino.prezzo_vendita ? `€ ${vino.prezzo_vendita.toFixed(2).replace('.', ',')}` : ''}
                  </div>
                </div>
              `).join('')}
            </div>
          `).join('')}
        </div>
      `;
    };
    
    // Crea contenuto HTML per la stampa
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Carta dei Vini${stampaSettings.nomeRistorante ? ' - ' + stampaSettings.nomeRistorante : ''}</title>
        <style>
          @page { 
            size: A4; 
            margin: ${stampaSettings.margineSup}mm ${stampaSettings.margineLat}mm; 
          }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Garamond', 'Georgia', 'Times New Roman', serif; 
            background: #fff;
            color: #1a1a1a;
            line-height: ${stile.lineHeight};
            font-size: ${stile.fontSize};
          }
          
          /* INTESTAZIONE INLINE (non copertina) */
          .header-inline {
            text-align: center;
            padding: 15px 0;
            margin-bottom: 20px;
            border-bottom: 2px solid #8b4513;
          }
          .header-inline h1 {
            font-size: ${stile.headerSize};
            font-weight: 400;
            letter-spacing: 4px;
            text-transform: uppercase;
            color: #8b4513;
          }
          .header-inline .restaurant-name {
            font-size: calc(${stile.headerSize} * 0.6);
            letter-spacing: 2px;
            color: #5c3317;
            margin-top: 5px;
          }
          
          /* COPERTINA (opzionale, pagina dedicata) */
          .cover {
            height: 100vh;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            background: linear-gradient(180deg, #f8f6f0 0%, #ebe8df 100%);
            page-break-after: always;
          }
          .cover h1 {
            font-size: 32pt;
            font-weight: 400;
            letter-spacing: 6px;
            text-transform: uppercase;
            color: #8b4513;
            margin-bottom: 20px;
          }
          .cover .subtitle {
            font-size: 24pt;
            letter-spacing: 8px;
            color: #5c3317;
            font-weight: 300;
            margin-bottom: 40px;
          }
          .cover .quote {
            font-style: italic;
            font-size: 14pt;
            color: #666;
            margin-top: 60px;
          }
          .cover .restaurant-name {
            font-size: 16pt;
            letter-spacing: 4px;
            color: #8b4513;
            margin-top: 40px;
            font-weight: 600;
          }
          
          /* SEZIONI TIPO VINO - elegante e compatto */
          .sezione-tipo {
            margin-bottom: ${stile.sectionMargin || '12px'};
          }
          .tipo-header {
            text-align: center;
            padding: 3px 0;
            margin-bottom: ${parseInt(stile.sectionMargin) / 2 || 6}px;
            border-bottom: 1px solid #c9a574;
          }
          .tipo-header h2 {
            font-size: ${stile.headerSize};
            font-weight: 500;
            letter-spacing: 3px;
            text-transform: uppercase;
            color: #6b4423;
            margin: 0;
          }
          
          /* Evita che il titolo sezione resti solo a fine pagina */
          .tipo-header {
            page-break-after: avoid;
            break-after: avoid;
          }
          
          /* REGIONI - compatte */
          .regione-section {
            margin-bottom: ${parseInt(stile.sectionMargin) / 2 || 6}px;
          }
          .regione-title {
            font-size: ${stile.regioneSize};
            font-weight: 600;
            letter-spacing: 1.5px;
            color: #8b4513;
            margin-bottom: 2px;
            padding-bottom: 1px;
            border-bottom: 1px dotted #d4a574;
            page-break-after: avoid;
            break-after: avoid;
          }
          
          /* SINGOLO VINO - compatto, non spezzare tra pagine */
          .vino-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: ${stile.marginVino} 0;
            border-bottom: 1px dotted #e0d5c5;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .vino-row:last-child {
            border-bottom: none;
          }
          .vino-left {
            flex: 1;
            padding-right: 10px;
          }
          .vino-nome {
            font-size: calc(${stile.fontSize} * 1.1);
            font-weight: 700;
            color: #1a1a1a;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .vino-cantina {
            font-size: calc(${stile.fontSize} * 0.9);
            color: #555;
            font-style: normal;
            margin-top: 1px;
          }
          .vino-uve {
            font-size: calc(${stile.fontSize} * 0.85);
            color: #777;
            font-style: italic;
            margin-top: 1px;
          }
          .vino-desc {
            font-size: calc(${stile.fontSize} * 0.85);
            color: #666;
            font-style: italic;
            margin-top: 2px;
            line-height: 1.2;
          }
          .vino-prezzo {
            font-size: calc(${stile.fontSize} * 1.1);
            font-weight: 700;
            color: #5c3317;
            min-width: 60px;
            text-align: right;
            white-space: nowrap;
          }
          
          /* FOOTER */
          .footer {
            text-align: center;
            margin-top: 50px;
            padding-top: 20px;
            border-top: 1px solid #d4a574;
            font-size: 9pt;
            color: #888;
            font-style: italic;
          }
          
          /* PRINT BUTTON */
          .print-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #8b4513;
            color: white;
            border: none;
            padding: 15px 30px;
            font-size: 14px;
            cursor: pointer;
            border-radius: 8px;
            z-index: 1000;
            font-family: sans-serif;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          }
          .print-btn:hover { background: #5c3317; }
          @media print {
            .print-btn { display: none; }
          }
        </style>
      </head>
      <body>
        <button class="print-btn" onclick="window.print()">🖨️ Stampa</button>
        
        ${stampaSettings.mostraCopertina ? `
        <!-- COPERTINA (pagina dedicata) -->
        <div class="cover">
          ${stampaSettings.nomeRistorante ? `<div class="restaurant-name">${stampaSettings.nomeRistorante}</div>` : ''}
          <h1>Carta dei Vini</h1>
          <div class="subtitle">WINE LIST</div>
          <div class="quote">"Il vino è poesia imbottigliata"</div>
        </div>
        ` : `
        ${stampaSettings.nomeRistorante ? `
        <!-- INTESTAZIONE INLINE (solo nome ristorante) -->
        <div class="header-inline">
          <div class="restaurant-name">${stampaSettings.nomeRistorante}</div>
        </div>
        ` : ''}
        `}
        
        <!-- CHAMPAGNE (se separato) -->
        ${stampaSettings.separaChampagne ? generaSezioneHTML('CHAMPAGNE', viniPerTipo.champagne, '#c9a227') : ''}
        
        <!-- BOLLICINE ITALIANE -->
        ${generaSezioneHTML(stampaSettings.separaChampagne ? 'BOLLICINE ITALIANE' : 'BOLLICINE', viniPerTipo.bollicine, '#d4af37')}
        
        <!-- VINI BIANCHI -->
        ${generaSezioneHTML('VINI BIANCHI', viniPerTipo.bianchi, '#f5deb3')}
        
        <!-- VINI ROSATI -->
        ${generaSezioneHTML('VINI ROSATI', viniPerTipo.rosati, '#ffb6c1')}
        
        <!-- VINI ROSSI -->
        ${generaSezioneHTML('VINI ROSSI', viniPerTipo.rossi, '#8b0000')}
      </body>
      </html>
    `;
    
    // Apri nuova finestra per la stampa
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // === VINI FUNCTIONS ===
  const loadViniList = async () => {
    setLoadingVini(true);
    try {
      const params = new URLSearchParams();
      if (viniSearch) params.append('search', viniSearch);
      if (viniNazione) params.append('nazione', viniNazione);
      if (viniRegione) params.append('regione', viniRegione);
      if (viniTipo) params.append('tipo', viniTipo);
      if (viniFornitore) params.append('fornitore', viniFornitore);
      params.append('limit', '300');
      
      const res = await axios.get(`${API}/gestione/vini?${params.toString()}`, { headers: getAuthHeaders() });
      setViniList(res.data.vini || []);
      setViniStats(res.data.statistiche || null);
      setViniFiltri(res.data.filtri_disponibili || { nazioni: [], regioni: [], tipi: [], fornitori: [] });
    } catch (error) {
      console.error('Error loading vini:', error);
      
    } finally {
      setLoadingVini(false);
    }
  };

  const getVinoTipoLabel = (tipo) => {
    const labels = {
      'rosso': 'Rosso',
      'bianco': 'Bianco',
      'rosato': '🌸 Rosato',
      'bollicine': 'Bollicine',
      'spumante': 'Spumante',
      'champagne': 'Champagne',
      'prosecco': 'Prosecco',
      'altro': '🍇 Altro'
    };
    return labels[tipo?.toLowerCase()] || tipo || 'Altro';
  };

  const getVinoMarginBg = (margine) => {
    if (margine >= 60) return 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400';
    if (margine >= 40) return 'bg-blue-500/20 border-blue-500/30 text-blue-400';
    if (margine >= 20) return 'bg-amber-500/20 border-amber-500/30 text-amber-400';
    return 'bg-red-500/20 border-red-500/30 text-red-400';
  };

  // Stato per editing avanzato (porzioni)
  const [editingPortions, setEditingPortions] = useState(null);

  const getBeverageCategoryIcon = (categoria) => {
    const icons = {
      'vino': <Wine className="h-4 w-4" />,
      'birra': <Beer className="h-4 w-4" />,
      'soft_drink': <GlassWater className="h-4 w-4" />,
      'acqua': <Droplets className="h-4 w-4" />,
      'liquore': <Wine className="h-4 w-4" />,
      'caffe': <Coffee className="h-4 w-4" />,
      'altro': <Package className="h-4 w-4" />
    };
    return icons[categoria] || <Package className="h-4 w-4" />;
  };

  const getBeverageCategoryColor = (categoria) => {
    const colors = {
      'vino': 'from-purple-500 to-pink-500',
      'birra': 'from-amber-500 to-yellow-500',
      'soft_drink': 'from-cyan-500 to-blue-500',
      'acqua': 'from-blue-400 to-cyan-400',
      'liquore': 'from-orange-500 to-red-500',
      'caffe': 'from-amber-700 to-yellow-700',
      'altro': 'from-slate-500 to-gray-500'
    };
    return colors[categoria] || 'from-slate-500 to-gray-500';
  };

  const getBeverageMarginColor = (margine) => {
    if (margine >= 70) return 'text-emerald-400';
    if (margine >= 50) return 'text-blue-400';
    if (margine >= 30) return 'text-amber-400';
    return 'text-red-400';
  };

  const getBeverageMarginBg = (margine) => {
    if (margine >= 70) return 'bg-emerald-500/20 border-emerald-500/30';
    if (margine >= 50) return 'bg-blue-500/20 border-blue-500/30';
    if (margine >= 30) return 'bg-amber-500/20 border-amber-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  // Stato per analisi beverage
  const [beverageAnalisi, setBeverageAnalisi] = useState(null);
  const [loadingBeverageAnalisi, setLoadingBeverageAnalisi] = useState(false);
  const [beverageAnalisiFilter, setBeverageAnalisiFilter] = useState(''); // '', 'ottimo', 'buono', 'attenzione', 'critico'

  // Stato per Cocktail
  const [cocktailList, setCocktailList] = useState([]);
  const [loadingCocktails, setLoadingCocktails] = useState(false);
  const [showCocktailModal, setShowCocktailModal] = useState(false);
  const [editingCocktail, setEditingCocktail] = useState(null);
  const [cocktailIngredientiSearch, setCocktailIngredientiSearch] = useState('');
  const [availableIngredienti, setAvailableIngredienti] = useState([]);
  const [cocktailSearch, setCocktailSearch] = useState('');
  const [newCocktail, setNewCocktail] = useState({
    nome: '',
    categoria: 'aperitivo',
    prezzo_vendita: 0,
    ingredienti: [],
    note: ''
  });

  const loadBeverageAnalisi = async () => {
    setLoadingBeverageAnalisi(true);
    try {
      const res = await axios.get(`${API}/gestione/beverage/analisi`, {
        headers: getAuthHeaders()
      });
      setBeverageAnalisi(res.data);
    } catch (error) {
      console.error('Error loading beverage analisi:', error);
      
    } finally {
      setLoadingBeverageAnalisi(false);
    }
  };

  // Funzioni per Cocktail
  const loadCocktails = async () => {
    setLoadingCocktails(true);
    try {
      const res = await axios.get(`${API}/gestione/cocktails`, {
        headers: getAuthHeaders(),
        params: { search: cocktailSearch || undefined }
      });
      setCocktailList(res.data.cocktails || []);
    } catch (error) {
      console.error('Error loading cocktails:', error);
      
    } finally {
      setLoadingCocktails(false);
    }
  };

  const loadAvailableIngredienti = async () => {
    try {
      const res = await axios.get(`${API}/gestione/cocktails/ingredienti-disponibili`, {
        headers: getAuthHeaders()
      });
      setAvailableIngredienti(res.data.ingredienti || []);
    } catch (error) {
      console.error('Error loading ingredienti:', error);
    }
  };

  const handleCreateCocktail = async () => {
    if (!newCocktail.nome || newCocktail.ingredienti.length === 0) {
      toast.error('Inserisci nome e almeno un ingrediente');
      return;
    }
    try {
      await axios.post(`${API}/gestione/cocktails`, newCocktail, {
        headers: getAuthHeaders()
      });
      toast.success('Cocktail creato!');
      setShowCocktailModal(false);
      setNewCocktail({ nome: '', categoria: 'aperitivo', prezzo_vendita: 0, ingredienti: [], note: '' });
      loadCocktails();
    } catch (error) {
      console.error('Error creating cocktail:', error);
      toast.error(error.response?.data?.detail || 'Errore creazione cocktail');
    }
  };

  const handleUpdateCocktail = async () => {
    if (!editingCocktail) return;
    try {
      await axios.put(`${API}/gestione/cocktails/${editingCocktail.id}`, {
        nome: editingCocktail.nome,
        categoria: editingCocktail.categoria,
        prezzo_vendita: editingCocktail.prezzo_vendita,
        ingredienti: editingCocktail.ingredienti,
        note: editingCocktail.note
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Cocktail aggiornato!');
      setEditingCocktail(null);
      loadCocktails();
    } catch (error) {
      console.error('Error updating cocktail:', error);
      toast.error('Errore aggiornamento cocktail');
    }
  };

  const handleDeleteCocktail = async (cocktailId) => {
    if (!window.confirm('Eliminare questo cocktail?')) return;
    try {
      await axios.delete(`${API}/gestione/cocktails/${cocktailId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Cocktail eliminato');
      loadCocktails();
    } catch (error) {
      console.error('Error deleting cocktail:', error);
      toast.error('Errore eliminazione cocktail');
    }
  };

  const addIngredienteToCocktail = (ingrediente, target = 'new') => {
    const newIng = {
      beverage_id: ingrediente.id,
      nome: ingrediente.nome,
      quantita_ml: 30, // default 30ml
      costo_per_ml: ingrediente.costo_per_ml,
      prezzo_bottiglia: ingrediente.prezzo_bottiglia,
      ml_bottiglia: ingrediente.ml_bottiglia
    };
    if (target === 'new') {
      setNewCocktail(prev => ({
        ...prev,
        ingredienti: [...prev.ingredienti, newIng]
      }));
    } else if (editingCocktail) {
      setEditingCocktail(prev => ({
        ...prev,
        ingredienti: [...prev.ingredienti, newIng]
      }));
    }
  };

  const removeIngredienteFromCocktail = (index, target = 'new') => {
    if (target === 'new') {
      setNewCocktail(prev => ({
        ...prev,
        ingredienti: prev.ingredienti.filter((_, i) => i !== index)
      }));
    } else if (editingCocktail) {
      setEditingCocktail(prev => ({
        ...prev,
        ingredienti: prev.ingredienti.filter((_, i) => i !== index)
      }));
    }
  };

  const updateIngredienteQuantita = (index, quantita, target = 'new') => {
    if (target === 'new') {
      setNewCocktail(prev => ({
        ...prev,
        ingredienti: prev.ingredienti.map((ing, i) => 
          i === index ? { ...ing, quantita_ml: parseFloat(quantita) || 0 } : ing
        )
      }));
    } else if (editingCocktail) {
      setEditingCocktail(prev => ({
        ...prev,
        ingredienti: prev.ingredienti.map((ing, i) => 
          i === index ? { ...ing, quantita_ml: parseFloat(quantita) || 0 } : ing
        )
      }));
    }
  };

  const calculateCocktailCost = (ingredienti) => {
    return ingredienti.reduce((sum, ing) => {
      const costoPerMl = ing.costo_per_ml || (ing.prezzo_bottiglia / ing.ml_bottiglia) || 0;
      return sum + (costoPerMl * (ing.quantita_ml || 0));
    }, 0);
  };

  useEffect(() => {
    if (activeTab === 'analisi') {
      loadAnalisiMarginalita();
    }
    if (activeTab === 'beverage') {
      // Carica tutti i beverage una volta, il filtro è locale
      loadBeverageList();
    }
    if (activeTab === 'beverage_analisi') {
      loadBeverageAnalisi();
    }
    if (activeTab === 'cocktails') {
      loadCocktails();
      loadAvailableIngredienti();
    }
    if (activeTab === 'vini') {
      loadViniList();
    }
  }, [activeTab]);

  // Cambio categoria immediato
  useEffect(() => {
    if (activeTab === 'beverage') {
      loadBeverageList();
    }
  }, [beverageCategoria]);

  // Cambio filtri vini
  useEffect(() => {
    if (activeTab === 'vini') {
      loadViniList();
    }
  }, [viniNazione, viniRegione, viniTipo, viniFornitore]);

  return (
    <div className="space-y-4 max-w-full">
      {/* Alert Match da Confermare */}
      {matchDaConfermare.length > 0 && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-white">{matchDaConfermare.length} da confermare</p>
              <p className="text-sm text-slate-400">Ingredienti con più prodotti</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowMatchModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl w-full sm:w-auto"
          >
            Conferma
          </Button>
        </div>
      )}

      {/* Header Mobile - Rimosso subtitle per salvare spazio */}
      {/* Header Desktop */}
      <div className="hidden lg:block space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Food & Beverage Cost</h1>
          <p className="text-gray-500">Gestisci ricette e calcola marginalità</p>
        </div>
      </div>
        
      {/* Action Buttons - More compact on mobile */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button 
          onClick={handleGeneraRicetteDaMenu}
          disabled={generatingFromMenu}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 rounded-lg sm:rounded-xl text-white lg:text-blue-600 text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all disabled:opacity-50"
        >
          {generatingFromMenu ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          <span className="hidden sm:inline">{generatingFromMenu ? 'Generando...' : 'Genera'}</span>
          <span className="sm:hidden">{generatingFromMenu ? '...' : 'Genera'}</span>
        </button>
        <button 
          onClick={handleAggiornaCostiDaFatture}
          disabled={updatingCosts}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 rounded-lg sm:rounded-xl text-white lg:text-green-600 text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all disabled:opacity-50"
        >
          {updatingCosts ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          <span className="hidden sm:inline">{updatingCosts ? 'Aggiornando...' : 'Aggiorna'}</span>
          <span className="sm:hidden">{updatingCosts ? '...' : 'Aggiorna'}</span>
        </button>
        
        {/* Pulsante Sincronizza Prezzi Menu */}
        <button 
          onClick={handleSincronizzaPrezziMenu}
          disabled={syncingPrices}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 rounded-lg sm:rounded-xl text-white lg:text-blue-600 text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all disabled:opacity-50"
          title="Sincronizza i prezzi delle ricette con quelli del menu"
        >
          {syncingPrices ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          <span className="hidden sm:inline">{syncingPrices ? 'Sincronizzando...' : 'Prezzi Menu'}</span>
          <span className="sm:hidden">{syncingPrices ? '...' : 'Menu'}</span>
        </button>
        
        {/* Pulsante Abbina Vini - Veloce, senza AI */}
        <button 
          onClick={handleAbbinaVini}
          disabled={abbinandoVini}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-rose-500 to-red-600 rounded-lg sm:rounded-xl text-white text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all disabled:opacity-50"
          title="Abbina vini dal menu alle fatture (senza AI - veloce)"
        >
          {abbinandoVini ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <Wine className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          <span className="hidden sm:inline">{abbinandoVini ? 'Abbinando...' : 'Vini'}</span>
          <span className="sm:hidden">{abbinandoVini ? '...' : 'Vini'}</span>
        </button>
        
        <button 
          onClick={handleAnalizzaIngredientiAI}
          disabled={analyzingIngredients}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg sm:rounded-xl text-white text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all disabled:opacity-50"
          title="Analizza ricette con AI e abbina ingredienti dalle fatture"
        >
          {analyzingIngredients ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          <span className="hidden sm:inline">{analyzingIngredients ? 'Analisi AI...' : '🤖 Analizza AI'}</span>
          <span className="sm:hidden">{analyzingIngredients ? '...' : 'AI'}</span>
        </button>
        
        {/* Analizza Food Cost da Menu */}
        <button 
          onClick={() => setShowAnalisiMenu(true)}
          className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg sm:rounded-xl text-white text-xs sm:text-sm font-medium whitespace-nowrap active:scale-95 transition-all"
          title="Analizza food cost da PDF, immagine o file di testo"
          data-testid="analizza-menu-btn"
        >
          <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">📊 Analizza Menu</span>
          <span className="sm:hidden">📊</span>
        </button>
        
        {/* Dropdown Nuovo */}
        <div className="relative">
          <button 
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setNuovoPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
              setShowNuovoDropdown(!showNuovoDropdown);
            }}
            data-testid="fbcost-nuovo-btn"
            className="flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 bg-blue-600 rounded-lg sm:rounded-xl text-white text-xs sm:text-sm font-semibold whitespace-nowrap active:scale-95 transition-all"
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Nuovo</span>
            <ChevronDown className={`h-3 w-3 ml-0.5 transition-transform ${showNuovoDropdown ? 'rotate-180' : ''}`} />
          </button>
          {/* Menu in portal: la barra pulsanti scorre in orizzontale e lo taglierebbe */}
          {showNuovoDropdown && nuovoPos && createPortal(
            <>
              {/* Overlay per chiudere dropdown cliccando fuori */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowNuovoDropdown(false)}
              />
              <div data-testid="fbcost-nuovo-menu" className="fixed bg-white rounded-xl shadow-xl border border-gray-200 py-1 min-w-[160px] z-50" style={{ top: nuovoPos.top, right: nuovoPos.right }}>
                <button
                  onClick={() => { 
                    resetForm(); 
                    setShowCreateModal(true); 
                    setShowNuovoDropdown(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Nuova Ricetta
                </button>
                <button
                  onClick={() => { 
                    resetForm(); 
                    setFormData({...formData, categoria: 'Lotto Produzione'});
                    setShowCreateModal(true); 
                    setShowNuovoDropdown(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  Nuovo Lotto
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded">DDT</span>
                </button>
              </div>
            </>,
            document.getElementById('root') || document.body
          )}
        </div>
      </div>

      {/* Tabs - Compact on mobile */}
      <div className="relative">
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-slate-900 lg:from-gray-100 to-transparent z-10 pointer-events-none lg:hidden" />
        <div className="flex bg-slate-800 lg:bg-gray-100 p-0.5 sm:p-1 rounded-lg sm:rounded-xl overflow-x-auto scrollbar-hide">
          <button 
            onClick={() => setActiveTab('dashboard')}
            data-testid="tab-dashboard"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'dashboard' 
                ? 'bg-amber-600 text-white' 
                : 'text-slate-400 lg:text-gray-500 hover:text-amber-400'
            }`}
          >
            <LayoutDashboard className="h-3 w-3" />
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">Dash</span>
          </button>
          <button 
            onClick={() => setActiveTab('ricette')}
            data-testid="tab-ricette"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === 'ricette' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            Food ({ricette.length})
          </button>
          <button 
            onClick={() => setActiveTab('analisi')}
            data-testid="tab-analisi"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'analisi' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            <TrendingUp className="h-3 w-3" />
            Analisi
          </button>
          <button 
            onClick={() => setActiveTab('costi')}
            data-testid="tab-costi"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'costi' 
                ? 'bg-blue-600 text-white' 
                : 'text-slate-400 lg:text-gray-500 hover:text-blue-400'
            }`}
          >
            <Scale className="h-3 w-3" />
            <span className="hidden sm:inline">Prezzi e grammi</span>
            <span className="sm:hidden">Prezzi</span>
          </button>
          <button 
            onClick={() => setActiveTab('calcolo_rese')}
            data-testid="tab-calcolo-rese"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'calcolo_rese' 
                ? 'bg-emerald-600 text-white' 
                : 'text-slate-400 lg:text-gray-500 hover:text-emerald-400'
            }`}
          >
            <Calculator className="h-3 w-3" />
            <span className="hidden sm:inline">Calcolo Rese</span>
            <span className="sm:hidden">Rese</span>
          </button>
          <button 
            onClick={() => setActiveTab('beverage')}
            data-testid="tab-beverage"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'beverage' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            <Beer className="h-3 w-3" />
            Beverage
          </button>
          <button 
            onClick={() => setActiveTab('vini')}
            data-testid="tab-vini"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'vini' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            <Wine className="h-3 w-3" />
            Vini
          </button>
          <button 
            onClick={() => setActiveTab('beverage_analisi')}
            data-testid="tab-beverage-analisi"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'beverage_analisi' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            <BarChart3 className="h-3 w-3" />
            <span className="hidden sm:inline">Analisi Bev.</span>
            <span className="sm:hidden">An.Bev</span>
          </button>
          <button 
            onClick={() => setActiveTab('cocktails')}
            data-testid="tab-cocktails"
            className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'cocktails' 
                ? 'bg-slate-700 lg:bg-white text-white lg:text-gray-900' 
                : 'text-slate-400 lg:text-gray-500'
            }`}
          >
            <GlassWater className="h-3 w-3" />
            Cocktails
          </button>
        </div>
      </div>

      {/* ============ TAB DASHBOARD FOOD COST ============ */}
      {activeTab === 'dashboard' && (
        <div className="space-y-4">
          {/* Header con toggle vista e selezione periodo */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Toggle Mese/Anno */}
              <div className="flex bg-slate-800/70 lg:bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setDashboardVista('mese')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dashboardVista === 'mese'
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-400 lg:text-gray-600 hover:text-white lg:hover:text-gray-900'
                  }`}
                >
                  Mensile
                </button>
                <button
                  onClick={() => setDashboardVista('anno')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dashboardVista === 'anno'
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-400 lg:text-gray-600 hover:text-white lg:hover:text-gray-900'
                  }`}
                >
                  Annuale
                </button>
              </div>
              
              {/* Selettore Mese (solo in vista mensile) */}
              {dashboardVista === 'mese' && (
                <select
                  value={dashboardMese}
                  onChange={(e) => setDashboardMese(parseInt(e.target.value))}
                  className="px-3 py-2 rounded-lg bg-slate-800/70 lg:bg-white border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900 text-sm"
                >
                  {['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              )}
              
              {/* Selettore Anno */}
              <select
                value={dashboardAnno}
                onChange={(e) => setDashboardAnno(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg bg-slate-800/70 lg:bg-white border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900 text-sm"
              >
                {[2024, 2025, 2026, 2027].map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadDashboard()}
                className="border-slate-600 lg:border-gray-300"
              >
                <RefreshCw className={`h-4 w-4 ${loadingDashboard ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {/* Bottone Inserisci Fatturato (solo in vista mensile) */}
            {dashboardVista === 'mese' && (
              <Button
                onClick={() => setShowFatturatoModal(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="dashboard-inserisci-fatturato"
              >
                <Plus className="h-4 w-4 mr-1" />
                Incassi e magazzino
              </Button>
            )}
          </div>

          {loadingDashboard ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          ) : dashboardData ? (
            <>
              {/* === SEZIONE FOOD === */}
              <div className="bg-slate-800/30 lg:bg-amber-50/50 rounded-xl p-4 border border-slate-700 lg:border-amber-200">
                <h4 className="text-sm font-bold text-amber-400 lg:text-amber-700 mb-3 flex items-center gap-2">
                  🍴 FOOD
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Acquisti Food */}
                  <div className="bg-slate-700/50 lg:bg-white rounded-lg p-3 border border-slate-600 lg:border-gray-200">
                    <p className="text-[10px] text-slate-400 lg:text-gray-500 mb-1">Consumo (senza IVA)</p>
                    <p className="text-lg font-bold text-white lg:text-gray-900" data-testid="dashboard-food-consumo">
                      €{(dashboardData.acquisti?.totale_food || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                    </p>
                    <p className="text-[10px] text-slate-500 lg:text-gray-400">
                      {dashboardData.acquisti?.num_fatture || 0} fatt.
                      {(dashboardData.acquisti?.extra_non_fatturati || 0) > 0 && ` + €${(dashboardData.acquisti?.extra_non_fatturati || 0).toLocaleString('it-IT')} extra`}
                    </p>
                  </div>
                  
                  {/* Fatturato Food */}
                  <div className="bg-slate-700/50 lg:bg-white rounded-lg p-3 border border-slate-600 lg:border-gray-200">
                    <p className="text-[10px] text-slate-400 lg:text-gray-500 mb-1">Incassi (IVA incl.)</p>
                    <p className="text-lg font-bold text-white lg:text-gray-900" data-testid="dashboard-food-incassi">
                      €{(dashboardData.fatturato?.food || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                    </p>
                    {!dashboardData.fatturato?.inserito && (
                      <p className="text-[10px] text-amber-400">⚠️ Da inserire</p>
                    )}
                  </div>
                  
                  {/* Food Cost % */}
                  <div className={`rounded-lg p-3 border ${
                    dashboardData.food_cost?.status === 'ottimo' ? 'bg-emerald-600 border-emerald-500' :
                    dashboardData.food_cost?.status === 'buono' ? 'bg-green-600 border-green-500' :
                    dashboardData.food_cost?.status === 'attenzione' ? 'bg-amber-600 border-amber-500' :
                    dashboardData.food_cost?.status === 'critico' ? 'bg-red-600 border-red-500' :
                    'bg-slate-600 border-slate-500'
                  }`}>
                    <p className="text-[10px] text-white/80 mb-1">Food Cost %</p>
                    <p className="text-xl font-bold text-white" data-testid="dashboard-food-cost-pct">
                      {dashboardData.food_cost?.percentuale !== null 
                        ? `${dashboardData.food_cost.percentuale}%` 
                        : 'N/D'}
                    </p>
                    <p className="text-[10px] text-white/70">
                      Target: {dashboardData.food_cost?.target}% 
                      {dashboardData.fatturato?.food > 0 && (
                        <span> (€{(dashboardData.food_cost?.acquisti_target || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})})</span>
                      )}
                    </p>
                  </div>
                  
                  {/* Scostamento Food */}
                  <div className={`rounded-lg p-3 border ${
                    dashboardData.food_cost?.risparmio_perdita > 0 ? 'bg-emerald-600 border-emerald-500' :
                    dashboardData.food_cost?.risparmio_perdita < 0 ? 'bg-red-600 border-red-500' :
                    'bg-slate-600 border-slate-500'
                  }`}>
                    <p className="text-[10px] text-white/80 mb-1">vs Target</p>
                    <p className="text-lg font-bold text-white">
                      {dashboardData.food_cost?.risparmio_perdita !== null
                        ? `${dashboardData.food_cost?.risparmio_perdita > 0 ? '+' : ''}€${Math.abs(dashboardData.food_cost.risparmio_perdita).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`
                        : 'N/D'}
                    </p>
                    <p className="text-[10px] text-white/70">
                      {dashboardData.food_cost?.risparmio_perdita > 0 ? '✅ Risparmiato' : dashboardData.food_cost?.risparmio_perdita < 0 ? '⚠️ Eccesso' : ''}
                    </p>
                  </div>
                </div>
              </div>

              <ContoFoodCost dati={dashboardData} />

              {/* === SEZIONE BEVERAGE === */}
              <div className="bg-purple-900/20 lg:bg-purple-50/50 rounded-xl p-4 border border-purple-700 lg:border-purple-200 mt-3">
                <h4 className="text-sm font-bold text-purple-400 lg:text-purple-700 mb-3 flex items-center gap-2">
                  🍷 BEVERAGE
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Acquisti Beverage */}
                  <div className="bg-purple-800/30 lg:bg-white rounded-lg p-3 border border-purple-600 lg:border-purple-200">
                    <p className="text-[10px] text-purple-300 lg:text-purple-500 mb-1">Acquisti</p>
                    <p className="text-lg font-bold text-white lg:text-purple-900">
                      €{(dashboardData.acquisti?.fatture_beverage || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                    </p>
                    <p className="text-[10px] text-purple-400 lg:text-purple-500">
                      {dashboardData.fornitori?.beverage_classificati || 0} fornitori
                    </p>
                  </div>
                  
                  {/* Fatturato Beverage */}
                  <div className="bg-purple-800/30 lg:bg-white rounded-lg p-3 border border-purple-600 lg:border-purple-200">
                    <p className="text-[10px] text-purple-300 lg:text-purple-500 mb-1">Fatturato</p>
                    <p className="text-lg font-bold text-white lg:text-purple-900">
                      €{(dashboardData.fatturato?.beverage || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                    </p>
                    {!dashboardData.fatturato?.inserito && (
                      <p className="text-[10px] text-amber-400">⚠️ Da inserire</p>
                    )}
                  </div>
                  
                  {/* Beverage Cost % */}
                  <div className={`rounded-lg p-3 border ${
                    dashboardData.beverage_cost?.status === 'ottimo' ? 'bg-emerald-600 border-emerald-500' :
                    dashboardData.beverage_cost?.status === 'buono' ? 'bg-green-600 border-green-500' :
                    dashboardData.beverage_cost?.status === 'attenzione' ? 'bg-amber-600 border-amber-500' :
                    dashboardData.beverage_cost?.status === 'critico' ? 'bg-red-600 border-red-500' :
                    'bg-slate-600 border-slate-500'
                  }`}>
                    <p className="text-[10px] text-white/80 mb-1">Beverage Cost %</p>
                    <p className="text-xl font-bold text-white">
                      {dashboardData.beverage_cost?.percentuale !== null 
                        ? `${dashboardData.beverage_cost.percentuale}%` 
                        : 'N/D'}
                    </p>
                    <p className="text-[10px] text-white/70">
                      Target: {dashboardData.beverage_cost?.target}%
                      {dashboardData.fatturato?.beverage > 0 && (
                        <span> (€{(dashboardData.beverage_cost?.acquisti_target || 0).toLocaleString('it-IT', {maximumFractionDigits: 0})})</span>
                      )}
                    </p>
                  </div>
                  
                  {/* Scostamento Beverage */}
                  <div className={`rounded-lg p-3 border ${
                    dashboardData.beverage_cost?.scostamento && dashboardData.beverage_cost.scostamento < 0 ? 'bg-emerald-600 border-emerald-500' :
                    dashboardData.beverage_cost?.scostamento && dashboardData.beverage_cost.scostamento > 0 ? 'bg-red-600 border-red-500' :
                    'bg-slate-600 border-slate-500'
                  }`}>
                    <p className="text-[10px] text-white/80 mb-1">vs Target</p>
                    <p className="text-lg font-bold text-white">
                      {dashboardData.beverage_cost?.scostamento !== null
                        ? `${dashboardData.beverage_cost?.scostamento > 0 ? '+' : ''}${dashboardData.beverage_cost.scostamento}%`
                        : 'N/D'}
                    </p>
                    <p className="text-[10px] text-white/70">
                      {dashboardData.beverage_cost?.scostamento && dashboardData.beverage_cost.scostamento < 0 ? '✅ Sotto target' : 
                       dashboardData.beverage_cost?.scostamento && dashboardData.beverage_cost.scostamento > 0 ? '⚠️ Sopra target' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Riga riepilogo totali */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-slate-800/30 lg:bg-gray-50 rounded-xl p-3 border border-slate-700 lg:border-gray-200 flex justify-between items-center">
                  <span className="text-sm text-slate-400 lg:text-gray-600">Totale Acquisti</span>
                  <span className="text-lg font-bold text-white lg:text-gray-900">
                    €{((dashboardData.acquisti?.totale_food || 0) + (dashboardData.acquisti?.fatture_beverage || 0)).toLocaleString('it-IT')}
                  </span>
                </div>
                <div className="bg-slate-800/30 lg:bg-gray-50 rounded-xl p-3 border border-slate-700 lg:border-gray-200 flex justify-between items-center">
                  <span className="text-sm text-slate-400 lg:text-gray-600">Totale Fatturato</span>
                  <span className="text-lg font-bold text-white lg:text-gray-900">
                    €{(dashboardData.fatturato?.totale || 0).toLocaleString('it-IT')}
                  </span>
                </div>
              </div>

              {/* Grafico Trend (solo vista mensile) */}
              {dashboardVista === 'mese' && dashboardData.trend && dashboardData.trend.length > 0 && (
                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-4 border border-slate-700 lg:border-gray-200 mt-3">
                  <h3 className="text-sm font-semibold text-white lg:text-gray-900 mb-4">Trend Food Cost % (Ultimi 6 mesi)</h3>
                  <div className="flex items-end justify-between h-32 gap-2">
                    {dashboardData.trend.map((t, i) => {
                      const maxFc = Math.max(...dashboardData.trend.filter(x => x.food_cost_pct).map(x => x.food_cost_pct), 40);
                      const height = t.food_cost_pct ? (t.food_cost_pct / maxFc) * 100 : 0;
                      const isCurrentMonth = t.mese === dashboardMese && t.anno === dashboardAnno;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs text-slate-400 lg:text-gray-500">
                            {t.food_cost_pct ? `${t.food_cost_pct}%` : '-'}
                          </span>
                          <div 
                            className={`w-full rounded-t transition-all ${
                              isCurrentMonth 
                                ? 'bg-amber-500' 
                                : t.food_cost_pct && t.food_cost_pct <= 30 
                                  ? 'bg-emerald-500' 
                                  : t.food_cost_pct && t.food_cost_pct <= 35 
                                    ? 'bg-amber-500' 
                                    : 'bg-red-500'
                            }`}
                            style={{ height: `${height}%`, minHeight: t.food_cost_pct ? '4px' : '0' }}
                          />
                          <span className="text-[10px] text-slate-500 lg:text-gray-400">{t.mese_nome}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Linea target */}
                  <div className="relative mt-2">
                    <div className="absolute left-0 right-0 border-t-2 border-dashed border-amber-400/50" style={{ bottom: `${(dashboardData.food_cost?.target || 30) / 40 * 100}%` }} />
                  </div>
                </div>
              )}

              {/* Top Fornitori */}
              {dashboardData.top_fornitori && dashboardData.top_fornitori.length > 0 && (
                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-4 border border-slate-700 lg:border-gray-200">
                  <h3 className="text-sm font-semibold text-white lg:text-gray-900 mb-3">
                    Top Fornitori {dashboardVista === 'anno' ? `${dashboardAnno}` : 'del Mese'}
                  </h3>
                  <div className="space-y-2">
                    {dashboardData.top_fornitori.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/50 lg:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-amber-500 text-white' : 
                            i === 1 ? 'bg-slate-400 text-white' : 
                            i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-600 text-white'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="text-sm text-white lg:text-gray-900 truncate max-w-[200px]">
                            {f.fornitore}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-white lg:text-gray-900">
                            €{f.totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">{f.num_fatture} fatt.</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messaggio se manca fatturato */}
              {!dashboardData.fatturato?.inserito && dashboardVista === 'mese' && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-200">Inserisci il fatturato mensile</p>
                      <p className="text-xs text-amber-300/80 mt-1">
                        Per calcolare il Food Cost % devi inserire il fatturato del mese. 
                        Clicca su "Inserisci Fatturato" in alto.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info mesi con dati in vista annuale */}
              {dashboardVista === 'anno' && dashboardData.fatturato?.mesi_inseriti > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                  <p className="text-xs text-blue-300 lg:text-blue-600">
                    📊 Dati calcolati su <strong>{dashboardData.fatturato.mesi_inseriti}</strong> mesi con fatturato inserito
                  </p>
                </div>
              )}
              
              {dashboardVista === 'anno' && !dashboardData.fatturato?.mesi_inseriti && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-200">Nessun dato fatturato per {dashboardAnno}</p>
                      <p className="text-xs text-amber-300/80 mt-1">
                        Inserisci il fatturato nei singoli mesi usando la vista mensile.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Messaggio fornitori da classificare */}
              {dashboardData.fornitori?.da_classificare > 0 && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-200">
                        {dashboardData.fornitori.da_classificare} fornitori da classificare
                      </p>
                      <p className="text-xs text-blue-300/80 mt-1">
                        Classifica i fornitori come "Alimentare" o "Non alimentare" per un calcolo preciso del Food Cost.
                      </p>
                      {dashboardData.fornitori.lista_da_classificare?.length > 0 && (
                        <p className="text-xs text-blue-300/60 mt-2">
                          Es: {dashboardData.fornitori.lista_da_classificare.slice(0, 3).join(', ')}...
                        </p>
                      )}
                      <Button
                        onClick={() => setShowFornitoriModal(true)}
                        size="sm"
                        className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      >
                        Classifica Fornitori
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              Seleziona un mese per visualizzare i dati
            </div>
          )}
        </div>
      )}

      {/* Modal Inserimento Fatturato */}
      {showFatturatoModal && (
        <FatturatoMeseModal anno={dashboardAnno} mese={dashboardMese} dati={dashboardData} headers={getAuthHeaders}
          onClose={() => setShowFatturatoModal(false)} onSaved={() => { setShowFatturatoModal(false); loadDashboard(); }} />
      )}

      {/* Modal Classificazione Fornitori */}
      {showFornitoriModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 lg:bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-700 lg:border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-white lg:text-gray-900">
                  Classifica Fornitori
                </h3>
                <p className="text-xs text-slate-400 lg:text-gray-500 mt-1">
                  Seleziona la categoria per ogni fornitore
                </p>
              </div>
              <button onClick={() => setShowFornitoriModal(false)} className="p-1 rounded-full hover:bg-slate-700 lg:hover:bg-gray-100">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            
            {/* Ricerca */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca fornitore..."
                  value={fornitoriSearch}
                  onChange={(e) => setFornitoriSearch(e.target.value)}
                  className="pl-9 bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-300 text-white lg:text-gray-900"
                />
              </div>
            </div>

            {/* Lista fornitori */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingFornitori ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
                </div>
              ) : (
                <div className="space-y-2">
                  {fornitoriList
                    .filter(f => !fornitoriSearch || f.fornitore.toLowerCase().includes(fornitoriSearch.toLowerCase()))
                    .map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50 lg:bg-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white lg:text-gray-900 truncate">
                          {f.fornitore}
                        </p>
                        <p className="text-xs text-slate-400 lg:text-gray-500">
                          €{f.totale_importo?.toLocaleString('it-IT')} • {f.totale_fatture} fatture
                        </p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => salvaCategroiaFornitore(f.fornitore, 'alimentare')}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            f.categoria === 'alimentare'
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-600 lg:bg-gray-200 text-slate-300 lg:text-gray-600 hover:bg-emerald-600 hover:text-white'
                          }`}
                        >
                          🍴 Food
                        </button>
                        <button
                          onClick={() => salvaCategroiaFornitore(f.fornitore, 'beverage')}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            f.categoria === 'beverage'
                              ? 'bg-purple-600 text-white'
                              : 'bg-slate-600 lg:bg-gray-200 text-slate-300 lg:text-gray-600 hover:bg-purple-600 hover:text-white'
                          }`}
                        >
                          🍷 Bev
                        </button>
                        <button
                          onClick={() => salvaCategroiaFornitore(f.fornitore, 'non_alimentare')}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            f.categoria === 'non_alimentare' || f.categoria === 'servizi'
                              ? 'bg-slate-500 text-white'
                              : 'bg-slate-600 lg:bg-gray-200 text-slate-300 lg:text-gray-600 hover:bg-slate-500 hover:text-white'
                          }`}
                        >
                          ❌ No
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 lg:border-gray-200">
              <Button
                onClick={() => setShowFornitoriModal(false)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Analisi Beverage */}
      {/* Tab Cocktail */}
      {activeTab === 'cocktails' && (
        <div className="space-y-4">
          {/* Header con bottone Nuovo */}
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca cocktail..."
                value={cocktailSearch}
                onChange={(e) => setCocktailSearch(e.target.value)}
                className="pl-9 bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
              />
            </div>
            <Button
              onClick={() => { 
                setNewCocktail({ nome: '', categoria: 'aperitivo', prezzo_vendita: 0, ingredienti: [], note: '' });
                setShowCocktailModal(true); 
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="h-4 w-4 mr-1" /> Nuovo
            </Button>
          </div>

          {loadingCocktails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : cocktailList.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <GlassWater className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-semibold">Nessun cocktail</p>
              <p className="text-sm mt-1">Crea il tuo primo cocktail!</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {cocktailList
                .filter(c => !cocktailSearch || c.nome.toLowerCase().includes(cocktailSearch.toLowerCase()))
                .map((cocktail) => (
                <div 
                  key={cocktail.id}
                  className="bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-xl p-4 border border-slate-700 lg:border-gray-200"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <GlassWater className="h-5 w-5 text-purple-400" />
                        <h3 className="font-semibold text-white lg:text-gray-900">{cocktail.nome}</h3>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                          {cocktail.categoria}
                        </span>
                      </div>
                      
                      {/* Ingredienti */}
                      <div className="space-y-1 mb-3">
                        {cocktail.ingredienti?.map((ing, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs text-slate-400 lg:text-gray-500">
                            <span className="truncate">{ing.nome?.substring(0, 30)}...</span>
                            <span className="flex-shrink-0 ml-2">
                              {ing.quantita_ml}ml = €{ing.costo_calcolato?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Totali */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-700 lg:border-gray-200">
                        <div>
                          <span className="text-xs text-slate-400">Costo: </span>
                          <span className="text-sm font-semibold text-red-400">€{cocktail.costo_ingredienti?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-xs text-slate-400">Vendita: </span>
                          <span className="text-sm font-semibold text-white lg:text-gray-900">€{cocktail.prezzo_vendita?.toFixed(2)}</span>
                        </div>
                        <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
                          cocktail.margine_perc >= 70 ? 'bg-emerald-500/20 text-emerald-400' :
                          cocktail.margine_perc >= 50 ? 'bg-blue-500/20 text-blue-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {cocktail.margine_perc?.toFixed(0)}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setCocktailIngredientiSearch(''); // Reset search
                          setEditingCocktail(cocktail);
                        }}
                        className="p-2 text-slate-400 hover:text-purple-400 transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCocktail(cocktail.id)}
                        className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Nuovo Cocktail */}
      {showCocktailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 lg:border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white lg:text-gray-900">Nuovo Cocktail</h2>
              <button onClick={() => setShowCocktailModal(false)} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-slate-400 lg:text-gray-500">Nome</label>
                <Input
                  value={newCocktail.nome}
                  onChange={(e) => setNewCocktail({...newCocktail, nome: e.target.value})}
                  placeholder="Es: Aperol Spritz"
                  className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-400 lg:text-gray-500">Categoria</label>
                  <select
                    value={newCocktail.categoria}
                    onChange={(e) => setNewCocktail({...newCocktail, categoria: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800/70 backdrop-blur-sm lg:bg-white border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  >
                    <option value="aperitivo">Aperitivo</option>
                    <option value="cocktail">Cocktail</option>
                    <option value="long_drink">Long Drink</option>
                    <option value="shot">Shot</option>
                    <option value="analcolico">Analcolico</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 lg:text-gray-500">Prezzo Vendita €</label>
                  <Input
                    type="number"
                    step="0.5"
                    value={newCocktail.prezzo_vendita}
                    onChange={(e) => setNewCocktail({...newCocktail, prezzo_vendita: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  />
                </div>
              </div>
              
              {/* Ingredienti selezionati */}
              <div>
                <label className="text-sm text-slate-400 lg:text-gray-500 mb-2 block">
                  Ingredienti ({newCocktail.ingredienti.length})
                </label>
                {newCocktail.ingredienti.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {newCocktail.ingredienti.map((ing, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-800 lg:bg-gray-100 rounded-lg">
                        <span className="flex-1 text-sm text-white lg:text-gray-900 truncate">{ing.nome?.substring(0, 25)}...</span>
                        <Input
                          type="number"
                          value={ing.quantita_ml}
                          onChange={(e) => updateIngredienteQuantita(idx, e.target.value, 'new')}
                          className="w-20 text-center bg-slate-700 lg:bg-white border-slate-600 text-white lg:text-gray-900"
                        />
                        <span className="text-xs text-slate-400">ml</span>
                        <span className="text-xs text-emerald-400 w-16 text-right">
                          €{((ing.costo_per_ml || (ing.prezzo_bottiglia/ing.ml_bottiglia)) * ing.quantita_ml).toFixed(2)}
                        </span>
                        <button onClick={() => removeIngredienteFromCocktail(idx, 'new')} className="text-red-400 hover:text-red-300">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                      <span className="text-sm text-slate-400">Costo totale:</span>
                      <span className="font-bold text-emerald-400">€{calculateCocktailCost(newCocktail.ingredienti).toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mb-3">Nessun ingrediente aggiunto</p>
                )}
                
                {/* Ricerca ingredienti */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca ingrediente (Aperol, Prosecco, Soda...)"
                    value={cocktailIngredientiSearch}
                    onChange={(e) => setCocktailIngredientiSearch(e.target.value)}
                    className="pl-9 bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  />
                </div>
                
                {/* Lista ingredienti disponibili */}
                {cocktailIngredientiSearch && typeof cocktailIngredientiSearch === 'string' && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {availableIngredienti
                      .filter(i => i.nome?.toLowerCase().includes((cocktailIngredientiSearch || '').toLowerCase()))
                      .slice(0, 10)
                      .map((ing) => (
                        <button
                          key={ing.id}
                          onClick={() => {
                            addIngredienteToCocktail(ing, 'new');
                            setCocktailIngredientiSearch('');
                          }}
                          className="w-full flex items-center justify-between p-2 bg-slate-700 lg:bg-gray-50 rounded-lg hover:bg-slate-600 lg:hover:bg-gray-100 transition-colors text-left"
                        >
                          <span className="text-sm text-white lg:text-gray-900 truncate">{ing.nome?.substring(0, 35)}...</span>
                          <span className="text-xs text-slate-400 flex-shrink-0 ml-2">€{ing.costo_per_30ml?.toFixed(2)}/30ml</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm text-slate-400 lg:text-gray-500">Note</label>
                <Input
                  value={newCocktail.note}
                  onChange={(e) => setNewCocktail({...newCocktail, note: e.target.value})}
                  placeholder="Es: Servire con fetta di arancia"
                  className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-700 lg:border-gray-200 flex gap-2">
              <Button
                onClick={() => setShowCocktailModal(false)}
                variant="outline"
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleCreateCocktail}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Crea Cocktail
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifica Cocktail */}
      {editingCocktail && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 lg:border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white lg:text-gray-900">Modifica Cocktail</h2>
              <button onClick={() => setEditingCocktail(null)} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-slate-400 lg:text-gray-500">Nome</label>
                <Input
                  value={editingCocktail.nome}
                  onChange={(e) => setEditingCocktail({...editingCocktail, nome: e.target.value})}
                  className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-400 lg:text-gray-500">Categoria</label>
                  <select
                    value={editingCocktail.categoria}
                    onChange={(e) => setEditingCocktail({...editingCocktail, categoria: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800/70 backdrop-blur-sm lg:bg-white border border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  >
                    <option value="aperitivo">Aperitivo</option>
                    <option value="cocktail">Cocktail</option>
                    <option value="long_drink">Long Drink</option>
                    <option value="shot">Shot</option>
                    <option value="analcolico">Analcolico</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 lg:text-gray-500">Prezzo Vendita €</label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingCocktail.prezzo_vendita}
                    onChange={(e) => setEditingCocktail({...editingCocktail, prezzo_vendita: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  />
                </div>
              </div>
              
              {/* Ingredienti */}
              <div>
                <label className="text-sm text-slate-400 lg:text-gray-500 mb-2 block">Ingredienti</label>
                {editingCocktail.ingredienti?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {editingCocktail.ingredienti.map((ing, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-800 lg:bg-gray-100 rounded-lg">
                        <span className="flex-1 text-sm text-white lg:text-gray-900 truncate">{ing.nome?.substring(0, 25)}...</span>
                        <Input
                          type="number"
                          value={ing.quantita_ml}
                          onChange={(e) => updateIngredienteQuantita(idx, e.target.value, 'edit')}
                          className="w-20 text-center bg-slate-700 lg:bg-white border-slate-600 text-white lg:text-gray-900"
                        />
                        <span className="text-xs text-slate-400">ml</span>
                        <button onClick={() => removeIngredienteFromCocktail(idx, 'edit')} className="text-red-400 hover:text-red-300">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Aggiungi ingrediente */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca ingrediente..."
                    value={cocktailIngredientiSearch}
                    onChange={(e) => setCocktailIngredientiSearch(e.target.value)}
                    className="pl-9 bg-slate-800/70 backdrop-blur-sm lg:bg-white border-slate-700 lg:border-gray-200 text-white lg:text-gray-900"
                  />
                </div>
                
                {cocktailIngredientiSearch && typeof cocktailIngredientiSearch === 'string' && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {availableIngredienti
                      .filter(i => i.nome?.toLowerCase().includes((cocktailIngredientiSearch || '').toLowerCase()))
                      .slice(0, 10)
                      .map((ing) => (
                        <button
                          key={ing.id}
                          onClick={() => {
                            addIngredienteToCocktail(ing, 'edit');
                            setCocktailIngredientiSearch('');
                          }}
                          className="w-full flex items-center justify-between p-2 bg-slate-700 lg:bg-gray-50 rounded-lg hover:bg-slate-600 lg:hover:bg-gray-100 transition-colors text-left"
                        >
                          <span className="text-sm text-white lg:text-gray-900 truncate">{ing.nome?.substring(0, 35)}...</span>
                          <span className="text-xs text-slate-400 flex-shrink-0 ml-2">€{ing.costo_per_30ml?.toFixed(2)}/30ml</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-700 lg:border-gray-200 flex gap-2">
              <Button
                onClick={() => setEditingCocktail(null)}
                variant="outline"
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleUpdateCocktail}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Salva Modifiche
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'beverage_analisi' && (
        <div className="space-y-4">
          {loadingBeverageAnalisi ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : beverageAnalisi ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div 
                  onClick={() => setBeverageAnalisiFilter(beverageAnalisiFilter === 'ottimo' ? '' : 'ottimo')}
                  className={`bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                    beverageAnalisiFilter === 'ottimo' ? 'ring-2 ring-white' : ''
                  }`}
                >
                  <p className="text-xs text-emerald-200">Ottimo (&gt;70%)</p>
                  <p className="text-2xl font-bold text-white">{beverageAnalisi.statistiche.prodotti_ottimo}</p>
                </div>
                <div 
                  onClick={() => setBeverageAnalisiFilter(beverageAnalisiFilter === 'buono' ? '' : 'buono')}
                  className={`bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                    beverageAnalisiFilter === 'buono' ? 'ring-2 ring-white' : ''
                  }`}
                >
                  <p className="text-xs text-blue-200">Buono (50-70%)</p>
                  <p className="text-2xl font-bold text-white">{beverageAnalisi.statistiche.prodotti_buono}</p>
                </div>
                <div 
                  onClick={() => setBeverageAnalisiFilter(beverageAnalisiFilter === 'attenzione' ? '' : 'attenzione')}
                  className={`bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                    beverageAnalisiFilter === 'attenzione' ? 'ring-2 ring-white' : ''
                  }`}
                >
                  <p className="text-xs text-amber-200">Attenzione (30-50%)</p>
                  <p className="text-2xl font-bold text-white">{beverageAnalisi.statistiche.prodotti_attenzione}</p>
                </div>
                <div 
                  onClick={() => setBeverageAnalisiFilter(beverageAnalisiFilter === 'critico' ? '' : 'critico')}
                  className={`bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                    beverageAnalisiFilter === 'critico' ? 'ring-2 ring-white' : ''
                  }`}
                >
                  <p className="text-xs text-red-200">Critico (&lt;30%)</p>
                  <p className="text-2xl font-bold text-white">{beverageAnalisi.statistiche.prodotti_critico}</p>
                </div>
              </div>

              {/* Margine Medio */}
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Margine Medio Beverage</p>
                  <p className={`text-3xl font-bold ${getBeverageMarginColor(beverageAnalisi.statistiche.margine_medio)}`}>
                    {beverageAnalisi.statistiche.margine_medio}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Prodotti con prezzo</p>
                  <p className="text-lg font-bold text-white">
                    {beverageAnalisi.statistiche.con_prezzo_vendita}/{beverageAnalisi.statistiche.totale_prodotti}
                  </p>
                </div>
              </div>

              {/* Statistiche per Categoria */}
              {beverageAnalisi.statistiche_categoria && Object.keys(beverageAnalisi.statistiche_categoria).length > 0 && (
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3">Margine per Categoria</h3>
                  <div className="space-y-2">
                    {Object.entries(beverageAnalisi.statistiche_categoria)
                      .sort((a, b) => b[1].margine_medio - a[1].margine_medio)
                      .map(([cat, stats]) => (
                      <div key={cat} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`p-1.5 rounded-lg bg-gradient-to-br ${getBeverageCategoryColor(cat)}`}>
                            {getBeverageCategoryIcon(cat)}
                          </span>
                          <span className="text-sm text-white capitalize">{cat.replace('_', ' ')}</span>
                          <span className="text-xs text-slate-500">({stats.count})</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                stats.margine_medio >= 70 ? 'bg-emerald-500' :
                                stats.margine_medio >= 50 ? 'bg-blue-500' :
                                stats.margine_medio >= 30 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(stats.margine_medio, 100)}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold ${getBeverageMarginColor(stats.margine_medio)}`}>
                            {stats.margine_medio}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista Prodotti Filtrati */}
              {beverageAnalisiFilter && beverageAnalisi.per_status[beverageAnalisiFilter] && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">
                      Prodotti {beverageAnalisiFilter.toUpperCase()} ({beverageAnalisi.per_status[beverageAnalisiFilter].length})
                    </h3>
                    <button 
                      onClick={() => setBeverageAnalisiFilter('')}
                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                    >
                      <X className="h-3 w-3" /> Chiudi
                    </button>
                  </div>
                  {beverageAnalisi.per_status[beverageAnalisiFilter].map(p => (
                    <div key={p.id} className="bg-slate-700 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`p-1 rounded bg-gradient-to-br ${getBeverageCategoryColor(p.categoria_bevanda)}`}>
                          {getBeverageCategoryIcon(p.categoria_bevanda)}
                        </span>
                        <span className="text-sm text-white truncate">{p.nome}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-slate-400">Acquisto</p>
                          <p className="text-sm text-amber-400">€{p.prezzo_acquisto.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">Vendita</p>
                          <p className="text-sm text-emerald-400">€{p.prezzo_vendita.toFixed(2)}</p>
                        </div>
                        <span className={`text-sm font-bold ${getBeverageMarginColor(p.margine_perc)}`}>
                          {p.margine_perc}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Critici se nessun filtro */}
              {!beverageAnalisiFilter && beverageAnalisi.top_critici?.length > 0 && (
                <div className="bg-red-900/80 lg:bg-red-50 border border-red-500/50 lg:border-red-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-red-300 lg:text-red-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Top 5 Critici - Rivedere Prezzi
                  </h3>
                  <div className="space-y-2">
                    {beverageAnalisi.top_critici.map(p => (
                      <div key={p.id} className="flex items-center justify-between text-sm">
                        <span className="text-white lg:text-gray-800 truncate flex-1">{p.nome}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-red-200 lg:text-gray-600">€{p.prezzo_acquisto.toFixed(2)} → €{p.prezzo_vendita.toFixed(2)}</span>
                          <span className="text-red-300 lg:text-red-600 font-bold">{p.margine_perc}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prodotti Senza Prezzo */}
              {beverageAnalisi.senza_prezzi?.length > 0 && (
                <div className="bg-amber-900/80 lg:bg-amber-50 border border-amber-500/50 lg:border-amber-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-amber-300 lg:text-amber-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {beverageAnalisi.statistiche.senza_prezzo} Prodotti senza Prezzo Vendita
                  </h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {beverageAnalisi.senza_prezzi.map(p => (
                      <div key={p.id} className="flex items-center justify-between text-sm py-1">
                        <span className="text-white lg:text-gray-800 truncate flex-1">{p.nome}</span>
                        <span className="text-amber-300 lg:text-amber-700">€{p.prezzo_acquisto.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-200 lg:text-amber-600 mt-2">
                    Vai al tab &quot;Beverage&quot; per aggiungere i prezzi di vendita
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-slate-800/50 rounded-xl p-8 text-center">
              <BarChart3 className="h-12 w-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">Nessun dato disponibile</p>
            </div>
          )}
        </div>
      )}

      {/* Tab Vini */}
      {activeTab === 'vini' && (
        <div className="space-y-4" data-testid="vini-tab-content">
          {loadingVini ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <>
              {/* Filtri Vini - 4 dropdown in riga */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <select
                  value={viniNazione}
                  onChange={(e) => setViniNazione(e.target.value)}
                  className="px-2 py-2 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-slate-600 lg:border-gray-200 rounded-lg text-xs sm:text-sm"
                  data-testid="vini-filter-nazione"
                >
                  <option value="">🌍 Nazione</option>
                  {viniFiltri.nazioni?.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <select
                  value={viniRegione}
                  onChange={(e) => setViniRegione(e.target.value)}
                  className="px-2 py-2 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-slate-600 lg:border-gray-200 rounded-lg text-xs sm:text-sm"
                  data-testid="vini-filter-regione"
                >
                  <option value="">📍 Regione</option>
                  {viniFiltri.regioni?.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <select
                  value={viniTipo}
                  onChange={(e) => setViniTipo(e.target.value)}
                  className="px-2 py-2 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-slate-600 lg:border-gray-200 rounded-lg text-xs sm:text-sm"
                  data-testid="vini-filter-tipo"
                >
                  <option value="">Tipo</option>
                  {viniFiltri.tipi?.map(t => (
                    <option key={t} value={t}>{getVinoTipoLabel(t)}</option>
                  ))}
                </select>
                <select
                  value={viniFornitore}
                  onChange={(e) => setViniFornitore(e.target.value)}
                  className="px-2 py-2 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-slate-600 lg:border-gray-200 rounded-lg text-xs sm:text-sm"
                  data-testid="vini-filter-fornitore"
                >
                  <option value="">🏢 Fornitore</option>
                  {viniFiltri.fornitori?.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              {/* Barra ricerca + reset filtri + STAMPA */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca vino..."
                    value={viniSearch}
                    onChange={(e) => setViniSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadViniList()}
                    className="pl-9 h-10 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400 rounded-lg"
                    data-testid="vini-search-input"
                  />
                </div>
                {(viniNazione || viniRegione || viniTipo || viniFornitore || viniSearch) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setViniNazione('');
                      setViniRegione('');
                      setViniTipo('');
                      setViniFornitore('');
                      setViniSearch('');
                    }}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    data-testid="vini-reset-filters"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                {/* Pulsante Stampa Carta Vini */}
                <Button
                  onClick={() => setShowStampaVini(true)}
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                  data-testid="stampa-carta-vini-btn"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Stampa Carta</span>
                </Button>
                
                {/* Pulsante Genera Tutte Descrizioni AI */}
                <Button
                  onClick={handleGeneraTutteDescrizioni}
                  disabled={generandoTutte || viniList.length === 0}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                  data-testid="genera-tutte-descrizioni-btn"
                >
                  {generandoTutte ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      <span className="hidden sm:inline">Genero...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">AI Descrizioni</span>
                    </>
                  )}
                </Button>
              </div>

              {/* Statistiche rapide */}
              {viniStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-3 text-center">
                    <p className="text-xs text-purple-200">Totale Vini</p>
                    <p className="text-xl font-bold text-white">{viniList.length}</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-600 to-rose-600 rounded-xl p-3 text-center">
                    <p className="text-xs text-red-200">Rossi</p>
                    <p className="text-xl font-bold text-white">{viniStats.per_tipo?.rosso || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl p-3 text-center">
                    <p className="text-xs text-amber-100">Bianchi</p>
                    <p className="text-xl font-bold text-white">{viniStats.per_tipo?.bianco || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl p-3 text-center">
                    <p className="text-xs text-cyan-100">Bollicine</p>
                    <p className="text-xl font-bold text-white">
                      {(viniStats.per_tipo?.bollicine || 0) + (viniStats.per_tipo?.spumante || 0) + (viniStats.per_tipo?.champagne || 0) + (viniStats.per_tipo?.prosecco || 0)}
                    </p>
                  </div>
                </div>
              )}

              {/* Lista Vini - raggruppata per nazione/regione */}
              {viniList.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Wine className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">Nessun vino trovato</p>
                  <p className="text-sm mt-1">Carica una lista vini dalla pagina Menu</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Raggruppa per nazione */}
                  {(() => {
                    const grouped = {};
                    viniList.forEach(vino => {
                      const key = `${vino.nazione || 'Italia'}`;
                      if (!grouped[key]) grouped[key] = [];
                      grouped[key].push(vino);
                    });
                    
                    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([nazione, vini]) => (
                      <div key={nazione} className="space-y-2">
                        {/* Header Nazione */}
                        <div className="flex items-center gap-2 py-2 border-b border-slate-700">
                          <span className="text-lg">🌍</span>
                          <h3 className="font-bold text-white lg:text-gray-900">{nazione}</h3>
                          <span className="text-xs text-slate-400 ml-auto">{vini.length} vini</span>
                        </div>
                        
                        {/* Vini della nazione - raggruppa per regione */}
                        {(() => {
                          const byRegion = {};
                          vini.forEach(v => {
                            const reg = v.regione || 'Altra';
                            if (!byRegion[reg]) byRegion[reg] = [];
                            byRegion[reg].push(v);
                          });
                          
                          return Object.entries(byRegion).sort((a, b) => a[0].localeCompare(b[0])).map(([regione, viniRegione]) => (
                            <div key={regione} className="ml-4 space-y-1">
                              {/* Header Regione (se non è "Altra") */}
                              {regione !== 'Altra' && (
                                <div className="flex items-center gap-2 py-1">
                                  <span className="text-sm">📍</span>
                                  <span className="text-sm font-medium text-slate-300 lg:text-gray-700">{regione}</span>
                                  <span className="text-xs text-slate-500">({viniRegione.length})</span>
                                </div>
                              )}
                              
                              {/* Lista vini */}
                              {viniRegione.map((vino) => (
                                <div 
                                  key={vino.id}
                                  className="bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-xl p-3 border border-slate-700 lg:border-gray-200 hover:border-purple-500/50 transition-all"
                                  data-testid={`vino-item-${vino.id}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="font-semibold text-white lg:text-gray-900 truncate">{vino.nome}</h4>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          vino.tipo_vino === 'rosso' ? 'bg-red-500/20 text-red-400' :
                                          vino.tipo_vino === 'bianco' ? 'bg-amber-500/20 text-amber-400' :
                                          vino.tipo_vino === 'rosato' ? 'bg-pink-500/20 text-pink-400' :
                                          'bg-cyan-500/20 text-cyan-400'
                                        }`}>
                                          {getVinoTipoLabel(vino.tipo_vino)}
                                        </span>
                                        {/* Pulsante modifica */}
                                        <button
                                          onClick={() => handleApriCorrezioneVino(vino)}
                                          className="p-1 rounded-lg hover:bg-slate-600 lg:hover:bg-gray-100 transition-colors"
                                          title="Modifica vino (descrizione, abbinamento)"
                                        >
                                          <Edit className="h-3.5 w-3.5 text-slate-400 lg:text-gray-400" />
                                        </button>
                                      </div>
                                      
                                      {/* Cantina */}
                                      {(vino.cantina || vino.produttore) && (
                                        <div className="text-xs text-slate-300 lg:text-gray-600 mt-1">
                                          {vino.cantina || vino.produttore}
                                        </div>
                                      )}
                                      
                                      {/* Vitigno */}
                                      {vino.vitigno && (
                                        <div className="text-xs text-slate-400 italic mt-0.5">
                                          ({vino.vitigno})
                                        </div>
                                      )}
                                      
                                      {/* Descrizione */}
                                      {vino.descrizione && (
                                        <div className="text-xs text-slate-400 lg:text-gray-500 mt-1 line-clamp-2">
                                          {vino.descrizione}
                                        </div>
                                      )}
                                      
                                      {/* Prodotto fattura abbinato */}
                                      {vino.prodotto_fattura && (
                                        <div className="mt-1 text-xs text-emerald-400 lg:text-emerald-600 flex items-center gap-1">
                                          <Check className="h-3 w-3" />
                                          <span className="truncate">{vino.prodotto_fattura}</span>
                                          {vino.abbinamento_manuale && <span className="text-yellow-400">(manuale)</span>}
                                        </div>
                                      )}
                                      
                                      {/* Fornitore */}
                                      {vino.fornitore && !vino.cantina && (
                                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                                          <Building2 className="h-3 w-3" />
                                          {vino.fornitore}
                                        </div>
                                      )}
                                      
                                      {/* Avviso se manca descrizione */}
                                      {!vino.descrizione && !vino.cantina && (
                                        <div className="mt-1 text-xs text-orange-400 flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          Aggiungi descrizione per la carta vini
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Prezzi e margine */}
                                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                      {vino.prezzo_acquisto > 0 ? (
                                        <>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-400">Acquisto:</span>
                                            <span className="text-sm font-medium text-red-400">
                                              €{vino.prezzo_acquisto?.toFixed(2)}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-400">Vendita:</span>
                                            <span className="text-sm font-bold text-white lg:text-gray-900">
                                              €{vino.prezzo_vendita?.toFixed(2) || '—'}
                                            </span>
                                          </div>
                                          {vino.margine_perc > 0 && (
                                            <div className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${getVinoMarginBg(vino.margine_perc)}`}>
                                              {vino.margine_perc}% (€{vino.margine_euro?.toFixed(2)})
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="text-xs text-slate-500">
                                          Vendita: €{vino.prezzo_vendita?.toFixed(2) || vino.prezzo?.toFixed(2) || '—'}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Modal Impostazioni Stampa Carta Vini */}
      {showStampaVini && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  Stampa Carta dei Vini
                </h3>
                <button
                  onClick={() => setShowStampaVini(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Nome Ristorante */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Ristorante (opzionale)
                </label>
                <Input
                  placeholder="Es: Ristorante La Pergola"
                  value={stampaSettings.nomeRistorante}
                  onChange={(e) => setStampaSettings(prev => ({ ...prev, nomeRistorante: e.target.value }))}
                  className="w-full"
                />
              </div>
              
              {/* Opzioni di visualizzazione */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Opzioni Stampa
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stampaSettings.mostraPrezzi}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, mostraPrezzi: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">Mostra prezzi di vendita</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stampaSettings.mostraDescrizione}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, mostraDescrizione: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">Mostra cantina e descrizione</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stampaSettings.separaChampagne}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, separaChampagne: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">Separa Champagne dalle bollicine italiane</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stampaSettings.mostraCopertina}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, mostraCopertina: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">Copertina su pagina dedicata</span>
                  </label>
                </div>
              </div>
              
              {/* Numero Pagine - Input libero */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adatta a numero pagine (facoltativo)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    placeholder="Auto"
                    value={stampaSettings.numeroPagine}
                    onChange={(e) => setStampaSettings(prev => ({ ...prev, numeroPagine: e.target.value }))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-center"
                  />
                  <span className="text-sm text-gray-500">pagine</span>
                  {stampaSettings.numeroPagine && (
                    <button
                      onClick={() => setStampaSettings(prev => ({ ...prev, numeroPagine: '' }))}
                      className="text-xs text-amber-600 hover:text-amber-800 underline"
                    >
                      Reset Auto
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {!stampaSettings.numeroPagine 
                    ? `Layout automatico ottimale per ${viniList.length} vini` 
                    : `Font ridotto per far entrare ${viniList.length} vini in ${stampaSettings.numeroPagine} pagine`}
                </p>
                {stampaSettings.numeroPagine && parseInt(stampaSettings.numeroPagine) < Math.ceil(viniList.length / 40) && (
                  <p className="text-xs text-amber-600 mt-1">
                    💡 Suggerimento: disattiva "Mostra cantina e descrizione" per risparmiare spazio
                  </p>
                )}
              </div>
              
              {/* Margini */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Margini pagina (mm)
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Sup/Inf:</span>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={stampaSettings.margineSup}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, margineSup: parseInt(e.target.value) || 0 }))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-amber-500 text-center text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Laterali:</span>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={stampaSettings.margineLat}
                      onChange={(e) => setStampaSettings(prev => ({ ...prev, margineLat: parseInt(e.target.value) || 0 }))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-amber-500 text-center text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {/* Anteprima info */}
              <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-2">Struttura Carta (tutto conseguenziale):</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {stampaSettings.separaChampagne && (
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      CHAMPAGNE
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                    {stampaSettings.separaChampagne ? 'BOLLICINE ITALIANE' : 'BOLLICINE'}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-200 rounded-full"></span>
                    VINI BIANCHI
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-pink-300 rounded-full"></span>
                    VINI ROSATI
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                    VINI ROSSI
                  </div>
                </div>
                <p className="mt-2 text-xs text-amber-600">
                  {viniList.length} vini • Senza interruzioni di pagina tra sezioni
                </p>
              </div>
              
              {/* Pulsanti */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowStampaVini(false)}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={() => {
                    handleStampaCartaVini();
                    setShowStampaVini(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Genera PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Correzione Abbinamento Vino */}
      {showCorreggiVino && vinoCorrezione && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Modifica Vino
                  </h3>
                  <p className="text-purple-200 text-sm mt-1 truncate">
                    {vinoCorrezione.nome}
                  </p>
                </div>
                <button
                  onClick={() => setShowCorreggiVino(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 border-b space-y-3">
              {/* Campi Descrizione Vino */}
              <div className="bg-purple-50 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-purple-800 text-sm">📝 Descrizione per Carta dei Vini</h4>
                  <Button
                    size="sm"
                    onClick={handleGeneraDescrizioneAI}
                    disabled={generandoDescrizione}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white text-xs"
                  >
                    {generandoDescrizione ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Genero...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3 mr-1" />
                        Genera con AI
                      </>
                    )}
                  </Button>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Cantina/Produttore</label>
                  <Input
                    placeholder="Es: Tenuta San Guido, Antinori..."
                    value={vinoCorrezione.cantina || vinoCorrezione.produttore || ''}
                    onChange={(e) => setVinoCorrezione(prev => ({...prev, cantina: e.target.value}))}
                    className="text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Uvaggio (Vitigni)</label>
                  <Input
                    placeholder="Es: Sangiovese 80%, Merlot 20%"
                    value={vinoCorrezione.vitigno || vinoCorrezione.uve || ''}
                    onChange={(e) => setVinoCorrezione(prev => ({...prev, vitigno: e.target.value}))}
                    className="text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Descrizione (max 4 righe)</label>
                  <textarea
                    placeholder="Colore, profumo, sapore, abbinamenti..."
                    value={vinoCorrezione.descrizione || ''}
                    onChange={(e) => setVinoCorrezione(prev => ({...prev, descrizione: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={4}
                  />
                </div>
                
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/gestione/ricette/vino-correggi-abbinamento`, {
                        vino_menu_id: vinoCorrezione.id || vinoCorrezione._id,
                        descrizione: vinoCorrezione.descrizione,
                        vitigno: vinoCorrezione.vitigno,
                        cantina: vinoCorrezione.cantina
                      }, { headers: getAuthHeaders() });
                      if (res.data.success) {
                        toast.success('✅ Descrizione salvata');
                        loadViniList();
                        setShowCorreggiVino(false);
                      } else {
                        toast.error(res.data.error || 'Errore salvataggio');
                      }
                    } catch (err) {
                      console.error('Save error:', err);
                      toast.error('Errore salvataggio');
                    }
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Salva Descrizione
                </Button>
              </div>
              
              {/* Info abbinamento corrente */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600 font-medium mb-2">🔗 Abbinamento Fattura</p>
                {vinoCorrezione.prodotto_fattura ? (
                  <p className="text-sm text-emerald-600">
                    ✓ {vinoCorrezione.prodotto_fattura}
                    {vinoCorrezione.abbinamento_manuale && <span className="text-yellow-600 ml-1">(manuale)</span>}
                  </p>
                ) : (
                  <p className="text-sm text-orange-500">⚠ Non abbinato</p>
                )}
                {vinoCorrezione.prezzo_acquisto > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Prezzo acquisto: €{vinoCorrezione.prezzo_acquisto?.toFixed(2)}</p>
                )}
              </div>
              
              {/* Cerca nei prodotti fattura */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca prodotto nelle fatture per abbinare..."
                  value={searchViniFatture}
                  onChange={(e) => setSearchViniFatture(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {loadingViniFatture ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
              ) : viniFattureDisponibili.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Wine className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Nessun vino trovato nelle fatture</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-2">Clicca per abbinare a questo prodotto:</p>
                  {viniFattureDisponibili
                    .filter(v => 
                      !searchViniFatture || 
                      v.nome?.toLowerCase().includes(searchViniFatture.toLowerCase()) ||
                      v.fornitore?.toLowerCase().includes(searchViniFatture.toLowerCase())
                    )
                    .map((prodotto, idx) => (
                      <div 
                        key={idx}
                        onClick={() => handleCorreggiAbbinamento(prodotto)}
                        className="bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg p-3 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{prodotto.nome}</p>
                            {prodotto.fornitore && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {prodotto.fornitore}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="font-bold text-purple-600">€{prodotto.prezzo_unitario?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            
            {/* Azioni */}
            <div className="p-4 border-t bg-gray-50 flex gap-3">
              {vinoCorrezione.prodotto_fattura && (
                <Button
                  variant="outline"
                  onClick={handleRimuoviAbbinamento}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Rimuovi abbinamento
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowCorreggiVino(false)}
                className="ml-auto"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Progress Generazione Batch */}
      {generandoTutte && progressGenerazione.total > 0 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 animate-pulse" />
                Generazione AI in corso...
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progresso</span>
                  <span className="font-bold text-purple-600">
                    {progressGenerazione.current} / {progressGenerazione.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-violet-500 to-purple-600 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${(progressGenerazione.current / progressGenerazione.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {Math.round((progressGenerazione.current / progressGenerazione.total) * 100)}% completato
                </p>
              </div>
              
              {/* Vino corrente */}
              {progressGenerazione.vinoCorrente && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 mb-1">Sto generando per:</p>
                  <p className="font-medium text-purple-800 truncate">{progressGenerazione.vinoCorrente}</p>
                </div>
              )}
              
              {/* Info */}
              <p className="text-xs text-gray-400 text-center">
                Non chiudere questa finestra durante la generazione
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Beverage */}
      {activeTab === 'beverage' && (
        <div className="space-y-3">
          {loadingBeverage ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <>
              {/* Search & Filter */}
              <div className="flex gap-1.5 sm:gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca..."
                    value={beverageSearch}
                    onChange={(e) => setBeverageSearch(e.target.value)}
                    className="pl-8 sm:pl-10 h-9 sm:h-10 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400 rounded-lg"
                  />
                </div>
                <select
                  value={beverageCategoria}
                  onChange={(e) => setBeverageCategoria(e.target.value)}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-slate-600 lg:border-gray-200 rounded-lg text-xs sm:text-sm flex-shrink-0"
                >
                  <option value="">Tutte</option>
                  <option value="vino">Vino</option>
                  <option value="birra">🍺 Birra</option>
                  <option value="soft_drink">🥤 Soft</option>
                  <option value="acqua">💧 Acqua</option>
                  <option value="liquore">🥃 Liquori</option>
                  <option value="caffe">☕ Caffè</option>
                </select>
              </div>

              {/* Stats Cards - 3 column grid */}
              {beverageStats && Object.keys(beverageStats).length > 0 && (() => {
                const entries = Object.entries(beverageStats);
                const categoryNames = { 'vino': 'Vino', 'birra': 'Birra', 'soft_drink': 'Soft', 'acqua': 'Acqua', 'liquore': 'Liquori', 'caffe': 'Caffè', 'altro': 'Altro' };
                const bgColors = {
                  'acqua': 'bg-gradient-to-br from-blue-500 to-cyan-500',
                  'vino': 'bg-gradient-to-br from-violet-500 to-pink-500',
                  'liquore': 'bg-gradient-to-br from-orange-500 to-yellow-500',
                  'birra': 'bg-gradient-to-br from-yellow-500 to-lime-500',
                  'soft_drink': 'bg-gradient-to-br from-cyan-500 to-emerald-500',
                  'caffe': 'bg-gradient-to-br from-amber-800 to-yellow-700',
                  'altro': 'bg-gradient-to-br from-gray-500 to-gray-400'
                };
                // Split into rows of 3
                const rows = [];
                for (let i = 0; i < entries.length; i += 3) {
                  rows.push(entries.slice(i, i + 3));
                }
                return (
                  <table className="w-full table-fixed" style={{ borderCollapse: 'separate', borderSpacing: '4px' }}>
                    <tbody>
                      {rows.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {row.map(([cat, stats]) => (
                            <td 
                              key={cat}
                              onClick={() => setBeverageCategoria(beverageCategoria === cat ? '' : cat)}
                              className={`${bgColors[cat] || 'bg-gray-500'} p-2 rounded-lg text-center cursor-pointer ${beverageCategoria === cat ? 'ring-2 ring-white' : ''}`}
                              style={{ width: '33.33%' }}
                            >
                              <div className="flex items-center justify-center gap-0.5">
                                <span className="text-sm">{getBeverageCategoryIcon(cat)}</span>
                                <span className="text-base font-bold text-white">{stats.count}</span>
                              </div>
                              <div className="text-[11px] text-white font-medium truncate">{categoryNames[cat] || cat}</div>
                              <div className="text-[10px] text-white/70">{stats.margine_medio}%</div>
                            </td>
                          ))}
                          {row.length < 3 && [...Array(3 - row.length)].map((_, i) => (
                            <td key={`empty-${i}`} style={{ width: '33.33%' }}></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}

              {/* Filter Status Badge */}
              {beverageFilterStatus && (
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getBeverageMarginBg(
                    beverageFilterStatus === 'ottimo' ? 70 :
                    beverageFilterStatus === 'buono' ? 50 :
                    beverageFilterStatus === 'attenzione' ? 30 : 0
                  )}`}>
                    Filtro: {beverageFilterStatus.toUpperCase()}
                  </span>
                  <button 
                    onClick={() => setBeverageFilterStatus('')}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Rimuovi
                  </button>
                </div>
              )}

              {/* Beverage List - Larger and more readable with clear price visibility */}
              <div className="space-y-2">
                {beverageList.length === 0 ? (
                  <div className="bg-slate-800/50 rounded-xl p-6 text-center">
                    <Wine className="h-10 w-10 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400 text-base">Nessun prodotto</p>
                  </div>
                ) : (
                  beverageList
                    .filter(b => {
                      // Filter by search term (local instant search)
                      if (beverageSearch && !b.nome.toLowerCase().includes(beverageSearch.toLowerCase())) {
                        return false;
                      }
                      // Filter by status
                      if (!beverageFilterStatus) return true;
                      if (beverageFilterStatus === 'ottimo') return b.margine_perc >= 70;
                      if (beverageFilterStatus === 'buono') return b.margine_perc >= 50 && b.margine_perc < 70;
                      if (beverageFilterStatus === 'attenzione') return b.margine_perc >= 30 && b.margine_perc < 50;
                      if (beverageFilterStatus === 'critico') return b.margine_perc < 30;
                      return true;
                    })
                    .map((beverage) => (
                    <div 
                      key={beverage.id}
                      className="bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-xl px-3 py-2.5 border border-slate-700 lg:border-gray-200"
                    >
                      {/* Row 1: Icon + Name */}
                      <div className="flex items-center gap-2.5">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${getBeverageCategoryColor(beverage.categoria_bevanda)}`}>
                          {getBeverageCategoryIcon(beverage.categoria_bevanda)}
                        </span>
                        <p className="text-sm text-white lg:text-gray-900 font-medium truncate flex-1 uppercase">
                          {beverage.nome}
                        </p>
                      </div>
                      
                      {/* Row 2: Prices prominent, then supplier */}
                      <div className="flex items-center justify-between mt-1.5 pl-9">
                        {/* Prices + Margin - LEFT side for visibility */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500" title="Prezzo netto fattura">€{beverage.prezzo_acquisto_netto?.toFixed(2) || '0.00'}</span>
                          <span className="text-slate-500 text-xs">+{beverage.aliquota_iva || 22}%→</span>
                          <span className="text-sm text-amber-400 lg:text-amber-600 font-bold" title={`Prezzo con IVA ${beverage.aliquota_iva || 22}%`}>€{beverage.prezzo_acquisto?.toFixed(2) || '0.00'}</span>
                          <span className="text-slate-500 text-sm">/</span>
                          {editingBeverage === beverage.id ? (
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={beverage.prezzo_vendita || ''}
                              className="w-16 h-7 text-sm px-1 bg-slate-700 lg:bg-gray-100 text-emerald-400 lg:text-emerald-600 border border-emerald-500 rounded text-center"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { updateBeveragePrice(beverage.id, e.target.value); }
                                if (e.key === 'Escape') { setEditingBeverage(null); }
                              }}
                              onBlur={(e) => { if (e.target.value) updateBeveragePrice(beverage.id, e.target.value); else setEditingBeverage(null); }}
                              autoFocus
                            />
                          ) : (
                            <span 
                              onClick={(e) => { e.stopPropagation(); setEditingBeverage(beverage.id); }}
                              className={`cursor-pointer text-sm font-bold ${beverage.prezzo_vendita > 0 ? 'text-emerald-400 lg:text-emerald-600' : 'text-slate-500'}`}
                            >
                              €{beverage.prezzo_vendita > 0 ? beverage.prezzo_vendita.toFixed(2) : '---'}
                            </span>
                          )}
                          {beverage.prezzo_vendita > 0 && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              beverage.margine_perc >= 70 ? 'bg-emerald-500/30 text-emerald-300 lg:text-emerald-600' :
                              beverage.margine_perc >= 50 ? 'bg-blue-500/30 text-blue-300 lg:text-blue-600' :
                              beverage.margine_perc >= 30 ? 'bg-amber-500/30 text-amber-300 lg:text-amber-600' : 
                              'bg-red-500/30 text-red-300 lg:text-red-600'
                            }`}>
                              {beverage.margine_perc}%
                            </span>
                          )}
                        </div>
                        
                        {/* Supplier - RIGHT side, smaller */}
                        <p className="text-[11px] text-slate-500 lg:text-gray-400 truncate max-w-[100px]">
                          {beverage.fornitore || ''}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Summary Footer */}
              {beverageList.length > 0 && (
                <div className="bg-slate-800/80 lg:bg-gray-100 rounded-xl p-4 flex flex-wrap gap-4 justify-between items-center">
                  <div>
                    <p className="text-xs text-slate-400 lg:text-gray-500">Totale prodotti</p>
                    <p className="text-lg font-bold text-white lg:text-gray-900">{beverageList.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 lg:text-gray-500">Con prezzo vendita</p>
                    <p className="text-lg font-bold text-emerald-400 lg:text-emerald-600">
                      {beverageList.filter(b => b.prezzo_vendita > 0).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 lg:text-gray-500">Senza prezzo</p>
                    <p className="text-lg font-bold text-amber-400 lg:text-amber-600">
                      {beverageList.filter(b => !b.prezzo_vendita || b.prezzo_vendita === 0).length}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab Calcolo Rese - Food Cost Reale */}
      {activeTab === 'costi' && <CostiProdotti headers={getAuthHeaders} />}

      {activeTab === 'calcolo_rese' && (
        <FoodCostCalculator />
      )}

      {/* Tab Analisi Marginalità */}
      {activeTab === 'analisi' && (
        <div className="space-y-4">
          {loadingAnalisi ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
            </div>
          ) : analisiMarginalita ? (
            <>
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                <Input
                  placeholder="Cerca..."
                  value={analisiSearchTerm}
                  onChange={(e) => setAnalisiSearchTerm(e.target.value)}
                  className="pl-8 sm:pl-10 h-9 sm:h-10 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400 rounded-lg"
                />
              </div>

              {/* Active Filter Badge - Compact */}
              {analisiFilterStatus && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${
                    analisiFilterStatus === 'ottimo' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                    analisiFilterStatus === 'buono' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                    analisiFilterStatus === 'attenzione' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {analisiFilterStatus.toUpperCase()}
                  </span>
                  <button 
                    onClick={() => setAnalisiFilterStatus('')}
                    className="text-[10px] sm:text-xs text-slate-400 hover:text-white flex items-center gap-0.5"
                  >
                    <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> <span className="hidden sm:inline">Rimuovi</span>
                  </button>
                </div>
              )}

              {/* KPI Cards - Clickable, colorful on mobile too */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="analisi-kpi-grid">
                <div 
                  className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl p-3 sm:p-4 cursor-pointer hover:scale-[1.02] transition-transform"
                  onClick={() => setAnalisiFilterStatus('')}
                >
                  <p className="text-blue-100 text-[10px] sm:text-xs font-medium">Margine Medio</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{analisiMarginalita.statistiche?.margine_medio_percentuale}%</p>
                  <p className="text-[9px] sm:text-[10px] text-blue-200">Vedi tutte</p>
                </div>
                <div 
                  className={`bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl p-3 sm:p-4 cursor-pointer hover:scale-[1.02] transition-transform ${
                    analisiFilterStatus === 'ottimo' ? 'ring-2 ring-white' : ''
                  }`}
                  onClick={() => setAnalisiFilterStatus(analisiFilterStatus === 'ottimo' ? '' : 'ottimo')}
                >
                  <p className="text-green-100 text-[10px] sm:text-xs font-medium">Ottimo (≥75%)</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{analisiMarginalita.statistiche?.ricette_ottimo}</p>
                  <p className="text-[9px] sm:text-[10px] text-green-200">Filtra</p>
                </div>
                <div 
                  className={`bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 cursor-pointer hover:scale-[1.02] transition-transform ${
                    analisiFilterStatus === 'attenzione' ? 'ring-2 ring-white' : ''
                  }`}
                  onClick={() => setAnalisiFilterStatus(analisiFilterStatus === 'attenzione' ? '' : 'attenzione')}
                >
                  <p className="text-yellow-100 text-[10px] sm:text-xs font-medium">Attenzione (55-65%)</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{analisiMarginalita.statistiche?.ricette_attenzione}</p>
                  <p className="text-[9px] sm:text-[10px] text-yellow-200">Filtra</p>
                </div>
                <div 
                  className={`bg-gradient-to-br from-red-500 to-rose-600 rounded-xl sm:rounded-2xl p-3 sm:p-4 cursor-pointer hover:scale-[1.02] transition-transform ${
                    analisiFilterStatus === 'critico' ? 'ring-2 ring-white' : ''
                  }`}
                  onClick={() => setAnalisiFilterStatus(analisiFilterStatus === 'critico' ? '' : 'critico')}
                >
                  <p className="text-red-100 text-[10px] sm:text-xs font-medium">Critiche (&lt;55%)</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{analisiMarginalita.statistiche?.ricette_critico}</p>
                  <p className="text-[9px] sm:text-[10px] text-red-200">Filtra</p>
                </div>
              </div>

              {/* Filtered results count */}
              {(() => {
                const filteredRicette = analisiMarginalita.ricette?.filter(r => {
                  const matchesStatus = !analisiFilterStatus || r.status === analisiFilterStatus;
                  const matchesSearch = !analisiSearchTerm || r.nome?.toLowerCase().includes(analisiSearchTerm.toLowerCase());
                  return matchesStatus && matchesSearch;
                }) || [];
                
                return (
                  <>
                    <p className="text-xs text-slate-400 lg:text-gray-500">
                      {filteredRicette.length} ricette {analisiFilterStatus ? `con status "${analisiFilterStatus}"` : ''} {analisiSearchTerm ? `contengono "${analisiSearchTerm}"` : ''}
                    </p>

                    {/* Mobile: Card List - Same style as Beverage */}
                    <div className="lg:hidden space-y-2">
                      {filteredRicette.map((r) => (
                        <div key={r.id} className="bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-xl px-3 py-2.5 border border-slate-700 lg:border-gray-200" data-testid={`analisi-card-${r.id}`}>
                          {/* Row 1: Name + Status Badge */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white lg:text-gray-900 font-medium truncate uppercase">{r.nome}</p>
                              <p className="text-[10px] text-slate-400 lg:text-gray-500">{r.categoria}</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0 ${
                              r.status === 'ottimo' ? 'bg-emerald-500/30 text-emerald-300' :
                              r.status === 'buono' ? 'bg-blue-500/30 text-blue-300' :
                              r.status === 'attenzione' ? 'bg-amber-500/30 text-amber-300' :
                              'bg-red-500/30 text-red-300'
                            }`}>
                              {r.status}
                            </span>
                          </div>
                          
                          {/* Row 2: Prices like Beverage style - con IVA e RESE */}
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm text-amber-400 font-bold">€{r.costo_ricetta?.toFixed(2)}</span>
                              <span className="text-slate-600">→</span>
                              <span className="text-sm text-red-400 font-bold">€{r.costo_reale?.toFixed(2)}</span>
                              <span className="text-slate-500 text-sm">/</span>
                              <span className="text-sm text-emerald-400 font-bold">€{r.prezzo_vendita?.toFixed(2)}</span>
                              {r.differenza_rese_pct > 0 && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                  r.differenza_rese_pct > 50 ? 'bg-red-500/30 text-red-300' :
                                  r.differenza_rese_pct > 25 ? 'bg-orange-500/30 text-orange-300' :
                                  'bg-yellow-500/30 text-yellow-300'
                                }`}>
                                  +{r.differenza_rese_pct?.toFixed(0)}%
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-[11px] text-emerald-400 font-medium">+€{r.margine_reale_euro?.toFixed(2)}</span>
                              <span className="text-[9px] text-slate-500">{r.margine_reale_pct?.toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {filteredRicette.length === 0 && (
                        <div className="text-center py-6 text-slate-400">
                          <Search className="h-6 w-6 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Nessuna ricetta trovata</p>
                        </div>
                      )}
                    </div>

                    {/* Desktop: Table - Filtered */}
                    <Card className="hidden lg:block">
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
                              <tr>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Piatto</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Vendita</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Costo Teorico</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-red-700 uppercase bg-red-50">Costo REALE</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Guadagno Teorico</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-red-700 uppercase bg-red-50">Guadagno REALE</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Margine %</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-red-700 uppercase bg-red-50">Diff Rese</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {filteredRicette.map((r) => (
                                <tr 
                                  key={r.id} 
                                  className="hover:bg-blue-100/70 transition-colors cursor-pointer"
                                  onClick={() => loadDettaglioRese(r.id)}
                                  data-testid={`ricetta-row-${r.id}`}
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <Eye className="h-4 w-4 text-blue-400 opacity-0 group-hover:opacity-100" />
                                      <div>
                                        <p className="font-medium text-gray-900">{r.nome}</p>
                                        <p className="text-xs text-gray-500">{r.categoria}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-right font-semibold text-gray-900">€{r.prezzo_vendita?.toFixed(2)}</td>
                                  <td className="py-3 px-4 text-right font-medium text-amber-600">€{r.costo_ricetta?.toFixed(2)}</td>
                                  <td className="py-3 px-4 text-right font-bold text-red-600 bg-red-50/30">€{r.costo_reale?.toFixed(2)}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-green-600">€{r.margine_euro?.toFixed(2)}</td>
                                  <td className="py-3 px-4 text-right font-bold text-green-700 bg-red-50/30">€{r.margine_reale_euro?.toFixed(2)}</td>
                                  <td className="py-3 px-4 text-center">
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        (r.margine_percentuale || 0) >= 75 ? 'bg-green-100 text-green-700' :
                                        (r.margine_percentuale || 0) >= 65 ? 'bg-blue-100 text-blue-700' :
                                        (r.margine_percentuale || 0) >= 55 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {r.margine_percentuale?.toFixed(0)}%
                                      </span>
                                      <span className="text-[10px] text-gray-400">
                                        reale: {r.margine_reale_pct?.toFixed(0)}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-center bg-red-50/30">
                                    {r.differenza_rese > 0 && (
                                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                        r.differenza_rese_pct > 50 ? 'bg-red-200 text-red-800' :
                                        r.differenza_rese_pct > 25 ? 'bg-orange-200 text-orange-800' :
                                        'bg-yellow-200 text-yellow-800'
                                      }`}>
                                        +{r.differenza_rese_pct?.toFixed(0)}%
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </>
          ) : (
            <div className="text-center py-12 bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-2xl">
              <BarChart3 className="h-12 w-12 text-slate-500 lg:text-blue-300 mx-auto mb-4" />
              <p className="text-slate-400 lg:text-gray-500">Nessuna ricetta da analizzare</p>
              <p className="text-sm text-slate-500 lg:text-gray-400 mt-2">Crea delle ricette o genera dal menu</p>
            </div>
          )}
        </div>
      )}

      {/* Tab Ricette */}
      {activeTab === 'ricette' && (
        <>
          {/* Filters */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
              <input 
                type="text"
                placeholder="Cerca..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-3 h-9 sm:h-11 rounded-lg sm:rounded-xl bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 text-white lg:text-gray-900 placeholder-slate-400 lg:placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select 
              value={selectedCategoria}
              onChange={(e) => setSelectedCategoria(e.target.value)}
              className="h-9 sm:h-11 px-2 sm:px-3 rounded-lg sm:rounded-xl bg-slate-800/70 backdrop-blur-sm lg:bg-white lg:border lg:border-gray-200 text-white lg:text-gray-900 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="" className="bg-slate-900/80 backdrop-blur-sm lg:bg-white">Tutte</option>
              {categorie.map(cat => (
                <option key={cat} value={cat} className="bg-slate-900/80 backdrop-blur-sm lg:bg-white">{cat}</option>
              ))}
            </select>
          </div>

          {/* Recipe List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
            </div>
          ) : ricette.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-2xl">
              <DollarSign className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400 lg:text-gray-500 mb-4">Nessuna ricetta trovata</p>
              <button 
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="px-5 py-2.5 bg-blue-600 rounded-xl text-white font-semibold"
              >
                Crea la prima ricetta
              </button>
            </div>
          ) : (
            <>
              {/* Mobile Card List - Same style as Beverage */}
              <div className="lg:hidden space-y-2">
                {ricette.map((ricetta) => (
                  <div 
                    key={ricetta.id}
                    onClick={() => loadRicettaDetail(ricetta)}
                    className="bg-slate-800/70 backdrop-blur-sm lg:bg-white rounded-xl px-3 py-2.5 border border-slate-700 lg:border-gray-200 active:scale-[0.98] transition-transform cursor-pointer"
                  >
                    {/* Row 1: Name + Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white lg:text-gray-900 font-medium truncate uppercase">{ricetta.nome}</p>
                        <p className="text-[10px] text-slate-400 lg:text-gray-500">{ricetta.ingredienti?.length || 0} ing. · {ricetta.categoria || 'N/A'}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                        (ricetta.margine_percentuale || 0) >= 75 ? 'bg-emerald-500/30 text-emerald-300' :
                        (ricetta.margine_percentuale || 0) >= 65 ? 'bg-blue-500/30 text-blue-300' :
                        (ricetta.margine_percentuale || 0) >= 55 ? 'bg-amber-500/30 text-amber-300' : 'bg-red-500/30 text-red-300'
                      }`}>
                        {(ricetta.margine_percentuale || 0).toFixed(0)}%
                      </span>
                    </div>
                    
                    {/* Row 2: Prices like Beverage style */}
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-amber-400 font-bold">€{(ricetta.costo_ricetta || 0).toFixed(2)}</span>
                        <span className="text-slate-500 text-sm">/</span>
                        <span className="text-sm text-emerald-400 font-bold">€{(ricetta.prezzo_vendita || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-emerald-400 font-medium">+€{(ricetta.margine_euro || 0).toFixed(2)}</span>
                        <ChevronRight className="h-4 w-4 text-slate-500" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table */}
              <Card className="hidden lg:block border-gray-100 shadow-sm">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Ricetta</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Vendita</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Costo</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Guadagno</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Margine %</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Categoria</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-blue-900 uppercase">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {ricette.map((ricetta) => (
                          <tr key={ricetta.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="py-3 px-4">
                              <p className="font-medium text-gray-900">{ricetta.nome}</p>
                              <p className="text-xs text-blue-500">{ricetta.ingredienti?.length || 0} ingredienti</p>
                            </td>
                            <td className="py-3 px-4 text-right font-semibold text-blue-900">
                              €{(ricetta.prezzo_vendita || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-gray-700">
                              €{(ricetta.costo_ricetta || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold text-green-600">
                              €{(ricetta.margine_euro || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                                (ricetta.margine_percentuale || 0) >= 75 ? 'text-green-600 bg-green-50 border-green-200' :
                                (ricetta.margine_percentuale || 0) >= 65 ? 'text-blue-600 bg-blue-50 border-blue-200' :
                                (ricetta.margine_percentuale || 0) >= 55 ? 'text-yellow-600 bg-yellow-50 border-yellow-200' : 'text-red-600 bg-red-50 border-red-200'
                              }`}>
                                {(ricetta.margine_percentuale || 0).toFixed(1)}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                {ricetta.categoria || '-'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => loadRicettaDetail(ricetta)} className="hover:bg-blue-100 hover:text-blue-600" title="Vedi dettaglio">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEditModal(ricetta)} className="hover:bg-blue-100 hover:text-blue-600">
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteRicetta(ricetta.id)} className="hover:bg-red-100 hover:text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

      {/* Info Card - iOS Style */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-0 rounded-2xl">
        <CardContent className="py-4 px-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-blue-900 text-sm">Come calcolare il Food Cost</p>
            <p className="text-xs text-blue-700">
              L&apos;incidenza indica quanto costa produrre un piatto rispetto al prezzo di vendita. 
              Un valore ideale è tra il 25% e il 35%.
            </p>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
              <h2 className="text-xl font-bold text-blue-900">
                {editingRicetta ? 'Modifica Ricetta' : 'Nuova Ricetta'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreateModal(false); setEditingRicetta(null); }} className="hover:bg-white/50">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Nome Ricetta</label>
                  <Input 
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Es. Frittura di pesce mista"
                    className="mt-1 border-gray-200 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Categoria</label>
                  <Input 
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    placeholder="Es. Secondi, Antipasti"
                    className="mt-1 border-gray-200 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Prezzo Vendita (€)</label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.prezzo_vendita}
                    onChange={(e) => setFormData({...formData, prezzo_vendita: e.target.value})}
                    placeholder="0.00"
                    className="mt-1 border-gray-200 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Ingredienti */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  Ingredienti
                  <span className="text-xs text-gray-400 font-normal">(Cerca nei DDT per ottenere prezzi automatici)</span>
                </label>
                <div className="mt-2 space-y-2">
                  {formData.ingredienti.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2 bg-blue-50 p-3 rounded-xl">
                      <span className="flex-1 font-medium text-blue-900">{ing.nome}</span>
                      <span className="text-blue-600">{ing.quantita} {ing.unita_misura}</span>
                      <span className="text-blue-500">€{ing.costo_kg}/{ing.unita_misura === 'pz' ? 'pz' : 'kg'}</span>
                      {ing.prodotto_ddt && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">DDT ✓</span>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => removeIngrediente(i)} className="hover:bg-red-100 hover:text-red-600">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                {/* Form nuovo ingrediente con ricerca DDT */}
                <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Aggiungi Ingrediente</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          cercaSottoRicette('');
                          setShowSottoRicetteSearch(!showSottoRicetteSearch);
                        }}
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <Package className="h-3 w-3 mr-1" />
                        + Ricetta Base
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (newIngrediente.nome && newIngrediente.nome.length >= 2) {
                            setSearchProdotti(newIngrediente.nome);
                            cercaProdottiFatture(newIngrediente.nome);
                            setShowProdottiSearchInCreate(true);
                          } else {
                            toast.info('Inserisci almeno 2 caratteri nel nome per cercare');
                          }
                        }}
                        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Search className="h-3 w-3 mr-1" />
                        Cerca in DDT
                      </Button>
                    </div>
                  </div>

                  {/* Sezione Aggiungi Sotto-Ricetta */}
                  {showSottoRicetteSearch && (
                    <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">Aggiungi Ricetta Base (sotto-ricetta)</span>
                      </div>
                      <p className="text-xs text-amber-600 mb-3">
                        Es: "Base Pinsa", "Impasto Pizza", "Salsa Pomodoro" - il costo viene calcolato dalla ricetta
                      </p>
                      
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Cerca ricetta..."
                          value={searchSottoRicetta}
                          onChange={(e) => {
                            setSearchSottoRicetta(e.target.value);
                            if (e.target.value.length >= 2) {
                              cercaSottoRicette(e.target.value);
                            }
                          }}
                          className="flex-1 bg-white"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowSottoRicetteSearch(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {loadingSottoRicette ? (
                        <div className="text-center py-3">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto text-amber-600" />
                        </div>
                      ) : sottoRicetteDisponibili.length > 0 ? (
                        <div className="max-h-40 overflow-auto space-y-1">
                          {sottoRicetteDisponibili
                            .filter(r => r.id !== editingRicetta?.id) // Escludi la ricetta corrente
                            .map((ric) => (
                            <button
                              key={ric.id}
                              onClick={() => aggiungiSottoRicetta(editingRicetta?.id, ric.id, 1)}
                              className="w-full flex justify-between items-center p-2 bg-white hover:bg-amber-100 rounded-lg border border-amber-100 transition-colors text-left"
                            >
                              <div>
                                <span className="font-medium text-gray-900">{ric.nome}</span>
                                <span className="text-xs text-gray-500 ml-2">{ric.categoria}</span>
                              </div>
                              <span className="text-sm font-bold text-amber-600">€{ric.costo_porzione?.toFixed(2)}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600 text-center py-2">
                          {searchSottoRicetta ? 'Nessuna ricetta trovata' : 'Cerca una ricetta da aggiungere come base'}
                        </p>
                      )}
                      
                      <p className="text-[10px] text-amber-500 mt-2 italic">
                        💡 Crea prima la ricetta "Base Pinsa" o "Impasto Pizza" con i suoi ingredienti, poi aggiungila qui
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-5 gap-2">
                    <Input 
                      className="col-span-2 border-gray-200"
                      placeholder="Nome ingrediente"
                      value={newIngrediente.nome}
                      onChange={(e) => setNewIngrediente({...newIngrediente, nome: e.target.value})}
                    />
                    <Input 
                      type="number"
                      className="border-gray-200"
                      placeholder="Qtà"
                      value={newIngrediente.quantita}
                      onChange={(e) => setNewIngrediente({...newIngrediente, quantita: e.target.value})}
                    />
                    <select 
                      className="border border-gray-200 rounded-lg px-2"
                      value={newIngrediente.unita_misura}
                      onChange={(e) => setNewIngrediente({...newIngrediente, unita_misura: e.target.value})}
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="l">l</option>
                      <option value="pz">pz</option>
                    </select>
                    <Input 
                      type="number"
                      step="0.01"
                      className="border-gray-200"
                      placeholder="€/kg"
                      value={newIngrediente.costo_kg}
                      onChange={(e) => setNewIngrediente({...newIngrediente, costo_kg: e.target.value})}
                    />
                  </div>
                  
                  {/* Risultati ricerca DDT inline */}
                  {showProdottiSearchInCreate && prodottiTrovati.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto border border-purple-200 rounded-lg bg-white">
                      <div className="p-2 bg-purple-50 border-b border-purple-200 flex justify-between items-center">
                        <span className="text-xs text-purple-700 font-medium">{prodottiTrovati.length} prodotti trovati nei DDT</span>
                        <Button variant="ghost" size="sm" onClick={() => setShowProdottiSearchInCreate(false)} className="h-6 w-6 p-0">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {prodottiTrovati.slice(0, 10).map((prod, idx) => (
                        <div 
                          key={idx}
                          onClick={() => {
                            setNewIngrediente({
                              ...newIngrediente,
                              nome: prod.descrizione,
                              costo_kg: prod.prezzo_kg?.toFixed(2) || '',
                              prodotto_ddt: true,
                              fornitore: prod.fornitore
                            });
                            setShowProdottiSearchInCreate(false);
                            toast.success(`Prezzo da DDT: €${prod.prezzo_kg?.toFixed(2)}/kg - ${prod.fornitore}`);
                          }}
                          className="p-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-0"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{prod.descrizione}</p>
                              <p className="text-xs text-gray-500">{prod.fornitore}</p>
                            </div>
                            <div className="text-right ml-2">
                              <p className="text-sm font-bold text-purple-600">€{prod.prezzo_kg?.toFixed(2)}/kg</p>
                              <p className="text-xs text-gray-400">{prod.data_fattura}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" className="mt-3 border-blue-200 text-blue-600 hover:bg-blue-50" onClick={addIngrediente}>
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi ingrediente
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingRicetta(null); }}>
                Annulla
              </Button>
              <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" onClick={saveRicetta}>
                {editingRicetta ? 'Salva Modifiche' : 'Crea Ricetta'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dettaglio Ricetta */}
      {selectedRicetta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header - Editabile */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <input
                    type="text"
                    defaultValue={selectedRicetta.nome}
                    className="text-xl font-bold text-blue-900 bg-transparent border-b-2 border-transparent hover:border-blue-300 focus:border-blue-500 focus:outline-none w-full"
                    onBlur={(e) => {
                      if (e.target.value !== selectedRicetta.nome) {
                        aggiornaRicetta(selectedRicetta.id, 'nome', e.target.value);
                      }
                    }}
                  />
                  <input
                    type="text"
                    defaultValue={selectedRicetta.categoria}
                    placeholder="Categoria"
                    className="text-sm text-blue-600 bg-transparent border-b border-transparent hover:border-blue-200 focus:border-blue-400 focus:outline-none mt-1"
                    onBlur={(e) => {
                      if (e.target.value !== selectedRicetta.categoria) {
                        aggiornaRicetta(selectedRicetta.id, 'categoria', e.target.value);
                      }
                    }}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedRicetta(null); setRicettaDetail(null); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* KPI Ricetta - Prezzo editabile */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Prezzo Vendita</p>
                  <div className="flex items-center">
                    <span className="text-lg font-bold text-blue-900">€</span>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={selectedRicetta.prezzo_vendita || 0}
                      className="text-xl font-bold text-blue-900 bg-transparent border-b border-transparent hover:border-blue-300 focus:border-blue-500 focus:outline-none w-20"
                      onBlur={(e) => {
                        const nuovoPrezzo = parseFloat(e.target.value);
                        if (nuovoPrezzo !== selectedRicetta.prezzo_vendita) {
                          aggiornaRicetta(selectedRicetta.id, 'prezzo_vendita', nuovoPrezzo);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Costo Netto</p>
                  <p className="text-lg font-medium text-gray-600">€{(selectedRicetta.costo_ricetta_netto || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Costo +IVA</p>
                  <p className="text-xl font-bold text-orange-600">€{(selectedRicetta.costo_ricetta || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Guadagno</p>
                  <p className="text-xl font-bold text-green-600">€{(selectedRicetta.margine_euro || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Margine %</p>
                  <p className={`text-xl font-bold ${(selectedRicetta.margine_percentuale || 0) >= 65 ? 'text-green-600' : 'text-red-600'}`}>
                    {(selectedRicetta.margine_percentuale || 0).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
            
            {/* Ingredienti */}
            <div className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Ingredienti e Fonte Prezzi
              </h3>
              
              {loadingDetail ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {(ricettaDetail?.ingredienti || selectedRicetta.ingredienti || []).map((ing, idx) => {
                    const costoIng = ((ing.quantita || 0) / 1000) * (ing.costo_kg || 0);
                    return (
                      <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            {/* Nome ingrediente e badge IVA */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-900">{ing.nome}</p>
                              {ing.aliquota_iva !== undefined && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  ing.aliquota_iva === 4 ? 'bg-green-100 text-green-700' :
                                  ing.aliquota_iva === 10 ? 'bg-blue-100 text-blue-700' :
                                  'bg-orange-100 text-orange-700'
                                }`}>
                                  IVA {ing.aliquota_iva}%
                                </span>
                              )}
                            </div>
                            
                            {/* FORNITORE - Ben visibile */}
                            {ing.fornitore ? (
                              <div className="flex items-center gap-1.5 mt-1.5 text-sm">
                                <span className="text-gray-500">Fornitore:</span>
                                <span className="font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                                  {ing.fornitore}
                                </span>
                              </div>
                            ) : (
                              <p className="text-xs text-amber-600 mt-1">⚠️ Fornitore non identificato</p>
                            )}
                            
                            {/* Quantità e costi */}
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
                                <input 
                                  type="number"
                                  defaultValue={ing.quantita || 100}
                                  className="w-16 text-sm font-medium text-gray-900 bg-transparent focus:outline-none"
                                  onBlur={(e) => {
                                    const nuovaQta = parseFloat(e.target.value);
                                    if (nuovaQta && nuovaQta !== ing.quantita) {
                                      aggiornaQuantitaIngrediente(selectedRicetta.id, idx, nuovaQta);
                                    }
                                  }}
                                />
                                <span className="text-xs text-gray-500 font-medium">{ing.unita_misura || 'g'}</span>
                              </div>
                              <span className="text-sm text-gray-500">×</span>
                              <span className="text-sm font-medium text-gray-700">€{(ing.costo_kg || 0).toFixed(2)}/kg</span>
                            </div>
                            
                            {/* Costi Netto → Lordo */}
                            {ing.costo_netto !== undefined && (
                              <div className="flex items-center gap-2 mt-2 text-sm">
                                <span className="text-gray-500">Costo:</span>
                                <span className="text-gray-600">€{ing.costo_netto?.toFixed(3)}</span>
                                <span className="text-gray-400">→</span>
                                <span className="font-semibold text-amber-600">€{ing.costo_lordo?.toFixed(3)}</span>
                                <span className="text-xs text-gray-400">(+IVA)</span>
                              </div>
                            )}
                            
                            {/* Prodotto fattura abbinato */}
                            {ing.prodotto_fattura ? (
                              <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200 flex flex-wrap items-center justify-between gap-1">
                                <div className="flex items-center gap-2 max-w-full">
                                  <FileText className="h-4 w-4 shrink-0 text-green-600" />
                                  <span className="text-sm text-green-800 [overflow-wrap:anywhere]">{ing.prodotto_fattura}</span>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-6 px-2 text-xs text-green-700 hover:bg-green-100"
                                  onClick={() => openCercaProdotto(selectedRicetta.id, idx, ing)}
                                >
                                  <Search className="h-3 w-3 mr-1" />
                                  Cambia
                                </Button>
                              </div>
                            ) : (
                              <Button 
                                variant="outline"
                                size="sm"
                                className="mt-2 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => openCercaProdotto(selectedRicetta.id, idx, ing)}
                              >
                                <Search className="h-3 w-3 mr-1" />
                                Abbina a prodotto fattura
                              </Button>
                            )}
                          </div>
                          
                          {/* Colonna destra - Costo totale e azioni */}
                          <div className="text-right ml-2 sm:ml-4 shrink-0 flex flex-col items-end">
                            <p className="text-lg font-bold text-blue-900">€{costoIng.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full mt-1 ${
                              ing.prodotto_fattura ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {ing.prodotto_fattura ? '✓ Abbinato' : '○ Da abbinare'}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-7 w-7 p-0 mt-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                              onClick={() => eliminaIngrediente(selectedRicetta.id, idx)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Form aggiungi ingrediente - Migliorato con unità di misura */}
                  <div className="mt-4 p-4 border-2 border-dashed border-blue-200 rounded-xl bg-blue-50/30">
                    <p className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Aggiungi nuovo ingrediente
                    </p>
                    <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 items-end">
                      {/* Nome ingrediente */}
                      <div className="col-span-6 sm:col-span-5">
                        <label className="text-xs text-gray-500 mb-1 block">Nome</label>
                        <Input 
                          placeholder="Es: Pomodori, Mozzarella..."
                          id="nuovo-ing-nome"
                          className="text-sm"
                        />
                      </div>
                      {/* Quantità */}
                      <div className="col-span-3 sm:col-span-2">
                        <label className="text-xs text-gray-500 mb-1 block">Quantità</label>
                        <Input 
                          type="number"
                          placeholder="100"
                          defaultValue="100"
                          id="nuovo-ing-qta"
                          className="text-sm"
                        />
                      </div>
                      {/* Unità di misura */}
                      <div className="col-span-3 sm:col-span-2">
                        <label className="text-xs text-gray-500 mb-1 block">Unità</label>
                        <select 
                          id="nuovo-ing-um"
                          className="w-full h-9 px-2 text-sm border border-gray-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                          defaultValue="g"
                        >
                          <option value="g">g (grammi)</option>
                          <option value="kg">kg (chili)</option>
                          <option value="ml">ml (millilitri)</option>
                          <option value="l">l (litri)</option>
                          <option value="pz">pz (pezzi)</option>
                          <option value="cl">cl (centilitri)</option>
                        </select>
                      </div>
                      {/* Costo al kg (opzionale) */}
                      <div className="col-span-4 sm:col-span-2">
                        <label className="text-xs text-gray-500 mb-1 block">€/kg <span className="text-gray-400">(opz.)</span></label>
                        <Input 
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          id="nuovo-ing-costo"
                          className="text-sm"
                        />
                      </div>
                      {/* Bottone aggiungi */}
                      <div className="col-span-2 sm:col-span-1">
                        <Button 
                          size="sm"
                          className="w-full h-9 bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            const nome = document.getElementById('nuovo-ing-nome').value;
                            const qta = document.getElementById('nuovo-ing-qta').value;
                            const um = document.getElementById('nuovo-ing-um').value;
                            const costo = document.getElementById('nuovo-ing-costo').value;
                            if (nome) {
                              aggiungiIngredienteARicetta(selectedRicetta.id, {
                                nome: nome,
                                quantita: parseFloat(qta) || 100,
                                unita_misura: um,
                                costo_kg: costo ? parseFloat(costo) : 0
                              });
                              document.getElementById('nuovo-ing-nome').value = '';
                              document.getElementById('nuovo-ing-qta').value = '100';
                              document.getElementById('nuovo-ing-um').value = 'g';
                              document.getElementById('nuovo-ing-costo').value = '';
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      💡 Dopo aver aggiunto, potrai abbinare l'ingrediente a un prodotto dalle fatture per ottenere il costo reale
                    </p>
                  </div>
                </div>
              )}
              
              {/* Note */}
              {selectedRicetta.note && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-900">{selectedRicetta.note}</p>
                </div>
              )}
              
              {/* Legenda */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-xs font-semibold text-gray-700 mb-2">Legenda fonte prezzi:</p>
                <div className="flex gap-4">
                  <span className="text-xs flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    Da Fattura = prezzo reale acquisto
                  </span>
                  <span className="text-xs flex items-center gap-1">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                    Da abbinare = cerca nelle fatture
                  </span>
                </div>
              </div>
            </div>
            
            {/* Footer - Solo bottone Chiudi */}
            <div className="p-6 border-t border-gray-100 flex justify-end bg-gray-50">
              <Button onClick={() => { setSelectedRicetta(null); setRicettaDetail(null); }}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Match */}
      {showMatchModal && matchDaConfermare.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-amber-900">Conferma Abbinamenti</h2>
                  <p className="text-sm text-amber-700">Seleziona il prodotto corretto per ogni ingrediente</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowMatchModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Lista Match */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {matchDaConfermare.map((match, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="mb-3">
                    <p className="font-semibold text-gray-900">{match.ricetta_nome}</p>
                    <p className="text-sm text-blue-600">Ingrediente: <span className="font-medium">{match.ingrediente}</span></p>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-2">Seleziona il prodotto corretto dalle fatture:</p>
                  
                  <div className="space-y-2">
                    {match.opzioni?.map((opzione, optIdx) => (
                      <div 
                        key={optIdx}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                        onClick={() => handleConfermaMatch(match, opzione)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{opzione.prodotto}</p>
                            <p className="text-xs text-gray-500">
                              {opzione.fornitore} • {opzione.fattura_rif}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600">€{opzione.prezzo_kg?.toFixed(2)}/kg</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              opzione.confidenza === 'alta' ? 'bg-green-100 text-green-700' :
                              opzione.confidenza === 'media' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {opzione.confidenza || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {loadingMatch && (
                    <div className="mt-2 flex items-center gap-2 text-blue-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Salvando...</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="p-6 border-t border-gray-100 flex justify-between bg-gray-50">
              <p className="text-sm text-gray-500">
                {matchDaConfermare.length} abbinamenti rimanenti
              </p>
              <Button onClick={() => setShowMatchModal(false)}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ricerca Prodotti Fatture */}
      {showProdottiModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-blue-900">Cerca Prodotto dalle Fatture</h2>
                  <p className="text-sm text-blue-700">
                    Ingrediente: <span className="font-semibold">{editingIngrediente?.nome}</span>
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProdottiModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex gap-2">
                <Input 
                  placeholder="Cerca prodotto..."
                  value={searchProdotti}
                  onChange={(e) => setSearchProdotti(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      cercaProdottiFatture(searchProdotti);
                    }
                  }}
                />
                <Button 
                  onClick={() => cercaProdottiFatture(searchProdotti)}
                  disabled={loadingProdotti}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loadingProdotti ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Suggerimenti: prova &ldquo;olio&rdquo;, &ldquo;mozzarella&rdquo;, &ldquo;pomodoro&rdquo;, ecc.
              </p>
            </div>
            
            {/* Results */}
            <div className="p-4 max-h-[50vh] overflow-y-auto">
              {loadingProdotti ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : prodottiTrovati.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun prodotto trovato</p>
                  <p className="text-sm">Prova con una parola chiave diversa</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {prodottiTrovati.map((prod, idx) => (
                    <div 
                      key={idx}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                      onClick={() => selezionaProdottoPerIngrediente(prod)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{prod.descrizione}</p>
                          <p className="text-xs text-gray-500">
                            {prod.fornitore || 'Fornitore N/A'} • {prod.occorrenze || 0} acquisti
                          </p>
                        </div>
                        <p className="font-bold text-blue-600">€{(prod.ultimo_prezzo || prod.prezzo || 0).toFixed(2)}/kg</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {prodottiTrovati.length} prodotti trovati
              </p>
              <Button variant="outline" onClick={() => setShowProdottiModal(false)}>
                Annulla
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Analisi Food Cost da Menu */}
      {showAnalisiMenu && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="analisi-menu-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-100 rounded-xl">
                  <BarChart3 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Analizza Food Cost Menu</h3>
                  <p className="text-sm text-gray-500">Carica un menu (PDF, immagine o TXT) per analizzare i costi</p>
                </div>
              </div>
              <button 
                onClick={() => { 
                  setShowAnalisiMenu(false); 
                  setAnalisiMenuFile(null); 
                  setAnalisiMenuRisultato(null); 
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-160px)]">
              {!analisiMenuRisultato ? (
                <div className="space-y-4">
                  {/* Upload Area */}
                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      analisiMenuFile 
                        ? 'border-emerald-300 bg-emerald-50' 
                        : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/30'
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf,.txt,.jpg,.jpeg,.png,.webp,.heic"
                      onChange={(e) => setAnalisiMenuFile(e.target.files[0])}
                      className="hidden"
                      id="menu-file-upload"
                      data-testid="menu-file-input"
                    />
                    <label htmlFor="menu-file-upload" className="cursor-pointer block">
                      {analisiMenuFile ? (
                        <div className="space-y-2">
                          <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                            {analisiMenuFile.name.endsWith('.pdf') ? (
                              <FileText className="h-8 w-8 text-emerald-600" />
                            ) : analisiMenuFile.name.match(/\.(jpg|jpeg|png|webp|heic)$/i) ? (
                              <FileImage className="h-8 w-8 text-emerald-600" />
                            ) : (
                              <FileType className="h-8 w-8 text-emerald-600" />
                            )}
                          </div>
                          <p className="font-semibold text-emerald-700">{analisiMenuFile.name}</p>
                          <p className="text-sm text-emerald-600">
                            {(analisiMenuFile.size / 1024).toFixed(1)} KB
                          </p>
                          <button 
                            onClick={(e) => { e.preventDefault(); setAnalisiMenuFile(null); }}
                            className="text-xs text-red-500 hover:text-red-700 underline"
                          >
                            Rimuovi
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <Upload className="h-8 w-8 text-gray-400" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-700">Clicca per caricare un file</p>
                            <p className="text-sm text-gray-500">PDF, immagine (JPG, PNG) o TXT</p>
                          </div>
                        </div>
                      )}
                    </label>
                  </div>
                  
                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <p className="font-semibold mb-1">Come funziona:</p>
                        <ul className="space-y-1 text-blue-600">
                          <li>• L'AI estrae automaticamente i piatti dal menu</li>
                          <li>• Stima gli ingredienti per ogni piatto</li>
                          <li>• Calcola il food cost usando i prezzi delle tue fatture</li>
                          <li>• Mostra margini e valutazione per ogni piatto</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Button */}
                  <button
                    onClick={analizzaMenuFoodCost}
                    disabled={!analisiMenuFile || analisiMenuLoading}
                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                    data-testid="analizza-menu-submit"
                  >
                    {analisiMenuLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Analisi in corso...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        Analizza Food Cost
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Info IVA */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-700 flex items-center gap-2">
                    <span className="font-semibold">ℹ️ Info Prezzi:</span>
                    <span>I prezzi fattura sono <strong>NETTI</strong> (senza IVA). L'IVA viene applicata automaticamente (4% ortofrutta, 10% carni/pesce, 22% bevande).</span>
                  </div>
                  
                  {/* RIEPILOGO MENU COMPLETO - Nuova sezione */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                    <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                      Riepilogo Menu Completo
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Costo Totale Menu */}
                      <div className="bg-white/20 rounded-lg p-3 text-center">
                        <p className="text-white/80 text-xs mb-1">Food Cost Totale</p>
                        <p className="text-2xl font-bold">€{analisiMenuRisultato.statistiche?.totale_food_cost?.toFixed(2) || '0.00'}</p>
                        <p className="text-white/60 text-[10px]">Tutti i piatti (IVA incl.)</p>
                      </div>
                      
                      {/* Prezzo Menu - EDITABILE */}
                      <div className="bg-white/20 rounded-lg p-3 text-center">
                        <p className="text-white/80 text-xs mb-1">Prezzo Menu Vendita</p>
                        {(analisiMenuRisultato.prezzoMenuVendita || 0) > 0 ? (
                          <div className="flex items-center justify-center gap-2">
                            <p className="text-2xl font-bold">€{analisiMenuRisultato.prezzoMenuVendita?.toFixed(2)}</p>
                            <button
                              onClick={() => {
                                const nuovoPrezzo = prompt('Modifica prezzo menu completo (€):', analisiMenuRisultato.prezzoMenuVendita);
                                if (nuovoPrezzo && !isNaN(parseFloat(nuovoPrezzo))) {
                                  const prezzo = parseFloat(nuovoPrezzo);
                                  const costoTotale = analisiMenuRisultato.statistiche?.totale_food_cost || 0;
                                  setAnalisiMenuRisultato(prev => ({
                                    ...prev,
                                    prezzoMenuVendita: prezzo,
                                    margineMenuEuro: prezzo - costoTotale,
                                    margineMenuPerc: prezzo > 0 ? ((prezzo - costoTotale) / prezzo * 100) : 0,
                                    foodCostMenuPerc: prezzo > 0 ? (costoTotale / prezzo * 100) : 0
                                  }));
                                  toast.success(`Prezzo menu aggiornato: €${prezzo.toFixed(2)}`);
                                }
                              }}
                              className="p-1 bg-white/30 rounded hover:bg-white/50 transition-colors"
                              title="Modifica prezzo"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              const nuovoPrezzo = prompt('Inserisci il prezzo di vendita del MENU COMPLETO (€):', '50');
                              if (nuovoPrezzo && !isNaN(parseFloat(nuovoPrezzo))) {
                                const prezzo = parseFloat(nuovoPrezzo);
                                const costoTotale = analisiMenuRisultato.statistiche?.totale_food_cost || 0;
                                setAnalisiMenuRisultato(prev => ({
                                  ...prev,
                                  prezzoMenuVendita: prezzo,
                                  margineMenuEuro: prezzo - costoTotale,
                                  margineMenuPerc: prezzo > 0 ? ((prezzo - costoTotale) / prezzo * 100) : 0,
                                  foodCostMenuPerc: prezzo > 0 ? (costoTotale / prezzo * 100) : 0
                                }));
                                toast.success(`Prezzo menu impostato: €${prezzo.toFixed(2)}`);
                              }
                            }}
                            className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors font-bold text-sm"
                          >
                            + Inserisci Prezzo Menu
                          </button>
                        )}
                      </div>
                      
                      {/* Margine Menu */}
                      <div className="bg-white/20 rounded-lg p-3 text-center">
                        <p className="text-white/80 text-xs mb-1">Margine Menu</p>
                        {(analisiMenuRisultato.prezzoMenuVendita || 0) > 0 ? (
                          <>
                            <p className="text-2xl font-bold">€{analisiMenuRisultato.margineMenuEuro?.toFixed(2)}</p>
                            <p className="text-white/60 text-[10px]">Guadagno netto</p>
                          </>
                        ) : (
                          <p className="text-lg text-white/50">—</p>
                        )}
                      </div>
                      
                      {/* Food Cost % Menu */}
                      <div className="bg-white/20 rounded-lg p-3 text-center">
                        <p className="text-white/80 text-xs mb-1">Food Cost % Menu</p>
                        {(analisiMenuRisultato.prezzoMenuVendita || 0) > 0 ? (
                          <>
                            <p className={`text-2xl font-bold ${
                              analisiMenuRisultato.foodCostMenuPerc <= 25 ? 'text-green-300' 
                              : analisiMenuRisultato.foodCostMenuPerc <= 35 ? 'text-yellow-300' 
                              : 'text-red-300'
                            }`}>{analisiMenuRisultato.foodCostMenuPerc?.toFixed(1)}%</p>
                            <p className="text-white/60 text-[10px]">
                              {analisiMenuRisultato.foodCostMenuPerc <= 25 ? '✓ Ottimo' 
                              : analisiMenuRisultato.foodCostMenuPerc <= 35 ? '⚠ Buono' 
                              : '✗ Da rivedere'}
                            </p>
                          </>
                        ) : (
                          <p className="text-lg text-white/50">—</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Statistiche per Singolo Piatto (opzionale) */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900">
                      📊 Statistiche per singolo piatto (se hai prezzi individuali)
                    </summary>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-blue-700">{analisiMenuRisultato.piatti_analizzati}</p>
                        <p className="text-xs text-blue-600">Piatti Analizzati</p>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-emerald-700">{analisiMenuRisultato.statistiche?.food_cost_medio_perc?.toFixed(1) || '—'}%</p>
                        <p className="text-xs text-emerald-600">Food Cost Medio</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-purple-700">{analisiMenuRisultato.statistiche?.margine_medio_perc?.toFixed(1) || '—'}%</p>
                        <p className="text-xs text-purple-600">Margine Medio</p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-amber-700">€{analisiMenuRisultato.statistiche?.totale_food_cost?.toFixed(2)}</p>
                        <p className="text-xs text-amber-600">Costo Totale</p>
                      </div>
                    </div>
                  </details>
                  
                  {/* Valutazione Piatti */}
                  <div className="flex flex-wrap gap-2 text-sm mt-4">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                      ✓ {analisiMenuRisultato.statistiche?.piatti_ottimi || 0} Ottimi
                    </span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                      ⚠ {analisiMenuRisultato.statistiche?.piatti_buoni || 0} Buoni
                    </span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">
                      ✗ {analisiMenuRisultato.statistiche?.piatti_da_rivedere || 0} Da rivedere
                    </span>
                    {analisiMenuRisultato.statistiche?.piatti_non_valutabili > 0 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                        ○ {analisiMenuRisultato.statistiche?.piatti_non_valutabili} Non valutabili
                      </span>
                    )}
                  </div>
                  
                  {/* Lista Piatti */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Dettaglio Piatti</h4>
                    {analisiMenuRisultato.risultati?.map((piatto, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-semibold text-gray-900">{piatto.nome}</h5>
                            <p className="text-xs text-gray-500">{piatto.categoria}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            piatto.valutazione === 'ottimo' 
                              ? 'bg-green-100 text-green-700' 
                              : piatto.valutazione === 'buono'
                                ? 'bg-yellow-100 text-yellow-700'
                                : piatto.valutazione === 'non_valutabile'
                                  ? 'bg-gray-100 text-gray-500'
                                  : piatto.valutazione === 'prezzo_mancante'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-red-100 text-red-700'
                          }`}>
                            {piatto.valutazione === 'ottimo' ? '✓ Ottimo' 
                              : piatto.valutazione === 'buono' ? '⚠ Buono' 
                              : piatto.valutazione === 'non_valutabile' ? '○ Non valutabile'
                              : piatto.valutazione === 'prezzo_mancante' ? '💰 Prezzo mancante'
                              : '✗ Da rivedere'}
                          </span>
                        </div>
                        
                        {/* Info prezzi - mostro sempre se ci sono ingredienti */}
                        {(piatto.valutazione !== 'non_valutabile' || piatto.ingredienti?.length > 0) && (
                          <div className="grid grid-cols-4 gap-2 text-sm mb-3">
                            <div>
                              <p className="text-gray-500 text-xs">Prezzo Vendita</p>
                              {piatto.prezzo_vendita > 0 ? (
                                <div className="flex items-center gap-1">
                                  <p className="font-semibold text-gray-900">€{piatto.prezzo_vendita?.toFixed(2)}</p>
                                  <button
                                    onClick={() => {
                                      const nuovoPrezzo = prompt(`Modifica prezzo vendita per "${piatto.nome}":`, piatto.prezzo_vendita);
                                      if (nuovoPrezzo && !isNaN(parseFloat(nuovoPrezzo))) {
                                        const prezzo = parseFloat(nuovoPrezzo);
                                        setAnalisiMenuRisultato(prev => ({
                                          ...prev,
                                          risultati: prev.risultati.map((pl, i) => {
                                            if (i === idx) {
                                              const foodCostPerc = prezzo > 0 ? (pl.costo_lordo / prezzo * 100) : 0;
                                              return {
                                                ...pl,
                                                prezzo_vendita: prezzo,
                                                margine_euro: prezzo - pl.costo_lordo,
                                                margine_perc: prezzo > 0 ? ((prezzo - pl.costo_lordo) / prezzo * 100) : 0,
                                                food_cost_perc: foodCostPerc,
                                                valutazione: pl.ingredienti?.length === 0 ? 'non_valutabile'
                                                  : prezzo === 0 ? 'prezzo_mancante'
                                                  : foodCostPerc <= 25 ? 'ottimo'
                                                  : foodCostPerc <= 35 ? 'buono' : 'da_rivedere'
                                              };
                                            }
                                            return pl;
                                          })
                                        }));
                                        toast.success(`Prezzo aggiornato: €${prezzo.toFixed(2)}`);
                                      }
                                    }}
                                    className="p-0.5 hover:bg-gray-200 rounded"
                                    title="Modifica prezzo"
                                  >
                                    <Edit className="h-3 w-3 text-gray-400" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    const nuovoPrezzo = prompt(`Inserisci prezzo vendita per "${piatto.nome}" (€):`, '15');
                                    if (nuovoPrezzo && !isNaN(parseFloat(nuovoPrezzo))) {
                                      const prezzo = parseFloat(nuovoPrezzo);
                                      setAnalisiMenuRisultato(prev => ({
                                        ...prev,
                                        risultati: prev.risultati.map((pl, i) => {
                                          if (i === idx) {
                                            const foodCostPerc = prezzo > 0 ? (pl.costo_lordo / prezzo * 100) : 0;
                                            return {
                                              ...pl,
                                              prezzo_vendita: prezzo,
                                              margine_euro: prezzo - pl.costo_lordo,
                                              margine_perc: prezzo > 0 ? ((prezzo - pl.costo_lordo) / prezzo * 100) : 0,
                                              food_cost_perc: foodCostPerc,
                                              valutazione: pl.ingredienti?.length === 0 ? 'non_valutabile'
                                                : prezzo === 0 ? 'prezzo_mancante'
                                                : foodCostPerc <= 25 ? 'ottimo'
                                                : foodCostPerc <= 35 ? 'buono' : 'da_rivedere'
                                            };
                                          }
                                          return pl;
                                        })
                                      }));
                                      toast.success(`Prezzo inserito: €${prezzo.toFixed(2)}`);
                                    }
                                  }}
                                  className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-xs font-medium flex items-center gap-1"
                                >
                                  <Plus className="h-3 w-3" /> Inserisci €
                                </button>
                              )}
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Food Cost <span className="text-[9px] text-gray-400">(+IVA)</span></p>
                              <div className="flex flex-col">
                                <span className="text-[10px] text-gray-400">Netto: €{piatto.costo_netto?.toFixed(2)}</span>
                                <p className="font-semibold text-emerald-600">€{piatto.costo_lordo?.toFixed(2)}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Margine</p>
                              {piatto.prezzo_vendita > 0 ? (
                                <p className="font-semibold text-blue-600">€{piatto.margine_euro?.toFixed(2)}</p>
                              ) : (
                                <p className="font-semibold text-gray-400">—</p>
                              )}
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Food Cost %</p>
                              {piatto.prezzo_vendita > 0 ? (
                                <p className={`font-semibold ${
                                  piatto.food_cost_perc <= 25 ? 'text-green-600' : piatto.food_cost_perc <= 35 ? 'text-yellow-600' : 'text-red-600'
                                }`}>{piatto.food_cost_perc?.toFixed(1)}%</p>
                              ) : (
                                <p className="font-semibold text-gray-400">—</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Messaggio per non valutabili */}
                        {piatto.valutazione === 'non_valutabile' && (
                          <div className="bg-gray-100 rounded-lg p-2 mb-3 text-sm text-gray-500">
                            <p>⚠️ Nessun ingrediente identificato. Aggiungi ingredienti manualmente per calcolare il food cost.</p>
                          </div>
                        )}
                        
                        {/* Messaggio per prezzo mancante */}
                        {piatto.valutazione === 'prezzo_mancante' && (
                          <div className="bg-orange-50 rounded-lg p-2 mb-3 text-sm text-orange-700">
                            <p>💰 Prezzo vendita non disponibile. Il food cost è stato calcolato (€{piatto.costo_lordo?.toFixed(2)}). Inserisci il prezzo di vendita per calcolare margine e %.</p>
                          </div>
                        )}
                        
                        {/* Ingredienti - sempre visibile con possibilità di modifica */}
                        <div className="border-t border-gray-200 pt-3 mt-2">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-gray-700">Ingredienti ({piatto.ingredienti?.length || 0})</p>
                            <button
                              onClick={() => {
                                const nuovoIngrediente = prompt('Nome ingrediente:');
                                if (nuovoIngrediente) {
                                  const quantita = prompt('Quantità in grammi:', '100');
                                  const prezzo = prompt('Prezzo al kg (€):', '10');
                                  if (quantita && prezzo) {
                                    const q = parseFloat(quantita) || 100;
                                    const p = parseFloat(prezzo) || 10;
                                    const costo_netto = (q / 1000) * p;
                                    const costo_lordo = costo_netto * 1.1;
                                    
                                    setAnalisiMenuRisultato(prev => ({
                                      ...prev,
                                      risultati: prev.risultati.map((pl, i) => {
                                        if (i === idx) {
                                          const nuoviIng = [...(pl.ingredienti || []), {
                                            nome: nuovoIngrediente,
                                            quantita_g: q,
                                            prezzo_kg: p,
                                            costo_netto: costo_netto,
                                            costo_lordo: costo_lordo,
                                            aliquota_iva: 10,
                                            fonte_prezzo: 'manuale'
                                          }];
                                          const nuovoCostoLordo = nuoviIng.reduce((s, ing) => s + (ing.costo_lordo || 0), 0);
                                          const nuovoFoodCostPerc = pl.prezzo_vendita > 0 ? (nuovoCostoLordo / pl.prezzo_vendita * 100) : 0;
                                          return {
                                            ...pl,
                                            ingredienti: nuoviIng,
                                            costo_lordo: nuovoCostoLordo,
                                            costo_netto: nuoviIng.reduce((s, ing) => s + (ing.costo_netto || 0), 0),
                                            food_cost_perc: nuovoFoodCostPerc,
                                            margine_euro: pl.prezzo_vendita - nuovoCostoLordo,
                                            margine_perc: pl.prezzo_vendita > 0 ? ((pl.prezzo_vendita - nuovoCostoLordo) / pl.prezzo_vendita * 100) : 0,
                                            valutazione: nuoviIng.length === 0 ? 'non_valutabile' 
                                              : pl.prezzo_vendita === 0 ? 'prezzo_mancante'
                                              : nuovoFoodCostPerc <= 25 ? 'ottimo' 
                                              : nuovoFoodCostPerc <= 35 ? 'buono' : 'da_rivedere'
                                          };
                                        }
                                        return pl;
                                      })
                                    }));
                                    toast.success(`Aggiunto ${nuovoIngrediente}`);
                                  }
                                }
                              }}
                              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                            >
                              <Plus className="h-3 w-3" /> Aggiungi
                            </button>
                          </div>
                          
                          {piatto.ingredienti && piatto.ingredienti.length > 0 ? (
                            <div className="space-y-1.5">
                              {piatto.ingredienti.map((ing, i) => (
                                <div key={i} className={`rounded-lg ${
                                  ing.fonte_prezzo === 'fatture' 
                                    ? 'bg-blue-50 border border-blue-200' 
                                    : ing.fonte_prezzo === 'manuale'
                                      ? 'bg-purple-50 border border-purple-200'
                                      : 'bg-gray-100 border border-gray-200'
                                }`}>
                                  <div className="flex items-center justify-between text-xs px-2 py-1.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-gray-700">{ing.nome}</span>
                                      <span className="text-gray-500">{ing.quantita_g}g</span>
                                      <span className="text-gray-400" title="Prezzo netto (senza IVA)">€{ing.prezzo_kg?.toFixed(2)}/kg</span>
                                      {ing.aliquota_iva > 0 && (
                                        <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 text-gray-600" title={`IVA ${ing.aliquota_iva}%`}>+{ing.aliquota_iva}%</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold ${
                                        ing.fonte_prezzo === 'fatture' ? 'text-blue-600' 
                                          : ing.fonte_prezzo === 'manuale' ? 'text-purple-600'
                                          : 'text-gray-600'
                                      }`} title={`Netto: €${ing.costo_netto?.toFixed(2)} + IVA ${ing.aliquota_iva || 10}%`}>
                                        €{ing.costo_lordo?.toFixed(2)}
                                      </span>
                                      <button
                                        onClick={() => {
                                          const nuovaQta = prompt(`Nuova quantità per ${ing.nome} (g):`, ing.quantita_g);
                                          if (nuovaQta && !isNaN(parseFloat(nuovaQta))) {
                                            const q = parseFloat(nuovaQta);
                                            setAnalisiMenuRisultato(prev => ({
                                              ...prev,
                                              risultati: prev.risultati.map((pl, pIdx) => {
                                                if (pIdx === idx) {
                                                  const nuoviIng = pl.ingredienti.map((ingr, iIdx) => {
                                                    if (iIdx === i) {
                                                      const costo_netto = (q / 1000) * (ingr.prezzo_kg || 0);
                                                      return {
                                                        ...ingr,
                                                        quantita_g: q,
                                                        costo_netto: costo_netto,
                                                        costo_lordo: costo_netto * (1 + (ingr.aliquota_iva || 10) / 100)
                                                      };
                                                    }
                                                    return ingr;
                                                  });
                                                  const nuovoCostoLordo = nuoviIng.reduce((s, ing) => s + (ing.costo_lordo || 0), 0);
                                                  const nuovoFoodCostPerc = pl.prezzo_vendita > 0 ? (nuovoCostoLordo / pl.prezzo_vendita * 100) : 0;
                                                  return {
                                                    ...pl,
                                                    ingredienti: nuoviIng,
                                                    costo_lordo: nuovoCostoLordo,
                                                    costo_netto: nuoviIng.reduce((s, ing) => s + (ing.costo_netto || 0), 0),
                                                    food_cost_perc: nuovoFoodCostPerc,
                                                    margine_euro: pl.prezzo_vendita - nuovoCostoLordo,
                                                    margine_perc: pl.prezzo_vendita > 0 ? ((pl.prezzo_vendita - nuovoCostoLordo) / pl.prezzo_vendita * 100) : 0,
                                                    valutazione: nuovoFoodCostPerc <= 25 ? 'ottimo' : nuovoFoodCostPerc <= 35 ? 'buono' : 'da_rivedere'
                                                  };
                                                }
                                                return pl;
                                              })
                                            }));
                                            toast.success(`Quantità aggiornata`);
                                          }
                                        }}
                                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        title="Modifica quantità"
                                      >
                                        <Edit className="h-3 w-3 text-gray-400" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (confirm(`Rimuovere ${ing.nome}?`)) {
                                            setAnalisiMenuRisultato(prev => ({
                                              ...prev,
                                              risultati: prev.risultati.map((pl, pIdx) => {
                                                if (pIdx === idx) {
                                                  const nuoviIng = pl.ingredienti.filter((_, iIdx) => iIdx !== i);
                                                  const nuovoCostoLordo = nuoviIng.reduce((s, ing) => s + (ing.costo_lordo || 0), 0);
                                                  const nuovoFoodCostPerc = pl.prezzo_vendita > 0 ? (nuovoCostoLordo / pl.prezzo_vendita * 100) : 0;
                                                  return {
                                                    ...pl,
                                                    ingredienti: nuoviIng,
                                                    costo_lordo: nuovoCostoLordo,
                                                    costo_netto: nuoviIng.reduce((s, ing) => s + (ing.costo_netto || 0), 0),
                                                    food_cost_perc: nuovoFoodCostPerc,
                                                    margine_euro: pl.prezzo_vendita - nuovoCostoLordo,
                                                    margine_perc: pl.prezzo_vendita > 0 ? ((pl.prezzo_vendita - nuovoCostoLordo) / pl.prezzo_vendita * 100) : 0,
                                                    valutazione: nuoviIng.length === 0 || pl.prezzo_vendita === 0 ? 'non_valutabile' 
                                                      : nuovoFoodCostPerc <= 25 ? 'ottimo' 
                                                      : nuovoFoodCostPerc <= 35 ? 'buono' : 'da_rivedere'
                                                  };
                                                }
                                                return pl;
                                              })
                                            }));
                                            toast.success(`Rimosso ${ing.nome}`);
                                          }
                                        }}
                                        className="p-1 hover:bg-red-100 rounded transition-colors"
                                        title="Rimuovi"
                                      >
                                        <X className="h-3 w-3 text-red-400" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Riga fornitore e prodotto fattura - CLICCABILE per modifica */}
                                  {ing.fonte_prezzo === 'fatture' && ing.fornitore && (
                                    <div 
                                      onClick={() => apriModificaAbbinamento(idx, i, ing)}
                                      className="px-2 pb-1.5 text-[10px] text-blue-600 flex items-center justify-between gap-1.5 border-t border-blue-100 mt-1 pt-1 cursor-pointer hover:bg-blue-100/50 transition-colors rounded-b-lg"
                                      title="Clicca per modificare l'abbinamento"
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-semibold">📦 {ing.fornitore}</span>
                                        {ing.prodotto_fattura && (
                                          <span className="text-blue-500">• {ing.prodotto_fattura}</span>
                                        )}
                                      </div>
                                      <span className="text-blue-400 hover:text-blue-600">✏️ Modifica</span>
                                    </div>
                                  )}
                                  {ing.fonte_prezzo === 'stimato' && (
                                    <div 
                                      onClick={() => apriModificaAbbinamento(idx, i, ing)}
                                      className="px-2 pb-1.5 text-[10px] text-gray-500 italic flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors rounded-b-lg"
                                      title="Clicca per abbinare a un prodotto dalle fatture"
                                    >
                                      <span>⚠️ Prezzo stimato - nessun match nelle fatture</span>
                                      <span className="text-blue-500 hover:text-blue-700 not-italic font-medium">🔗 Abbina</span>
                                    </div>
                                  )}
                                  {ing.fonte_prezzo === 'manuale' && (
                                    <div 
                                      onClick={() => apriModificaAbbinamento(idx, i, ing)}
                                      className="px-2 pb-1.5 text-[10px] text-purple-500 flex items-center justify-between cursor-pointer hover:bg-purple-100/50 transition-colors rounded-b-lg"
                                      title="Clicca per modificare o abbinare a fattura"
                                    >
                                      <span>✍️ Prezzo inserito manualmente</span>
                                      <span className="text-purple-400 hover:text-purple-600">✏️ Modifica</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">Nessun ingrediente. Clicca "Aggiungi" per inserirne.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* New Analysis Button */}
                  <button
                    onClick={() => { setAnalisiMenuFile(null); setAnalisiMenuRisultato(null); }}
                    className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 flex items-center justify-center gap-2 transition-all"
                  >
                    <RefreshCw className="h-5 w-5" />
                    Nuova Analisi
                  </button>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <Button 
                variant="outline" 
                onClick={() => { 
                  setShowAnalisiMenu(false); 
                  setAnalisiMenuFile(null); 
                  setAnalisiMenuRisultato(null); 
                }}
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifica Abbinamento Ingrediente */}
      {showModificaAbbinamento && ingredienteSelezionato && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="modifica-abbinamento-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h3 className="font-bold text-lg text-gray-900">Modifica Abbinamento</h3>
                <p className="text-sm text-gray-500">
                  Ingrediente: <span className="font-semibold text-blue-600">{ingredienteSelezionato.ingrediente.nome}</span>
                </p>
              </div>
              <button 
                onClick={() => { setShowModificaAbbinamento(false); setIngredienteSelezionato(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-180px)]">
              {/* Abbinamento attuale */}
              {ingredienteSelezionato.ingrediente.fonte_prezzo === 'fatture' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs text-blue-600 font-medium mb-1">Abbinamento attuale:</p>
                  <p className="text-sm font-semibold text-blue-800">
                    📦 {ingredienteSelezionato.ingrediente.fornitore} • {ingredienteSelezionato.ingrediente.prodotto_fattura}
                  </p>
                  <p className="text-xs text-blue-500">€{ingredienteSelezionato.ingrediente.prezzo_kg?.toFixed(2)}/kg</p>
                </div>
              )}
              
              {/* Ricerca */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 block mb-1">Cerca prodotto nelle fatture:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchProdotto}
                    onChange={(e) => setSearchProdotto(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadProdottiFatture(searchProdotto)}
                    placeholder="Es: polpo, gamberi, riso..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => loadProdottiFatture(searchProdotto)}
                    disabled={loadingProdotti}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {loadingProdotti ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    Cerca
                  </button>
                </div>
              </div>
              
              {/* Lista prodotti */}
              <div className="space-y-2 mb-4">
                <p className="text-xs text-gray-500">{prodottiFatture.length} prodotti trovati</p>
                {loadingProdotti ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  </div>
                ) : prodottiFatture.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {prodottiFatture.map((prod, i) => (
                      <div 
                        key={i}
                        onClick={() => applicaNuovoAbbinamento(prod)}
                        className="p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{prod.descrizione}</p>
                            <p className="text-xs text-gray-500">📦 {prod.fornitore}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600">€{prod.prezzo_kg?.toFixed(2)}/kg</p>
                            <p className="text-xs text-gray-400">{prod.frequenza}x in fatture</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-4">Nessun prodotto trovato. Prova una ricerca diversa.</p>
                )}
              </div>
              
              {/* Opzione manuale */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-xs text-gray-500 mb-2">Oppure inserisci un prezzo manuale:</p>
                <button
                  onClick={impostaPrezzoManuale}
                  className="w-full py-2.5 border-2 border-dashed border-purple-300 text-purple-600 font-medium rounded-xl hover:bg-purple-50 hover:border-purple-400 transition-all flex items-center justify-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Inserisci prezzo manualmente
                </button>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <Button 
                variant="outline" 
                onClick={() => { setShowModificaAbbinamento(false); setIngredienteSelezionato(null); }}
                className="w-full"
              >
                Annulla
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DETTAGLIO RESE RICETTA ===== */}
      {(selectedRicettaRese || loadingRicettaRese) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !loadingRicettaRese && setSelectedRicettaRese(null)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {loadingRicettaRese ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-600">Caricamento analisi rese...</span>
              </div>
            ) : selectedRicettaRese && (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold">{selectedRicettaRese.ricetta?.nome}</h2>
                      <p className="text-blue-100 text-sm mt-1">{selectedRicettaRese.ricetta?.categoria} • {selectedRicettaRese.ricetta?.num_ingredienti} ingredienti</p>
                    </div>
                    <button 
                      onClick={() => setSelectedRicettaRese(null)}
                      className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                      <p className="text-xs text-blue-100">Prezzo Vendita</p>
                      <p className="text-2xl font-bold">€{selectedRicettaRese.ricetta?.prezzo_vendita}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                      <p className="text-xs text-blue-100">Costo Teorico</p>
                      <p className="text-xl font-bold text-amber-300">€{selectedRicettaRese.costi?.teorico_con_iva}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                      <p className="text-xs text-blue-100">Costo REALE</p>
                      <p className="text-xl font-bold text-red-300">€{selectedRicettaRese.costi?.reale_con_iva}</p>
                      <p className="text-xs text-red-200">+{selectedRicettaRese.costi?.differenza_pct}%</p>
                    </div>
                    <div className={`backdrop-blur rounded-xl p-3 ${selectedRicettaRese.margini?.in_perdita ? 'bg-red-500/30' : 'bg-green-500/20'}`}>
                      <p className="text-xs text-blue-100">Margine REALE</p>
                      <p className={`text-xl font-bold ${selectedRicettaRese.margini?.in_perdita ? 'text-red-300' : 'text-green-300'}`}>
                        €{selectedRicettaRese.margini?.reale_euro} ({selectedRicettaRese.margini?.reale_pct}%)
                      </p>
                      {selectedRicettaRese.margini?.in_perdita && (
                        <p className="text-xs text-red-200 font-bold">⚠️ IN PERDITA!</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Copertura DB */}
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <span className="text-blue-200">Copertura database rese:</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded-full font-medium">
                      {selectedRicettaRese.analisi_rese?.ingredienti_matchati}/{selectedRicettaRese.ricetta?.num_ingredienti} ingredienti ({selectedRicettaRese.analisi_rese?.copertura_db})
                    </span>
                  </div>
                </div>
                
                {/* Tabella Ingredienti */}
                <div className="flex-1 overflow-auto p-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-500" />
                    Analisi Ingrediente per Ingrediente
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left py-3 px-3 font-semibold text-gray-700">Ingrediente</th>
                          <th className="text-center py-3 px-2 font-semibold text-gray-700">Qtà</th>
                          <th className="text-center py-3 px-2 font-semibold text-gray-700">€/kg</th>
                          <th className="text-center py-3 px-2 font-semibold text-purple-600 bg-purple-50">Utilizzo</th>
                          <th className="text-center py-3 px-2 font-semibold text-amber-600">Resa Mond.</th>
                          <th className="text-center py-3 px-2 font-semibold text-orange-600">Resa Cott.</th>
                          <th className="text-right py-3 px-2 font-semibold text-gray-700">Teorico</th>
                          <th className="text-right py-3 px-2 font-semibold text-red-600">REALE</th>
                          <th className="text-center py-3 px-2 font-semibold text-red-600">Diff</th>
                          <th className="text-left py-3 px-2 font-semibold text-gray-500">Match DB</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedRicettaRese.ingredienti?.map((ing, idx) => (
                          <tr key={idx} className={`hover:bg-gray-50 ${ing.impatto === 'alto' ? 'bg-red-50/50' : ing.impatto === 'medio' ? 'bg-orange-50/30' : ''}`}>
                            <td className="py-3 px-3">
                              <p className="font-medium text-gray-900">{ing.nome}</p>
                              {ing.prodotto_fattura && (
                                <p className="text-xs text-gray-400 truncate max-w-[200px]" title={ing.prodotto_fattura}>
                                  {ing.fornitore && <span className="text-blue-500">{ing.fornitore}: </span>}
                                  {ing.prodotto_fattura}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-2 text-center text-gray-600">
                              {ing.quantita}{ing.unita_misura}
                            </td>
                            <td className="py-3 px-2 text-center text-gray-600">
                              €{ing.costo_kg_fattura}
                            </td>
                            {/* COLONNA UTILIZZO - Toggle INTERO/LAVORATO */}
                            <td className="py-3 px-2 text-center bg-purple-50/30">
                              {ing.match_db ? (
                                <div className="flex flex-col items-center gap-1">
                                  <button
                                    onClick={() => updateTipoUtilizzo(
                                      selectedRicettaRese.ricetta?.id,
                                      ing.nome,
                                      ing.tipo_utilizzo === 'INTERO' ? 'LAVORATO' : 'INTERO'
                                    )}
                                    className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                                      ing.tipo_utilizzo === 'INTERO'
                                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    }`}
                                    title={ing.tipo_utilizzo === 'INTERO' 
                                      ? 'Servito INTERO (con guscio/buccia) - Clicca per cambiare'
                                      : 'LAVORATO (sgusciato/pulito) - Clicca per cambiare'
                                    }
                                  >
                                    {ing.tipo_utilizzo === 'INTERO' ? '🐚 Intero' : '🔪 Lavorato'}
                                  </button>
                                  {ing.tipo_utilizzo === 'INTERO' && ing.resa_mondatura_originale < 0.7 && (
                                    <span className="text-[9px] text-gray-400">
                                      se lavorato: {Math.round(ing.resa_mondatura_originale * 100)}%
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex flex-col items-center">
                                <span className={`font-bold ${ing.resa_mondatura < 0.7 ? 'text-red-600' : ing.resa_mondatura < 0.9 ? 'text-amber-600' : 'text-green-600'}`}>
                                  {ing.resa_mondatura_pct}
                                </span>
                                {ing.scarto_mondatura_g > 0 && (
                                  <span className="text-[10px] text-gray-400">-{ing.scarto_mondatura_g}g</span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex flex-col items-center">
                                <span className={`font-bold ${ing.resa_cottura < 0.7 ? 'text-red-600' : ing.resa_cottura < 0.85 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {ing.resa_cottura_pct}
                                </span>
                                {ing.metodo_cottura_usato && (
                                  <span className="text-[10px] text-gray-400">{ing.metodo_cottura_usato}</span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right text-gray-600">
                              €{ing.costo_teorico?.toFixed(2)}
                            </td>
                            <td className="py-3 px-2 text-right font-bold text-red-600">
                              €{ing.costo_reale?.toFixed(2)}
                              <p className="text-[10px] text-gray-400">€{ing.costo_reale_kg}/kg</p>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {ing.differenza_pct > 0 ? (
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  ing.differenza_pct > 50 ? 'bg-red-100 text-red-700' :
                                  ing.differenza_pct > 20 ? 'bg-orange-100 text-orange-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  +{ing.differenza_pct}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              {ing.match_db ? (
                                <div className="flex items-center gap-1">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span className="text-xs text-gray-600 truncate max-w-[100px]" title={ing.yield_product}>
                                    {ing.yield_product}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                                  <span className="text-xs text-gray-400">Non trovato</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold">
                        <tr>
                          <td colSpan="5" className="py-3 px-3 text-right text-gray-700">TOTALE:</td>
                          <td className="py-3 px-2 text-right text-amber-600">€{selectedRicettaRese.costi?.teorico_con_iva}</td>
                          <td className="py-3 px-2 text-right text-red-600">€{selectedRicettaRese.costi?.reale_con_iva}</td>
                          <td className="py-3 px-2 text-center">
                            <span className="px-2 py-1 rounded-full text-xs bg-red-200 text-red-800">
                              +{selectedRicettaRese.costi?.differenza_pct}%
                            </span>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  {/* Note e legenda */}
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    <div className="bg-purple-50 rounded-xl p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">🐚 Utilizzo Ingrediente</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">🐚 Intero</span>
                          <span className="text-gray-600">Servito con guscio/buccia (nessuno scarto)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-bold">🔪 Lavorato</span>
                          <span className="text-gray-600">Sgusciato/pulito (applica scarto)</span>
                        </div>
                        <p className="text-xs text-purple-600 mt-2 italic">
                          Clicca sul bottone per cambiare il tipo di utilizzo
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">📊 Legenda Colori</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-red-100 rounded"></span>
                          <span className="text-gray-600">Impatto alto (+50%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-orange-100 rounded"></span>
                          <span className="text-gray-600">Impatto medio (+20-50%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-white border rounded"></span>
                          <span className="text-gray-600">Impatto basso (&lt;20%)</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 rounded-xl p-4">
                      <h4 className="font-semibold text-amber-800 mb-2">💡 Suggerimenti</h4>
                      <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                        {selectedRicettaRese.margini?.in_perdita && (
                          <li className="text-red-600 font-medium">Piatto in PERDITA!</li>
                        )}
                        {selectedRicettaRese.ingredienti?.filter(i => i.impatto === 'alto').length > 0 && (
                          <li>{selectedRicettaRese.ingredienti?.filter(i => i.impatto === 'alto').length} ingredienti ad alto impatto</li>
                        )}
                        {selectedRicettaRese.analisi_rese?.ingredienti_non_matchati > 0 && (
                          <li>{selectedRicettaRese.analisi_rese?.ingredienti_non_matchati} non trovati nel DB</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedRicettaRese(null)}
                  >
                    Chiudi
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FBCostPage;
